/**
 * DealDetailView — 딜 상세 페이지 (P9-UI Step 1, loader 패턴 v2)
 *
 * Drawer → 새 탭 페이지로 전환.
 *
 * URL:    /deal/:id
 * 진입:   DealListView 의 행 클릭 → window.open('/deal/:id', '_blank')
 *
 * ─── Sola loader 패턴 (ADR-0002) ──────────────────────────────────────
 *
 * 이 view 는 fetch 를 자기 onMount 에서 하지 않습니다. 라우터의 loader 가
 * view 생성 *전* 에 api.fetchDealFull + api.fetchActiveAssignees 를 await
 * 하고, 결과를 constructor 의 loaded 인자로 전달합니다.
 *
 * 결과: this.data.full / assignees 가 처음부터 완전 (non-null) 상태.
 *       render() 가 loading / null 분기 없이 한 번에 호출됨.
 *
 * 라우트 정의 (main.ts):
 *   { pattern: '/deal/:id',
 *     loader: async ({ id }, _q, signal) => {
 *       const [full, assignees] = await Promise.all([
 *         api.fetchDealFull(id, { signal }),
 *         api.fetchActiveAssignees({ signal }).catch(() => []),
 *       ]);
 *       return { full, assignees };
 *     },
 *     create: ({ id }, _q, loaded) =>
 *       new DealDetailView({ dealId: id, ...(loaded as DealDetailLoaded) }) }
 *
 * 레이아웃 (2 컬럼):
 *   ┌──────────────────────────────────────────┐
 *   │ 상단 헤더 (sticky)                          │  ← 딜명 + ID + 버튼
 *   ├─────────────────────────┬─────────────────┤
 *   │ 좌측 main  (scroll-y)   │ 우측 사이드 (sticky)│
 *   │  - 기본 정보              │  - 상태 stepper   │
 *   │  - 품목                  │  - 채팅           │
 *   │  - 소싱  ← Step 2        │                  │
 *   │  - 견적  ← Step 3        │                  │
 *   │  - 원문/히스토리 ← Step 3 │                  │
 *   └─────────────────────────┴─────────────────┘
 *
 * 컬러: HPE Green (--primary) 사용.
 */

import { SolaView, htmlSolaWithInstance, navigate } from '@hm-spt/sola';
import * as api from '../api/client';
import { formatQuoteCopyText, formatRentalQuoteCopyText } from '../lib/quote-copy';
import { conditionGroup } from '../lib/condition';
import { catalogHitDescription } from '../lib/cells';
import { subscribeDealChanged } from '../lib/deal-channel';
// 딥 경로 import — 배럴(@hcltech/shared)은 auth/jwt(hono/jwt)까지 끌고 와
// 브라우저 번들에서 해석 실패한다. UI 는 항상 서브패스로 가져온다.
import { projectServerCondition } from '@hcltech/shared/condition/server-condition';
import { parseNum, parseInt0, computeQuoteTotals, quoteBlockReasons, serverEffectiveSellKrw } from '../lib/quote-math';
import * as rentalPresetApi from '../api/rental-preset-api';
import {
  computeRentalLine, computeRentalTotals, rentalBlockReasons, toRentalItemPayload,
} from '../lib/quote-rental';

/**
 * 판매 견적 라인이 보내는 '임대 아님' 표시. 전부 null + 빈 스냅샷이다.
 * 값이 null 인 것과 키가 없는 것이 서버에서 다른 뜻이라(전자는 지우기,
 * 후자는 유지) 되돌리기에는 반드시 이 쪽이 필요하다.
 */
const RENTAL_CLEAR = {
  rental_term_months:          null,
  rental_preset_id:            null,
  rental_margin_rate:          null,
  rental_manual_unit_krw:      null,
  rental_contract_cost_krw:    null,
  rental_short_term_months:    null,
  rental_short_term_surcharge: null,
  rental_normal_term_months:   null,
  rental_short_term_step_up:   null,
  rental_penalty_rate:         null,
  rental_penalty_adjust:       null,
  rental_costs:                [] as never[],
} as const;
// 견적 렌더 — Step 2 에서 분리 (분해 계획 v2 §2)
import {
  renderQuoteSection, renderQuoteModal, renderQuoteViewModal, renderQuoteDownloadModal,
  patchRentalUnitOutputs, rentalCostPopoverPos,
} from './deal-detail/render-quote';
// 소싱 렌더 — Step 3a 에서 분리
import {
  renderSourcingSection, renderNeedCreateModal, renderNeedEditModal, renderNeedSearchResultsHtml,
  renderRfqViewModal,
} from './deal-detail/render-sourcing';
// 품목 렌더 — Step 3b 에서 분리
import {
  renderItemsSection, renderItemSearchPanel, renderItemSearchResultsHtml, renderItemSearchApplyBtn,
} from './deal-detail/render-items';
// 서버 추가 모달 (신규 구성 만들기 / 기존 구성 불러오기)
import {
  renderServerAddModal, renderServerAddResults,
} from './deal-detail/render-server-group';
import { sourceSnapshot } from '../lib/source-options';
// 헤더/기본정보/거래처·담당자/메모 렌더 — Step 3c 에서 분리
import {
  renderHeader, renderInfoSection, renderHistorySectionStub,
  renderDealCancelModal, renderDealCustomerModal, renderDealContactModal,
  renderDealCustomerModalResults, renderDealContactModalResults,
} from './deal-detail/render-info';
// 채팅 — 독립 컴포넌트로 승격 (Step 5)
import { ChatPanel } from '../components/ChatPanel';
// state 정의 + factory + 도메인 상수 — 정리 Step 1 에서 분리 (분해 계획 v2 §2)
// (미사용 타입은 import 하지 않음 — noUnusedLocals. 필요해지면 그때 추가)
import {
  type DealDetailLoaded, type DealDetailData,
  type EditDraftItem, type QuoteModalState,
  ITEM_CATEGORY_OPTIONS,
  nextLineId, emptyItemSearchState, emptyNeedCreate, emptyNeedEdit, fmtThousands,
  emptyQuoteModalState, quoteItemToModalRow, emptyQuoteViewModalState,
  dealItemToModalRow, emptyQuoteModalRow,
  editItemFieldDirty, editConfigDirty,
  emptyDealCancelModal, emptyQuoteDownloadModal,
  emptyDealCustomerModal, emptyDealContactModal,
  emptyServerAdd,
} from './deal-detail/types';

// main.ts 가 이 모듈에서 import 하는 loader 계약 — 경로 호환 재export
export type { DealDetailLoaded } from './deal-detail/types';

// renderMetaOptions → deal-detail/render-info.ts 로 이동 (Step 3c).

// renderMsgSender / renderMsgAttachments → components/ChatPanel.ts 로 이동 (Step 5).

// ─── 메인 컬럼 리전(부분 갱신 단위) ─────────────────────────────────
//   각 섹션을 data-region wrapper 로 감싸고, 핸들러/채팅 refetch 가 바뀐
//   리전만 코어 SolaView.refreshRegion(selector, htmlFn) 으로 교체.
//   (구 refreshMain = 전체 합, 호환 유지)
type DealRegion = 'info' | 'items' | 'sourcing' | 'quote' | 'history';
const DEAL_REGIONS: readonly DealRegion[] = ['info', 'items', 'sourcing', 'quote', 'history'];

export class DealDetailView extends SolaView<DealDetailData> {
  override events = {
    click: {
      '@create-quote':         'handleCreateQuote',
      // ─── 견적 생성 모달 (P9-UI Step 3b) ────────────────────
      '@quote-modal-close':    'handleCloseQuoteModal',
      '@quote-modal-overlay':  'handleCloseQuoteModal',
      '@quote-modal-save':     'handleSaveQuote',
      '@quote-server-toggle':  'handleQuoteServerToggle',   // 서버 구성 접기/펼치기
      '@quote-item-delete':    'handleQuoteItemDelete',
      '@quote-item-add':       'handleQuoteItemAdd',
      '@quote-kind-open':      'handleQuoteKindMenuOpen',  // '견적 계산' ▼ (판매/임대)
      '@quote-rental-cost-open':  'handleQuoteRentalCostOpen',   // 임대총원가 ⓘ
      '@quote-rental-cost-close': 'handleQuoteRentalCostClose',
      '@quote-ref-more-open':  'handleQuoteRefMoreOpen',   // 레퍼런스 더보기
      '@quote-ref-more-close': 'handleQuoteRefMoreClose',
      '@quote-ref-open-deal':  'handleQuoteRefOpenDeal',   // 거래처명 → 딜 새탭
      '@quote-kind-close':     'handleQuoteKindMenuClose',
      '@assignee-phone-save-btn': 'handleAssigneePhoneSave',
      // ─── 거래처/담당자 지정 모달 (2026-07-14 스펙) ───────────
      '@customer-change-open':        'handleCustomerChangeOpen',
      '@customer-confirm-open':       'handleCustomerConfirmOpen',
      '@customer-modal-close':        'handleCustomerModalClose',
      '@customer-modal-overlay':      'handleCustomerModalClose',
      '@customer-modal-create-save':  'handleCustomerModalCreateSave',
      '@contact-change-open':         'handleContactChangeOpen',
      '@contact-confirm-open':        'handleContactConfirmOpen',
      '@contact-modal-close':         'handleContactModalClose',
      '@contact-modal-overlay':       'handleContactModalClose',
      '@contact-modal-create-save':   'handleContactModalCreateSave',
      '@quick-quote':          'handleQuickQuote',
      // ─── 견적 발송 (P9-UI Step 3c) ─────────────────────────
      '@send-quote':           'handleSendQuote',
      // ─── 견적 수락/거절 (P9-UI Step 3e) ─────────────────────
      '@accept-quote':         'handleAcceptQuote',
      '@reject-quote':         'handleRejectQuote',
      '@quote-edit':           'handleQuoteEdit',
      // ─── 견적 행 작업(⋯) 메뉴 — view-quote 앞에 등록(중첩 우선) ─
      '@quote-menu-open':      'handleQuoteMenuOpen',
      '@quote-menu-close':     'handleQuoteMenuClose',
      '@quote-download':       'handleQuoteDownload',
      '@quote-download-pick':  'handleQuoteDownloadPick',
      '@quote-download-close': 'handleQuoteDownloadClose',
      '@quote-copy':           'handleQuoteCopy',
      '@quote-delete':         'handleQuoteDelete',
      // ─── 견적 보기 모달 (P9-UI Step 3c) — 행 클릭으로 열림 ────
      '@view-quote':           'handleViewQuote',
      '@quote-view-close':     'handleCloseQuoteViewModal',
      '@quote-view-overlay':   'handleCloseQuoteViewModal',
      // (채팅 이벤트는 ChatPanel 컴포넌트가 자체 events 로 처리 — Step 5)
      // ─── 구성(Configuration) (B3) ──────────────────────────
      '@config-select':         'handleConfigSelect',
      '@config-create':         'handleConfigCreate',
      '@config-delete':         'handleConfigDelete',
      // ─── 품목 확정/취소 (B3-2) — 모달 없이 즉시 실행 ─────────
      '@config-confirm-open':   'handleConfigConfirmOpen',
      '@config-unconfirm-open': 'handleConfigUnconfirmOpen',
      // ─── 구성 편집 모드 (E2) ────────────────────────────────
      '@edit-add-row':          'handleEditAddRow',
      '@server-add':            'handleServerAdd',
      '@server-add-new':        'handleServerAddNew',
      '@server-add-import':     'handleServerAddImportOpen',
      '@server-add-pick':       'handleServerAddImportPick',
      '@server-add-more':       'handleServerAddMore',
      '@server-add-close':      'handleServerAddClose',
      '@server-toggle':         'handleServerToggle',
      '@server-menu-toggle':    'handleServerMenuToggle',
      '@server-menu-close':     'handleServerMenuClose',
      '@server-edit':           'handleServerEdit',
      '@server-duplicate':      'handleServerDuplicate',
      '@server-delete':         'handleServerDelete',
      '@edit-delete-row':       'handleEditDeleteRow',
      '@edit-duplicate-row':    'handleItemDuplicate',
      '@item-search-apply-unreg': 'handleItemSearchApplyUnregistered',
      '@item-search-clear':     'handleItemSearchClear',
      '@edit-complete':         'handleEditComplete',
      '@edit-cancel':           'handleEditCancel',
      '@edit-search-open':      'handleItemSearchOpen',
      '@item-search-close':     'handleItemSearchClose',
      '@item-search-overlay':   'handleItemSearchClose',
      '@item-search-select':    'handleItemSearchSelect',
      '@item-search-apply':     'handleItemSearchApply',
      // ─── 메모 패널 접기 ─────────────────────────────────────
      '@toggle-original':       'handleToggleOriginal',
      '@toggle-history':        'handleToggleHistory',
      // ─── 상태 영역 (헤더 트리거 + 팝오버) ───────────────────
      '@status-popover-toggle': 'handleStatusPopoverToggle',
      '@status-popover-close':  'handleStatusPopoverClose',
      '@restart-config':        'handleRestartConfig',
      // ─── 딜 취소 (확인 + 사유 모달) ─────────────────────────
      '@cancel-deal-open':      'handleCancelOpen',
      '@cancel-deal-close':     'handleCancelClose',
      '@cancel-deal-overlay':   'handleCancelClose',
      '@cancel-reason-pick':    'handleCancelReasonPick',
      '@cancel-deal-confirm':   'handleCancelConfirm',
      '@watch':                 'handleWatchToggle',
      '@copy-deal-link':        'handleCopyDealLink',
      // ─── 딜 생성 모드 ─────────────────────────────────────
      '@new-cancel':            'handleNewCancel',
      // ─── 레퍼런스(품목 RFQ 수신가) 보기 팝오버 ────────────
      // ─── 소싱 RFQ 버블 보기 모달 (6D) ───────────────────────
      '@rfq-bubble':            'handleRfqBubbleView',
      '@rfq-purchase-toggle':   'handleRfqPurchaseToggle',
      '@rfq-view-close':        'handleRfqViewClose',
      '@rfq-view-overlay':      'handleRfqViewClose',
      // ─── 소싱 태그 편집 (6D) ────────────────────────────────
      '@need-tag-open':         'handleNeedTagOpen',
      '@need-tag-close':        'handleNeedTagClose',
      '@need-tag-toggle':       'handleNeedTagToggle',
      // ─── 품목 행 작업(⋯) 메뉴 → 니즈 생성 ──────────────────
      '@item-menu-open':        'handleItemMenuOpen',
      '@item-menu-close':       'handleItemMenuClose',
      '@item-need-create':      'handleItemNeedCreateFromRow',
      // ─── 니즈 생성 모달 (6D) ────────────────────────────────
      '@need-create-open':      'handleNeedCreateOpen',
      '@need-create-close':     'handleNeedCreateClose',
      '@need-create-overlay':   'handleNeedCreateClose',
      '@need-create-tag':       'handleNeedCreateTag',
      '@need-create-pick':      'handleNeedCreatePick',
      '@need-create-clear':     'handleNeedCreateClear',
      '@need-create-submit':    'handleNeedCreateSubmit',
      '@need-create-link':      'handleNeedCreateLink',
      // ─── 소싱 니즈 행 작업(⋯) 메뉴 ─────────────────────────
      '@need-menu-open':        'handleNeedMenuOpen',
      '@need-menu-close':       'handleNeedMenuClose',
      '@need-delete':           'handleNeedDelete',
      '@need-purchase-request': 'handleNeedPurchaseRequest',
      '@need-edit':             'handleNeedEdit',
      '@need-edit-close':       'handleNeedEditClose',
      '@need-edit-save':        'handleNeedEditSave',
    },
    dblclick: {
      // 품목 검색 결과 더블클릭 → 즉시 적용
      '@item-search-select':   'handleItemSearchPick',
      // 거래처/담당자 선택 결과 더블클릭 → 딜에 지정
      '@customer-modal-pick':  'handleCustomerModalPick',
      '@contact-modal-pick':   'handleContactModalPick',
    },
    input: {
      '@quote-meta-field':     'handleQuoteMetaChange',
      '@quote-row-field':      'handleQuoteRowChange',
      '@quote-rental-field':   'handleQuoteRentalChange',
      '@edit-field':           'handleEditFieldChange',
      '@item-search-input':    'handleItemSearchInput',
      '@customer-modal-search':  'handleCustomerModalSearch',
      '@customer-modal-newname': 'handleCustomerModalNewName',
      '@contact-modal-search':   'handleContactModalSearch',
      '@contact-modal-newfield': 'handleContactModalNewField',
      '@need-create-field':    'handleNeedCreateField',
      '@need-create-search':   'handleNeedCreateSearch',
      '@need-edit-field':      'handleNeedEditField',
      '@server-add-search':    'handleServerAddSearch',
      '@item-sell-price':      'handleItemSellPriceInput',   // 타이핑 중 실시간 콤마(저장은 change)
      // 서버 금액 칸도 같은 포매터를 쓴다 — 같은 표에서 칸마다 동작이 다르면 안 된다.
      '@server-bundle':        'handleItemSellPriceInput',
      '@server-child-price':   'handleItemSellPriceInput',
    },
    change: {
      '@quote-source-change':  'handleQuoteSourceChange',
      '@quote-view-source-change': 'handleQuoteViewSourceChange',
      '@quote-view-cost-change':   'handleQuoteViewCostChange',
      '@status-select':        'handleStatusChange',
      '@assignee-select':      'handleAssigneeChange',
      '@item-search-stock-toggle': 'handleItemSearchStockToggle',
      '@items-select-all':     'handleItemsSelectAll',   // 품목 헤더 전체 선택/해제
      '@item-check':           'handleItemCheckSync',     // 행 체크 → 헤더 상태 동기화
      '@assignee-phone-save':  'handleAssigneePhoneSave',
      '@lead-source-select':   'handleLeadSourceChange',
      '@classification-select': 'handleClassificationChange',
      '@quote-meta-field':     'handleQuoteMetaChange',
      '@quote-row-field':      'handleQuoteRowChange',
      '@quote-rental-field':   'handleQuoteRentalChange',
      '@item-sell-price':      'handleItemSellPriceChange',
      // 서버 인라인 편집 — 헤더(대수·번들가) / 구성품(소스·판매가)
      //   금액 칸 2개는 input 맵에도 등록해 기존 판매가 칸과 같은 실시간 콤마를 준다.
      '@server-qty':           'handleServerQty',
      '@server-bundle':        'handleServerBundle',
      '@server-child-price':   'handleServerChildPrice',
      '@server-child-source':  'handleItemSource',
      '@item-source':          'handleItemSource',   // 개별 품목 (레퍼런스 자리)
      '@edit-category':        'handleEditFieldChange',
      '@edit-cond-bulk':       'handleEditCondBulk',   // 헤더 셀렉터 → 전 품목 컨디션 일괄
      '@need-create-condition': 'handleNeedCreateField',
      '@need-edit-field':      'handleNeedEditField',
    },
  };

  /**
   * loader 결과를 포함한 완전 초기화.
   *
   * params:
   *   dealId    — 라우트 :id (raw string)
   *   full      — loader 가 받아온 deal 상세 (non-null)
   *   assignees — loader 가 받아온 영업담당 후보 목록
   */
  /** 카탈로그 검색 디바운스 타이머 */
  private _itemSearchTimer: number | undefined;
  /** 니즈 생성 품목 검색 디바운스 타이머 */
  private _needSearchTimer: number | undefined;
  /** 기존 서버 구성 검색 — 디바운스 + stale 응답 차단 */
  private _savedServerTimer: number | undefined;
  private _savedServerSeq = 0;
  /** 거래처 자동완성 검색 디바운스 타이머 */
  private _customerSearchTimer: number | undefined;
  /** 오늘 USD→KRW 환율 (품목 검색 재고원가 KRW 환산용, fallback 1380) */
  private _fxRate = 1380;

  /**
   * 채팅 패널 — 독립 컴포넌트 (Step 5, 분해 계획 v2 §1).
   * 입력/첨부/pending 은 컴포넌트 소유, 메시지 목록은 syncChat() 으로 하향 공급.
   * send 성공 시 onSent 에서 refetch → 딜 데이터·채팅 동기화 (기존 흐름 그대로).
   */
  private readonly chat = new ChatPanel({
    send: async (content, atts) => { await api.postDealChat(this.data.full.id, content, atts); },
    onSent: async () => {
      // refetch — 새 메시지 (user + assistant) 가 chat_message 에 들어갔고,
      // agent 가 딜 데이터를 변경했을 수 있음 → 리전 단위 갱신.
      const newFull = await api.fetchDealFull(this.data.full.id);
      this.data.full = newFull;
      // 봇이 품목을 추가/변경했을 수 있으니 편집 드래프트부터 재조정
      // (없으면 새 행이 full 에만 들어오고 표엔 안 보임 — 새로고침 필요했던 원인).
      const added = this.reconcileEditDraft();
      // 봇은 어떤 리전이든 바꿀 수 있어 헤더 + 데이터 리전 전체 갱신.
      // 코어 SolaView.refreshRegion 재사용(selector + htmlFn) — [data-bind] 재등록·자식 경계 보호 포함.
      this.refreshHeader();
      for (const r of DEAL_REGIONS) {
        this.refreshRegion(`.deal-region[data-region="${r}"]`, () => this.renderRegion(r));
      }
      this.refreshModals();
      this.syncChat();
      if (added > 0) this.toast(`봇이 품목 ${added}개를 추가했습니다 — 표에 반영됨`);
    },
  });

  constructor(params: { dealId: string; newMode?: boolean } & DealDetailLoaded) {
    super();
    // 진입 시 우선 표시 구성 — 최저 config_no (대표 개념 없음, ITEM-003)
    const configs = [...(params.full.configurations ?? [])].sort((a, b) => a.config_no - b.config_no);
    const firstConfig = configs[0] ?? null;
    this.data = {
      ...params,
      newMode:          params.newMode ?? false,
      activeConfigId:   firstConfig?.id ?? null,
      editConfig:       null,
      itemSearch:       emptyItemSearchState(),
      contextCollapsed: { original: false, history: false },
      statusPopoverOpen: false,
      statusHistory: null,
      rfqViewBubble: null,
      tagEditor: null,
      itemMenuId: null,
      expandedServerIds: new Set<string>(),
      serverMenuId: null,
      itemSources: { loading: false, stock: [], rfq: [] },
      quoteMenuId: null,
      quoteKindMenuOpen: false,
      needMenuId: null,
      needCreate: emptyNeedCreate(),
      needEdit: emptyNeedEdit(),
      serverAdd:      emptyServerAdd(),
      quoteModal:     emptyQuoteModalState(),
      quoteSources:   [],
      quotePrices:    [],
      dealEvents:     [],
      customerModal:  emptyDealCustomerModal(),
      contactModal:   emptyDealContactModal(),
      quoteViewModal: emptyQuoteViewModalState(),
      quoteDownloadModal: emptyQuoteDownloadModal(),
      cancelModal:    emptyDealCancelModal(),
    };
    this.syncChat();   // 초기 메시지 공급 (마운트 전 — ChatPanel 이 저장해뒀다 render 에 사용)
  }

  // 채팅 메시지 동기화 — full.threads 평탄화해서 ChatPanel 에 하향 공급.
  // (구 refreshChat 대체 — 시스템 메시지 추가/refetch 후 호출)
  private syncChat(): void {
    const threads = this.data.full.threads ?? [];
    this.chat.setMessages(threads.flatMap((t) => t.messages ?? []));
  }

  override getTitle(): string {
    return this.data.newMode ? '신규 딜 생성 — HCLTech' : `${this.data.full.name} — HCLTech`;
  }

  // ─── 데이터 다시 받아오기 — status/assignee 변경 후 부분 갱신용 ─────
  //
  // loader 는 라우트 생성 시 1회만 돌고, 그 후 같은 view 인스턴스 안에서 일어나는
  // 부분 데이터 변경 (status 변경, assignee 변경 등) 은 우리가 메모리에서 갱신
  // 한 후 refreshBody() 로 화면을 다시 그림.
  //
  // 이 자리는 loader 패턴의 한계 — 같은 라우트 안에서 데이터가 바뀌면 라우터를
  // 다시 부르기엔 너무 무거우므로 view 가 책임.
  // TODO(future): router.reload() 같은 API 가 생기면 이 자리도 단순화 가능.
  // 부분 갱신 — 헤더/메인/모달만 다시 그림. 채팅(.deal-detail-side)은 건드리지 않음
  // → 상태 토글·태그 수정 등에서 채팅 이미지 재로딩/높이 출렁임 방지.
  // ─── 영역별 부분 렌더 ────────────────────────────────────────────
  // 핸들러는 바뀐 영역만 호출 → 불필요한 재렌더(스크롤 점프·이미지 깜빡임·포커스 손실) 방지.
  private refreshHeader(): void {
    const h = this._rootElement?.querySelector('.ddb-header');
    if (h) h.innerHTML = renderHeader(this.data.full, this.data.newMode, this.data.statusPopoverOpen, this.data.statusHistory);
  }
  private refreshMain(): void {
    const m = this._rootElement?.querySelector<HTMLElement>('.deal-detail-main');
    if (!m) return;
    const scrollTop = m.scrollTop;   // innerHTML 교체 시 스크롤 0 리셋 → 보존
    m.innerHTML = this.renderMainInner(this.data.full);
    m.scrollTop = scrollTop;
  }
  private refreshModals(): void {
    const d = this._rootElement?.querySelector('.ddb-modals');
    if (d) d.innerHTML = this.renderModals();
  }
  // 전체 (헤더+메인+모달) — 여러 영역이 동시에 바뀌는 경우에만.
  private refreshBody(): void {
    this.refreshHeader();
    this.refreshMain();
    this.refreshModals();
  }

  override onMount(): void {
    // 미확정 활성 구성이면 편집 모드 진입(구성 수정 버튼 폐기 — 상시 편집).
    this.ensureEditDraft();
    if (this.data.editConfig) this.refreshMain();
    void this._loadDealEvents();
    void this._loadQuoteSources();   // 레퍼런스 보기 + 견적 소스 드롭다운용 RFQ 후보
    void this._loadItemSources();  // 서버 구성품 소스 드롭다운(재고 포함)
    // 환율 1회 로드 — 품목 검색 재고원가 KRW 환산용 (실패 시 fallback 1380 유지)
    void api.fetchTodayExchangeRate().then((fx) => { this._fxRate = fx.rate_krw_per_usd; }).catch(() => {});

    // 서버 구성 편집기(별탭)가 저장하면 이 탭을 그 자리에서 갱신한다.
    //   opener 로 직접 만지지 않고 통지로 받는 이유: 딜 탭이 여러 개 열려 있어도
    //   전부 갱신돼야 하고, 편집기가 우리 window 를 직접 조작하지 않는 편이 낫다.
    //   새로고침이 아니라 refetch 라 편집 드래프트·스크롤이 살아 있다.
    this._dealChannelOff = subscribeDealChanged(this.data.dealId, () => {
      void this.reloadAfterServerCommand();
    });

    // 딜 생성 모드: 탭 닫기/새로고침 대비 pagehide 에서 빈 드래프트 정리(keepalive).
    //   앱 내 이동은 onUnmount 가 처리. 완료/취소로 끝냈으면 스킵.
    if (this.data.newMode) {
      this._pageHideHandler = () => {
        if (!this._newDone) api.cleanupDraftIfEmpty(this.data.dealId, { keepalive: true });
      };
      window.addEventListener('pagehide', this._pageHideHandler);
    }

    this._keydownHandler = (e: KeyboardEvent) => {
      // Esc — 열린 모달/팝오버를 위에서부터 하나만 닫는다.
      //   여러 개가 겹쳐 있을 수 있으므로 한 번에 하나만 닫아야 예측 가능하다.
      if (e.key === 'Escape') {
        if (this.closeTopLayer()) e.preventDefault();
        return;
      }
      // Ctrl/Cmd+S — 편집 중이면 브라우저 저장 대신 구성 저장.
      if ((e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey)) {
        const edit = this.data.editConfig;
        if (!edit) return;                 // 편집 모드 아니면 기본동작 유지
        e.preventDefault();                // 브라우저 저장 대화상자 차단
        if (edit.saving) return;
        if (editConfigDirty(edit.items, edit.baseline)) void this.handleEditComplete();
      }
    };
    window.addEventListener('keydown', this._keydownHandler);
  }

  // 앱 내 이동(다른 메뉴/뒤로가기/다른 딜)으로 이 뷰가 사라질 때 —
  //   딜 생성 모드에서 완료/취소 안 하고 이탈했으면 빈 드래프트면 비활성화.
  private _newDone = false;
  private _pageHideHandler: (() => void) | null = null;
  private _keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  /** 서버 구성 편집기(별탭) 저장 알림 구독 해제자. */
  private _dealChannelOff: (() => void) | null = null;
  override onUnmount(): void {
    if (this._dealChannelOff) { this._dealChannelOff(); this._dealChannelOff = null; }
    if (this._pageHideHandler) {
      window.removeEventListener('pagehide', this._pageHideHandler);
      this._pageHideHandler = null;
    }
    if (this._keydownHandler) {
      window.removeEventListener('keydown', this._keydownHandler);
      this._keydownHandler = null;
    }
    if (this.data.newMode && !this._newDone) {
      api.cleanupDraftIfEmpty(this.data.dealId);  // 서버가 빈 드래프트만 정리
    }
    super.onUnmount();
  }
  // 딜 RFQ 소스(가격 받은 것) 로드 — 레퍼런스 팝오버(품목)·견적 모달 공용.
  // ⚠ 중복 정의 2개(onMount용/모달용)를 통합 (Step 4, 2026-07-03 — TS2393 해소).
  //   onMount 경로는 refreshMain, 모달 열림 상태면 해당 모달만 추가 갱신.
  private async _loadQuoteSources(): Promise<void> {
    try {
      const { sources, quotePrices } = await api.fetchDealRfqSources(this.data.full.id);
      this.data.quoteSources = sources;
      this.data.quotePrices  = quotePrices;
      this.refreshMain();
      if (this.data.quoteModal.open)     this.refreshQuoteModal();
      if (this.data.quoteViewModal.open) this.refreshQuoteViewModal();
    } catch { /* 소스 없어도 페이지는 동작 */ }
  }
  /**
   * 품목 소스 후보 로드 — 서버 구성품과 개별 품목 **둘 다**.
   *
   * `_loadQuoteSources`(=/rfq/deal-sources)와 다른 API 다. 그쪽은 니즈에 걸린
   * RFQ 만 보므로 **재고**가 없고, 실제로는 재고에서 나가는 경우가 흔하다.
   * 서버 헤더행은 살 물건이 아니라 묶음이므로 제외한다(파트넘버도 없다).
   */
  private async _loadItemSources(): Promise<void> {
    const parts = (this.data.full.items ?? [])
      .filter((it) => it.is_active && it.line_type !== 'server')
      .map((it) => it.option_part)
      .filter((p): p is string => !!p);
    if (parts.length === 0) return;

    this.data.itemSources = { ...this.data.itemSources, loading: true };
    try {
      const res = await api.fetchSourceCandidates(parts, this.data.dealId);
      this.data.itemSources = { loading: false, stock: res.stock, rfq: res.rfq };
    } catch {
      // 소스를 못 불러와도 화면은 살아 있어야 한다 — select 가 '후보 없음' 이 된다.
      this.data.itemSources = { ...this.data.itemSources, loading: false };
    }
    this.refreshMain();
  }

  // 하단 활동 히스토리 로드(최신순). 이벤트 생성 액션 후에도 재호출해 갱신.
  private async _loadDealEvents(): Promise<void> {
    try {
      this.data.dealEvents = await api.fetchDealEvents(this.data.dealId);
      this.refreshMain();
    } catch { /* 히스토리 없어도 페이지는 동작 */ }
  }

  // 채팅 렌더/갱신/스크롤 → components/ChatPanel.ts 소유 (Step 5).
  // 메시지 동기화는 syncChat() (constructor 아래) 하나로.

  // 좌측 메인 컬럼 (채팅 제외) — 리전 wrapper 로 감싸 부분 갱신 주소화.
  //   wrapper 는 block div — .deal-detail-main(flex column, gap:24px)의 flex item 이
  //   각 리전 1개씩이라 간격은 그대로. 직접자식 선택자가 없어 레이아웃 영향 없음.
  private renderMainInner(full: api.DealFull): string {
    return DEAL_REGIONS
      .map((r) => `<div class="deal-region" data-region="${r}">${this.renderRegion(r, full)}</div>`)
      .join('');
  }

  // 리전 1개의 HTML — renderMainInner 와 리전 부분갱신이 공유하는 단일 소스.
  // 부분 갱신은 코어 SolaView.refreshRegion(selector, htmlFn) 을 그대로 재사용:
  //   this.refreshRegion(`.deal-region[data-region="${r}"]`, () => this.renderRegion(r))
  // (코어가 [data-bind] 재등록·자식 SolaView 경계 보호까지 해줌 — 수동 innerHTML 대비 이점)
  private renderRegion(region: DealRegion, full: api.DealFull = this.data.full): string {
    switch (region) {
      case 'info':
        return renderInfoSection(full, this.data.assignees);
      case 'items':
        return renderItemsSection(full, this.activeConfig(full), this.data.editConfig,
                                  this.data.itemMenuId,
                                  this.data.expandedServerIds, this.data.serverMenuId,
                                  this.data.itemSources, this.data.quoteKindMenuOpen);
      case 'sourcing':
        return renderSourcingSection(this.data.sourcing?.needs ?? [], this.data.needMenuId, this.data.tagEditor);
      case 'quote':
        return renderQuoteSection(full, this.data.quotes?.quotes ?? [], this.data.quoteMenuId);
      case 'history':
        return renderHistorySectionStub(full, this.data.contextCollapsed, this.data.dealEvents);
    }
  }

  // 모든 모달을 한 영역(.ddb-modals)에 모은다 → refreshModals/refreshBody 한 번이
  // 모든 모달을 덮음. 새 모달은 여기 추가만 하면 별도 refresh 메서드가 필요 없다.
  // (고빈도 입력용 targeted refresh 는 wrap 클래스로 따로 갱신 가능 — 아래 refreshQuote* )
  private renderModals(): string {
    return `
      <div class="deal-customer-modal-wrap">${renderDealCustomerModal(this.data.customerModal)}</div>
      <div class="deal-contact-modal-wrap">${renderDealContactModal(this.data.contactModal)}</div>
      ${renderItemSearchPanel(this.data.itemSearch, this._fxRate)}
      ${renderRfqViewModal(this.data.rfqViewBubble)}
      ${renderNeedCreateModal(this.data.needCreate)}
      ${renderNeedEditModal(this.data.needEdit)}
      <div class="deal-quote-modal-wrap">${renderQuoteModal(this.data.quoteModal, this.data.full, this.data.quoteSources, this.data.quotePrices, this.data.itemSources)}</div>
      <div class="deal-quote-view-modal-wrap">${renderQuoteViewModal(this.data.quoteViewModal, this.data.quoteSources, this.data.quotePrices, this._fxRate)}</div>
      ${renderDealCancelModal(this.data.cancelModal, this.data.full.name)}
      ${renderServerAddModal(this.data)}
      <div class="deal-qdl-wrap">${renderQuoteDownloadModal(this.data.quoteDownloadModal)}</div>
    `;
  }

  // ─── 렌더 ────────────────────────────────────────────────────────────
  // loader 패턴 채택 후 — this.data 가 처음부터 완전. 분기 없음.
  // 채팅(aside)은 ChatPanel 인스턴스 임베드 — refreshHeader/Main/Modals 가
  // 각자의 영역만 innerHTML 교체하므로 컴포넌트 DOM 은 안정 (기존 설계 그대로).
  render(): HTMLElement {
    const full = this.data.full;
    return htmlSolaWithInstance(this)`
      <article class="deal-detail-page">
        <div class="deal-detail-body">
          <div class="ddb-header">${renderHeader(full, this.data.newMode, this.data.statusPopoverOpen, this.data.statusHistory)}</div>
          <div class="deal-detail-grid">
            <main class="deal-detail-main">${this.renderMainInner(full)}</main>
            <aside class="deal-detail-side deal-detail-side-chat">${this.chat}</aside>
          </div>
          <div class="ddb-modals">${this.renderModals()}</div>
        </div>
      </article>
    `;
  }

  // 견적 모달은 renderModals 안에 있어 refreshModals/refreshBody 가 이미 덮는다.
  // 아래 두 메서드는 "그 모달만" 다시 그리는 targeted 갱신 — 환율/단가 입력처럼
  // 고빈도이거나, 모달 열기/닫기처럼 전체 refresh 가 과한 경우에만 쓴다.
  /**
   * 견적 모달 wrap 을 다시 그리되 **스크롤 위치를 넘겨준다**.
   *
   * 두 견적 모달(생성/수정, 보기)은 blur(change) 마다 전체를 다시 그린다.
   * 한 줄의 단가가 바뀌면 그 행의 합계·마진율만이 아니라 하단 총계까지 여러
   * 자리가 같이 바뀌어서, 갱신 대상을 손으로 열거하는 부분 갱신이 오히려
   * 위험하다(계산식이 늘 때 화면이 조용히 어긋난다).
   *
   * 문제는 innerHTML 을 갈아끼우면 스크롤을 들고 있던 .deal-quote-modal-body
   * 가 **새 노드로 교체**되면서 scrollTop 이 0 이 된다는 것이다. 12줄짜리
   * 구성에서 아래쪽 단가를 하나 넣고 빠져나올 때마다 화면이 맨 위로 튀었다.
   *
   * scrollTop 대입은 강제로 레이아웃을 유발하므로 innerHTML 직후에 넣어도
   * 반영된다. 내용이 줄어 스크롤 범위를 넘으면 브라우저가 알아서 자른다.
   *
   * 두 모달이 같은 마크업(.deal-quote-modal-body)을 쓰므로 한 곳에 둔다.
   * 각자 복붙해두면 다음에 한쪽만 고치게 된다.
   */
  private swapQuoteModal(wrapSelector: string, html: string): void {
    const wrap = this._rootElement?.querySelector(wrapSelector);
    if (!wrap) return;

    const prevTop = wrap.querySelector<HTMLElement>('.deal-quote-modal-body')?.scrollTop ?? 0;
    wrap.innerHTML = html;
    if (prevTop > 0) {
      const body = wrap.querySelector<HTMLElement>('.deal-quote-modal-body');
      if (body) body.scrollTop = prevTop;
    }
  }

  private refreshQuoteModal(): void {
    this.swapQuoteModal(
      '.deal-quote-modal-wrap',
      renderQuoteModal(this.data.quoteModal, this.data.full, this.data.quoteSources, this.data.quotePrices, this.data.itemSources),
    );
  }
  /**
   * 임대 단가 타이핑 중 — 파생 값만 제자리 갱신 (커서 보존).
   *
   * `refreshQuoteModal` 은 모달을 innerHTML 로 통째 교체하므로 입력 중인 칸의
   * 커서가 끝으로 튄다. 검색 결과만 갈아 끼우는 `_updateCustomerModalResults`
   * 와 같은 이유로, 여기서는 결과 셀만 덮는다. 실제 갱신 규칙은
   * render-quote 의 `patchRentalUnitOutputs` 가 갖는다 — 렌더러와 같은 파일에
   * 두어야 표식(data-rental-out)과 갱신이 한 곳에서 같이 움직인다.
   */
  private patchQuoteRentalOutputs(): void {
    const wrap = this._rootElement?.querySelector('.deal-quote-modal-wrap');
    if (!wrap) return;
    patchRentalUnitOutputs(wrap, this.data.quoteModal);
  }

  private refreshQuoteViewModal(): void {
    this.swapQuoteModal(
      '.deal-quote-view-modal-wrap',
      renderQuoteViewModal(this.data.quoteViewModal, this.data.quoteSources, this.data.quotePrices, this._fxRate),
    );
  }
  private refreshCustomerModal(): void {
    const wrap = this._rootElement?.querySelector('.deal-customer-modal-wrap');
    if (wrap) wrap.innerHTML = renderDealCustomerModal(this.data.customerModal);
  }
  private refreshContactModal(): void {
    const wrap = this._rootElement?.querySelector('.deal-contact-modal-wrap');
    if (wrap) wrap.innerHTML = renderDealContactModal(this.data.contactModal);
  }
  // 검색 중 결과 영역만 갱신 — 검색 input 포커스/커서 보존.
  private _updateCustomerModalResults(): void {
    const box = this._rootElement?.querySelector('.deal-customer-modal-wrap .deal-party-modal-scroll');
    if (box) box.innerHTML = renderDealCustomerModalResults(this.data.customerModal);
  }
  private _updateContactModalResults(): void {
    const box = this._rootElement?.querySelector('.deal-contact-modal-wrap .deal-party-modal-scroll');
    if (box) box.innerHTML = renderDealContactModalResults(this.data.contactModal);
  }

  // ─── 헤더 (sticky) ──────────────────────────────────────────────────
  // ─── 헤더/기본정보/거래처·담당자/메모 렌더 ──────────────────────────
  //   → deal-detail/render-info.ts 로 이동 (Step 3c, 2026-07-03)

  // ─── 좌측 — 품목 ───────────────────────────────────────────────────
  // 현재 활성 구성 — activeConfigId 매칭 → 최저 config_no fallback (대표 개념 없음).
  private activeConfig(full: api.DealFull): api.DealConfiguration | null {
    const configs = [...(full.configurations ?? [])].sort((a, b) => a.config_no - b.config_no);
    if (configs.length === 0) return null;
    return (
      configs.find((c) => c.id === this.data.activeConfigId)
      ?? configs[0]
    );
  }

  // ─── 좌측 — 품목 ─────────────────────────────────────────────────
  // 렌더는 deal-detail/render-items.ts (Step 3b 에서 분리) — 호출은 renderMainInner/renderModals.




  // ─── 좌측 — 소싱 ─────────────────────────────────────────────────
  // 렌더는 deal-detail/render-sourcing.ts (Step 3a 에서 분리) — 호출은 renderMainInner/renderModals.


  // ─── 좌측 — 견적 ──────────────────────────────────────────────────
  // 렌더는 deal-detail/render-quote.ts (Step 2 에서 분리) — 호출은 renderMainInner.

  // 옛 stub — 호출 자리 호환용 (실제로는 renderQuoteSection 사용)

  // ─── 좌측 — 원문/조건/히스토리 (Step 3) ────────────────────────────
  // ─── 메모 — 기타 조건 + 원문 (admin-ux-preview 기준) ───────────────
  // renderHistorySectionStub → deal-detail/render-info.ts 로 이동 (Step 3c).

  // ════════════════════════════════════════════════════════════════
  // 핸들러: 코어 — 헤더/상태 팝오버/메모 접기/딜 생성 모드/별표/레퍼런스
  // ════════════════════════════════════════════════════════════════
  handleToggleOriginal(): void {
    this.data.contextCollapsed.original = !this.data.contextCollapsed.original;
    this.refreshMain();
  }

  handleToggleHistory(): void {
    this.data.contextCollapsed.history = !this.data.contextCollapsed.history;
    this.refreshMain();
  }

  // ─── 우측 — 상태 (12 단계 stepper, 세로) ───────────────────────────
  // ─── 헤더 상태 팝오버 토글 ─────────────────────────────────────────
  async handleStatusPopoverToggle(): Promise<void> {
    this.data.statusPopoverOpen = !this.data.statusPopoverOpen;
    this.refreshHeader();
    // 열 때 상태 전이 이력 로드 (최신 반영 위해 매번 재조회).
    if (this.data.statusPopoverOpen) {
      try {
        this.data.statusHistory = await api.fetchDealStatusHistory(this.data.dealId);
        if (this.data.statusPopoverOpen) this.refreshHeader();
      } catch { /* 무시 — 이력 없이 단계만 표시 */ }
    }
  }
  handleStatusPopoverClose(): void {
    if (!this.data.statusPopoverOpen) return;
    this.data.statusPopoverOpen = false;
    this.refreshHeader();
  }

  // 구성 재시작 — 견적 반려 + 구성 확정취소 + 상태 리셋.
  async handleRestartConfig(): Promise<void> {
    if (!confirm(
      '품목 구성부터 다시 시작하시겠습니까?\n'
      + '발송됨/채택됨 견적은 반려 처리되고, 확정된 품목 구성은 모두 확정 취소됩니다.')) return;
    try {
      await api.restartDealConfig(this.data.dealId);
      await this.reloadFull();
      this.data.quotes = await api.fetchDealQuotes(this.data.dealId);  // 반려 반영
      this.data.statusHistory = null;   // 다음 팝오버 열 때 새 이력 재조회
      this.refreshBody();
      this.syncChat();   // '🔄 구성 재시작' 시스템 메시지 즉시 반영
      this.toast('구성을 재시작했습니다.');
    } catch (err) {
      alert(`구성 재시작 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ─── 딜 취소 (확인 + 사유 필수 모달) ───────────────────────────────
  handleCancelOpen(): void {
    this.data.cancelModal = { open: true, reason: null, saving: false, error: '' };
    this.refreshModals();
  }
  handleCancelClose(): void {
    if (this.data.cancelModal.saving) return;   // 저장 중엔 닫기 무시
    this.data.cancelModal.open = false;
    this.refreshModals();
  }
  handleCancelReasonPick(_e: Event, target: HTMLElement): void {
    const reason = target.dataset.reason;
    if (!reason) return;
    this.data.cancelModal.reason = reason;
    this.data.cancelModal.error  = '';
    this.refreshModals();
  }
  async handleCancelConfirm(): Promise<void> {
    const m = this.data.cancelModal;
    if (!m.reason || m.saving) return;
    m.saving = true;
    m.error  = '';
    this.refreshModals();
    try {
      // expected_version = 낙관적 잠금(마이그114). 편집 중 다른 변경 있으면 409.
      await api.cancelDeal(this.data.dealId, m.reason, this.data.full.version);
      m.open   = false;
      m.saving = false;
      await this.reloadFull();
      this.data.statusHistory = null;   // 상태 이력 재조회
      this.refreshBody();
      this.syncChat();                  // '🚫 딜 취소' 시스템 메시지 반영
      this.toast('딜을 취소했습니다.');
    } catch (err) {
      m.saving = false;
      m.error  = err instanceof Error ? err.message : '취소에 실패했습니다.';
      this.refreshModals();
    }
  }

  // ─── 헤더 — 딜 링크(이름+URL 하이퍼링크) 복사 ─────────────────────────
  // 슬랙/노션/메일에 붙이면 "딜 이름"이 링크 텍스트, deal URL 이 href 인 하이퍼링크로 인식.
  //   clipboard 에 text/html(리치) + text/plain(폴백)을 동시에 기록.
  async handleCopyDealLink(_e: Event, target: HTMLElement): Promise<void> {
    const full      = this.data.full;
    const url       = `${location.origin}/deal/${full.id}`;
    const dealNo    = full.deal_no || `DEAL-${full.id}`;
    const name      = full.name?.trim();
    const label     = name ? `${dealNo} ${name}` : dealNo;   // "DEAL-… 딜이름"
    const labelHtml = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html      = `<a href="${url}">${labelHtml}</a>`;
    const text      = `${label} ${url}`;   // 리치 미지원 환경 폴백
    const done = () => {
      this.toast('딜 링크를 복사했습니다.');
      target.textContent = '✓';
      setTimeout(() => { target.textContent = '⧉'; }, 1000);
    };
    try {
      if (navigator.clipboard && 'write' in navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([new ClipboardItem({
          'text/html':  new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        })]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      done();
    } catch {
      // 리치 복사 차단/미지원 → plain 폴백 재시도
      try { await navigator.clipboard.writeText(text); done(); }
      catch { this.toast('복사에 실패했습니다.'); }
    }
  }

  // ─── 딜 생성 모드 ─────────────────────────────────────────────────
  // 취소 — 드래프트 딜 삭제(soft delete) 후 목록으로.
  private _newBusy = false;
  async handleNewCancel(): Promise<void> {
    if (this._newBusy) return;
    if (!confirm('작성 중인 딜을 취소할까요? 저장된 내용은 삭제됩니다.')) return;
    this._newBusy = true;
    this._newDone = true;   // onUnmount 의 빈 드래프트 정리 스킵(여기서 명시 삭제)
    try {
      await api.deleteDeal(this.data.dealId);
    } catch { /* 이미 없더라도 목록으로 진행 */ }
    navigate('/deals');
  }


  // 별표 토글 (개인 설정 — deal.updated_at 미갱신). 낙관적 갱신.
  async handleWatchToggle(_e: Event, target: HTMLElement): Promise<void> {
    const btn = target.closest<HTMLElement>('[data-deal-id]');
    const id  = btn?.getAttribute('data-deal-id');
    if (!id || !btn) return;
    const next = !this.data.full.is_watched;
    this.data.full.is_watched = next;
    btn.classList.toggle('is-on', next);
    btn.textContent = next ? '★' : '☆';
    btn.setAttribute('aria-pressed', next ? 'true' : 'false');
    btn.setAttribute('title', next ? '관심 해제' : '관심 등록');
    try {
      await api.setDealWatch(id, next);
    } catch (err) {
      this.data.full.is_watched = !next;   // 롤백
      btn.classList.toggle('is-on', !next);
      btn.textContent = !next ? '★' : '☆';
      btn.setAttribute('aria-pressed', !next ? 'true' : 'false');
      alert(`관심 설정 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ════════════════════════════════════════════════════════════════
  // 핸들러: 소싱 — RFQ 버블/태그/품목 메뉴/니즈 생성
  // ════════════════════════════════════════════════════════════════
  // ─── 소싱 RFQ 버블 보기 모달 (6D) ─────────────────────────────────
  handleRfqBubbleView(_e: Event, target: HTMLElement): void {
    const id = target.dataset.rfqId;
    if (!id) return;
    const bubble = (this.data.sourcing?.needs ?? [])
      .flatMap((n) => n.rfqs)
      .find((b) => b.id === id);
    if (!bubble) return;
    this.data.rfqViewBubble = bubble;
    this.refreshModals();
  }
  handleRfqViewClose(): void {
    if (!this.data.rfqViewBubble) return;
    this.data.rfqViewBubble = null;
    this.refreshModals();
  }
  // 특정 거래처(RFQ) 구매요청 토글 (마이그140) — 🛒 표시 + 모달 버튼 갱신.
  async handleRfqPurchaseToggle(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.rfqId;
    if (!id) return;
    const requested = target.dataset.requested !== '1';   // 현재 요청상태면 취소, 아니면 요청
    try {
      await api.setRfqPurchaseRequest(id, requested);
      // 로컬 상태 갱신: 해당 rfq bubble
      for (const n of this.data.sourcing?.needs ?? []) {
        for (const b of n.rfqs) if (b.id === id) b.purchase_requested = requested;
      }
      this.data.rfqViewBubble = null;   // 처리 후 모달 닫기
      this.refreshMain();
      this.refreshModals();
      this.toast(requested ? '구매요청 했습니다.' : '구매요청을 취소했습니다.');
    } catch (err) {
      this.toast(err instanceof Error ? err.message : '구매요청 실패');
    }
  }

  // ─── 소싱 태그 편집 (6D) ──────────────────────────────────────────
  handleNeedTagOpen(_e: Event, target: HTMLElement): void {
    const needId = target.dataset.needId;
    const kind   = target.dataset.kind as 'sales' | 'memo' | undefined;
    if (!needId || !kind) return;
    const cur     = this.data.tagEditor;
    const willOpen = !(cur?.needId === needId && cur?.kind === kind);
    this.data.tagEditor = willOpen ? { needId, kind } : null;
    this.refreshMain();
    // 테이블 가로 스크롤(overflow)에 잘리지 않도록 팝오버를 fixed 로 띄우고
    // 트리거 좌표 기준으로 위치를 잡는다 (필요 시 위로 뒤집음).
    if (willOpen) this.positionTagPop();
  }

  private positionTagPop(): void {
    requestAnimationFrame(() => {
      const pop = document.querySelector<HTMLElement>('.deal-tag-pop');
      if (!pop) return;
      const cell    = pop.closest('.deal-tag-cell');
      const trigger = cell?.querySelector<HTMLElement>('.deal-tag-trigger');
      if (!trigger) return;
      const r  = trigger.getBoundingClientRect();
      const ph = pop.offsetHeight;
      let top = r.bottom + 4;
      if (top + ph > window.innerHeight - 8) top = Math.max(8, r.top - ph - 4);
      pop.style.position = 'fixed';
      pop.style.left     = `${r.left}px`;
      pop.style.top      = `${top}px`;
    });
  }
  handleNeedTagClose(): void {
    if (!this.data.tagEditor) return;
    this.data.tagEditor = null;
    this.refreshMain();
  }

  // ─── 품목 행 작업(⋯) 메뉴 ─────────────────────────────────────────
  handleItemMenuOpen(_e: Event, target: HTMLElement): void {
    const id = target.dataset.dealItemId;
    if (!id) return;
    const willOpen = this.data.itemMenuId !== id;
    this.data.itemMenuId = willOpen ? id : null;
    this.data.serverMenuId = null;   // 위 handleServerMenuToggle 주석과 같은 이유
    this.refreshMain();
    // 테이블 가로 overflow 에 잘리지 않게 메뉴를 fixed 로 띄움 (태그 팝오버와 동일).
    if (willOpen) this.positionItemMenu();
  }
  handleItemMenuClose(): void {
    if (!this.data.itemMenuId) return;
    this.data.itemMenuId = null;
    this.refreshMain();
  }
  private positionItemMenu(): void {
    requestAnimationFrame(() => {
      const menu = document.querySelector<HTMLElement>('.deal-item-menu');
      if (!menu) return;
      const cell    = menu.closest('.deal-tag-cell');
      const trigger = cell?.querySelector<HTMLElement>('.deal-item-menu-trigger');
      if (!trigger) return;
      const r  = trigger.getBoundingClientRect();
      const mh = menu.offsetHeight;
      let top = r.bottom + 4;
      if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 4);
      menu.style.position = 'fixed';
      menu.style.left     = `${Math.max(8, r.right - menu.offsetWidth)}px`;
      menu.style.top      = `${top}px`;
    });
  }

  /**
   * ⋯ → '니즈 생성' — 그 품목으로 니즈 생성 모달을 prefill 해서 연다 (딜 연결).
   *
   * 서버 구성품이면 부모 서버에서 기본값을 끌어온다:
   *   컨디션 = 부모 server_condition 투영(NEW→F/S, USED→REF) — 자식은 컨디션이 없다
   *   수량   = 품목 수량 × 부모 서버 대수
   * 둘 다 **기본값 제안**이라 모달에서 사람이 바꿀 수 있다. 다른 컨디션이
   * 필요할 때 쓰는 경로가 바로 여기다.
   */
  handleItemNeedCreateFromRow(_e: Event, target: HTMLElement): void {
    const id = target.dataset.dealItemId;
    if (!id) return;
    const items = this.data.full.items ?? [];
    const item  = items.find((it) => String(it.id) === id);
    if (!item) return;

    const parent = item.parent_id == null
      ? null
      : items.find((it) => String(it.id) === String(item.parent_id));

    const condition = parent
      ? (projectServerCondition(parent.server_condition) ?? '')
      // 인식 불가('미확인') 컨디션은 비워서 유효 컨디션을 다시 고르게 한다.
      : (conditionGroup(item.requested_condition) === '미확인' ? '' : (item.requested_condition ?? ''));

    const qty = (item.quantity ?? 1)
      * (parent ? Math.max(1, Number(parent.server_quantity) || 1) : 1);

    this.data.itemMenuId = null;
    this.data.needCreate = {
      ...emptyNeedCreate(),
      open:        true,
      option_part: item.option_part ?? '',
      item_text:   item.description_raw ?? item.item_description ?? '',   // 등록 파트는 카탈로그 원문
      category:    item.category ?? null,
      condition,
      quantity:    String(qty),
      sourceDealItemId: String(item.id),   // need_origin 링크
    };
    this.refreshMain();    // 메뉴 닫기
    this.refreshModals();  // prefill 된 니즈 생성 모달 열기
  }

  // ─── 니즈 생성 (6D) ──────────────────────────────────────────────
  // 품목 체크박스가 선택돼 있으면 → 모달 없이 그 품목들로 즉시 니즈 생성(이 딜에 연결).
  // 체크 없으면 → 모달(딜 연결 없음 기본)로 수동 생성.
  async handleNeedCreateOpen(): Promise<void> {
    const checkedIds = this.getCheckedItemIds();
    if (checkedIds.length > 0) {
      await this.createNeedsFromCheckedItems(checkedIds);
      return;
    }
    this.data.needCreate = { ...emptyNeedCreate(), open: true, dealLinked: false };
    this.refreshModals();
  }

  // 품목 헤더 체크박스 → 전체 선택/해제 (순수 DOM, 재렌더 없음).
  handleItemsSelectAll(_e: Event, target: HTMLElement): void {
    const checked = (target as HTMLInputElement).checked;
    (target as HTMLInputElement).indeterminate = false;
    this._rootElement
      ?.querySelectorAll<HTMLInputElement>('.deal-items-table tbody .deal-item-check input[type="checkbox"]')
      .forEach((cb) => { cb.checked = checked; });
  }

  // 행 체크 변경 → 헤더 체크박스 동기화. 헤더는 "전부 선택했을 때만" 체크로 표시하고,
  //   일부만 선택된 상태는 눌린 것처럼 보이지 않게 빈 상태로 둔다(현민 — indeterminate 제거).
  handleItemCheckSync(): void {
    const root = this._rootElement;
    if (!root) return;
    const head = root.querySelector<HTMLInputElement>('.deal-items-table thead .deal-item-check input[type="checkbox"]');
    if (!head) return;
    const boxes = Array.from(root.querySelectorAll<HTMLInputElement>('.deal-items-table tbody .deal-item-check input[type="checkbox"]'));
    const n = boxes.filter((b) => b.checked).length;
    head.checked = boxes.length > 0 && n === boxes.length;
    head.indeterminate = false;
  }

  // 품목 테이블에서 체크된 deal_item id 들.
  //   읽기 뷰: tr[data-deal-item-id] 에 실제 id. 편집 뷰: 체크박스 data-item-id 에 실제 id
  //   (편집 행 tr 은 client lineId 를 쓰므로 체크박스 자체에서 DB id 를 읽는다).
  private getCheckedItemIds(): string[] {
    const root = this._rootElement;
    if (!root) return [];
    const ids: string[] = [];
    root.querySelectorAll<HTMLInputElement>('.deal-items-table tbody .deal-item-check input[type="checkbox"]')
      .forEach((cb) => {
        if (!cb.checked) return;
        const id = cb.dataset.itemId ?? cb.closest('tr')?.dataset.dealItemId ?? undefined;
        if (id) ids.push(id);
      });
    return ids;
  }

  /**
   * 체크된 품목들 → 니즈 생성(이 딜에 연결).
   *
   * ★ 규칙은 전부 서버에 있다(2026-07-31). 화면은 선택한 id 만 보낸다.
   *   - 서버 구성품은 독립 컨디션이 없어 부모 서버 컨디션을 투영한다(NEW→F/S,
   *     USED→REF). 여기서 판정하면 `requested_condition` 이 항상 null 이라
   *     '미확인'으로 무조건 막혔다.
   *   - 수량은 `품목 수량 × 부모 서버 대수` 다. 화면은 부모를 몰라 곱하지 못했다.
   *   - `need_origin`(니즈 ← 출처 라인) 은 니즈 INSERT 와 같은 트랜잭션에서만
   *     정확히 쓸 수 있다.
   *   구성품에 다른 컨디션이 필요하면 그 품목만 **별도 니즈로** 따로 만든다.
   */
  private async createNeedsFromCheckedItems(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    try {
      const res = await api.createNeedsFromDealItems(this.data.dealId, ids);
      this.data.sourcing = await api.fetchDealSourcing(this.data.dealId);
      this.refreshMain();
      const merged = res.source_count - res.created.length;
      this.toast(
        `${res.created.length}개 니즈가 생성되었습니다.`
        + (merged > 0 ? ` (같은 파트·컨디션 ${merged}건 합산)` : ''),
      );
    } catch (err) {
      alert(`니즈 생성 실패: ${(err as Error).message}`);
    }
  }

  // 딜 연결 라디오 토글 — state 만 갱신(라디오 표시는 브라우저가 처리, 입력 보존).
  handleNeedCreateLink(_e: Event, target: HTMLElement): void {
    this.data.needCreate = { ...this.data.needCreate, dealLinked: target.dataset.linked === '1' };
  }

  // 품목 검색 (250ms 디바운스, 결과 컨테이너만 갱신 — 입력 포커스 보존)
  handleNeedCreateSearch(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value;
    this.data.needCreate.query = q;
    if (this._needSearchTimer) window.clearTimeout(this._needSearchTimer);
    if (q.trim().length < 2) {
      this.data.needCreate.hits = [];
      this.data.needCreate.searching = false;
      this.updateNeedSearchResults();
      return;
    }
    this._needSearchTimer = window.setTimeout(() => { void this.runNeedSearch(q); }, 250);
  }
  private async runNeedSearch(q: string): Promise<void> {
    this.data.needCreate.searching = true;
    this.updateNeedSearchResults();
    try {
      const res = await api.searchAll(q, 0, 20);
      if (this.data.needCreate.query !== q) return;
      this.data.needCreate.hits = res.catalog ?? [];
    } catch {
      this.data.needCreate.hits = [];
    } finally {
      if (this.data.needCreate.query === q) {
        this.data.needCreate.searching = false;
        this.updateNeedSearchResults();
      }
    }
  }
  private updateNeedSearchResults(): void {
    const box = this._rootElement?.querySelector('#need-search-results');
    if (box) box.innerHTML = renderNeedSearchResultsHtml(this.data.needCreate);
  }
  handleNeedCreatePick(_e: Event, target: HTMLElement): void {
    const key = target.dataset.key;
    if (!key) return;
    const hit = this.data.needCreate.hits.find((h) => `${h.source}::${h.option_part}` === key);
    if (!hit) return;
    const m = this.data.needCreate;
    m.option_part = hit.option_part;
    // 원문(description_raw) 우선, 없으면 정제본 폴백 — 전 사이트 일관
    m.item_text   = catalogHitDescription(hit);
    m.category    = hit.source;
    m.query = ''; m.hits = []; m.searching = false;
    this.refreshModals();
  }
  handleNeedCreateClear(): void {
    const m = this.data.needCreate;
    m.option_part = ''; m.item_text = ''; m.category = null; m.query = ''; m.hits = [];
    this.refreshModals();
  }
  handleNeedCreateClose(): void {
    if (this.data.needCreate.saving) return;
    this.data.needCreate = { ...this.data.needCreate, open: false };
    this.refreshModals();
  }
  handleNeedCreateField(_e: Event, target: HTMLElement): void {
    const el    = target as HTMLInputElement | HTMLSelectElement;
    const field = el.dataset.field;
    if (!field) return;
    const m = this.data.needCreate;
    if (field === 'quantity') m.quantity = el.value.replace(/[^0-9]/g, '');
    else if (field === 'option_part') m.option_part = el.value;
    else if (field === 'item_text')   m.item_text = el.value;
    else if (field === 'condition')   m.condition = el.value;
  }
  handleNeedCreateTag(_e: Event, target: HTMLElement): void {
    const input = target as HTMLInputElement;
    const tag   = input.dataset.tag;
    if (!tag) return;
    const m = this.data.needCreate;
    m.salesTags = input.checked ? [...new Set([...m.salesTags, tag])] : m.salesTags.filter((t) => t !== tag);
  }
  async handleNeedCreateSubmit(): Promise<void> {
    const m = this.data.needCreate;
    if (m.saving) return;
    const qty = Number(m.quantity);
    if (!m.option_part && !m.item_text) { alert('품목을 검색해 선택해 주세요.'); return; }
    // 빈 값 + 인식 불가('미확인' 그룹) 모두 차단 — 유효한 컨디션 필수.
    if (conditionGroup(m.condition) === '미확인') { alert('컨디션을 선택해 주세요.'); return; }
    if (!Number.isFinite(qty) || qty < 1) { alert('수량을 1 이상으로 입력해 주세요.'); return; }
    this.data.needCreate = { ...m, saving: true };
    this.refreshModals();
    try {
      await api.createNeed({
        deal_id:             m.dealLinked ? Number(this.data.dealId) : null,   // 딜 연결 여부
        option_part:         m.option_part || null,
        item_text:           m.item_text || null,
        category:            m.category,
        quantity:            qty,
        requested_condition: m.condition,
        sales_request_tags:  api.toSalesTags(m.salesTags),
        // 품목 행에서 연 경우에만 출처를 남긴다(need_origin).
        source_deal_item_id: m.sourceDealItemId ? Number(m.sourceDealItemId) : null,
        // 이 모달은 사람이 수량을 확정하는 자리다 → 파생 재계산 대상에서 제외.
        qty_is_manual:       true,
      });
      // 소싱 섹션 새로고침
      this.data.sourcing = await api.fetchDealSourcing(this.data.dealId);
      this.data.needCreate = { ...this.data.needCreate, open: false, saving: false };
      this.refreshMain();      // 소싱 섹션 갱신
      this.refreshModals();    // 모달 닫기
      this.toast(m.dealLinked
        ? '니즈가 생성되었습니다.'
        : '미연결 니즈가 생성되었습니다. (소싱 페이지에서 확인)');
    } catch (err) {
      this.data.needCreate = { ...this.data.needCreate, saving: false };
      this.refreshModals();
      alert(`니즈 생성 실패: ${(err as Error).message}`);
    }
  }

  async handleNeedTagToggle(_e: Event, target: HTMLElement): Promise<void> {
    const input  = target as HTMLInputElement;
    const needId = input.dataset.needId;
    const kind   = input.dataset.kind as 'sales' | 'memo' | undefined;
    const tag    = input.dataset.tag;
    if (!needId || !kind || !tag) return;
    const need = this.data.sourcing?.needs.find((n) => n.id === needId);
    if (!need) return;
    const arrKey = kind === 'sales' ? 'sales_request_tags' : 'sourcing_memo_tags';
    const cur    = need[arrKey];
    const next   = input.checked ? [...new Set([...cur, tag])] : cur.filter((t) => t !== tag);
    need[arrKey] = next;   // 낙관적
    this.refreshMain();    // 칩 즉시 반영 (팝오버는 tagEditor 유지로 열린 채)
    this.positionTagPop(); // 재렌더로 잃은 fixed 좌표 다시 적용
    try {
      await api.updateNeedTags(needId, { [arrKey]: next });
    } catch (err) {
      need[arrKey] = cur;  // 롤백
      this.refreshMain();
      this.positionTagPop();
      alert(`태그 수정 실패: ${(err as Error).message}`);
    }
  }

  // ════════════════════════════════════════════════════════════════
  // 채팅 — components/ChatPanel.ts 로 승격 (Step 5). 렌더·핸들러·state 전부 컴포넌트 소유.
  // ════════════════════════════════════════════════════════════════

  // ════════════════════════════════════════════════════════════════
  // 핸들러: 견적 — 생성/수정 모달·보기 모달·발송/채택/반려·payload 조립
  // ════════════════════════════════════════════════════════════════
  // ─── [+ 견적 생성] 클릭 — 모달 열기 (Step 3b) ──────────────────────
  //
  // 동작:
  //   1) 품목 표에서 체크된 deal_item id 들 수집
  //   2) 체크 0개 → deal_item 전체 prefill
  //   3) 체크 1개 이상 → 그 id 들만 prefill
  //   4) modal state 초기화 + open=true → refreshQuoteModal
  async handleCreateQuote(_e?: Event, target?: HTMLElement): Promise<void> {
    // 드롭다운에서 고른 유형. 값이 없으면 판매 — 다른 경로(단축키 등)로 들어와도
    //   기존 동작이 그대로 유지된다.
    const kind: 'SALE' | 'RENTAL' =
      target?.dataset.quoteType === 'RENTAL' ? 'RENTAL' : 'SALE';
    this.data.quoteKindMenuOpen = false;
    const full     = this.data.full;
    // 견적 대상 = '확정(locked) 구성'의 품목만. 딜 전체 deal_item(구성 2 등) 아님.
    // (견적 계산 버튼 자체가 locked 구성에서만 노출되므로 active = 확정 구성)
    const active   = this.activeConfig(full);
    const configs  = [...(full.configurations ?? [])].sort((a, b) => a.config_no - b.config_no);
    const lowestId = configs[0]?.id ?? null;
    const all      = full.items ?? [];
    const items    = active
      ? all.filter((it) =>
          it.configuration_id === active.id
          || (active.id === lowestId && it.configuration_id == null))
      : all;

    // 1. 모달 즉시 open — 환율은 fallback (1380) 으로 시작 (UX 빠르게)
    this.data.quoteModal = {
      open:         true,
      editId:       null,
      name:         active?.name ?? '구성 1',
      fxRate:       '1380',
      vatIncluded:  false,
      // 판매단가 prefill(직접 편집). 매입단가는 소스/직접입력으로 별도 채움.
      //
      // 서버 트리는 **헤더 바로 뒤에 그 자식들**이 오도록 정렬한다. 견적은
      // 이 순서 그대로 sort_order 를 받고, 다시 열 때도 그 순서로 복원된다.
      // deal_item 의 sort_order 는 최상위와 자식이 번호 공간을 공유해서
      // 그대로 쓰면 헤더와 자식이 섞인다.
      items:        DealDetailView.orderQuoteRows(items).map((it) => dealItemToModalRow(it, true)),
      // 진입 시 서버 구성품을 **펼친 채로** 연다 (2026-08-24 현민).
      //   견적을 만들며 구성품 판매가를 바로 채우는 흐름이라 매번 펼치는 게
      //   손이 갔다. 서버 헤더행의 deal_item_id 를 전부 담아 시작한다.
      //   접기는 그대로 동작한다(handleQuoteServerToggle 이 이 Set 을 토글).
      expandedServers: new Set<string>(
        items.filter((it) => it.line_type === 'server')
             .map((it) => String(it.id))),
      // (구 QUOTE-009 기본 접힘 — 2026-08-24 기본 펼침으로 전환)
      quoteType:    kind,                   // 진입 시점에 정해진다 — 모달 안에서 못 바꾼다
      presets:      [],
      presetsLoading: false,
      presetsError: '',
      saving:       false,
      errorMessage: '',
      costPopRow:   null,                   // 임대총원가 ⓘ 팝오버 — 처음엔 닫혀 있다
      refPopRow:    null,                   // 레퍼런스 "더보기" 팝오버 — 처음엔 닫혀 있다
      baseVersion:  this.data.full.version,
    };
    this.refreshMain();                    // 드롭다운 닫힘 반영
    this.refreshQuoteModal();
    void this._loadQuoteSources();
    if (kind === 'RENTAL') void this._loadRentalPresets();

    // 2. 환율 + cost basis prefill 병렬 fetch — 비동기, 받으면 갱신
    //    실패해도 모달은 그대로 동작 (수동 입력 가능)
    try {
      const [fx, prefill] = await Promise.all([
        api.fetchTodayExchangeRate().catch(() => null),
        api.fetchDealQuotePrefill(this.data.full.id).catch(() => null),
      ]);

      if (!this.data.quoteModal.open) return;     // 사이에 닫혔으면 무시

      if (fx) {
        this.data.quoteModal.fxRate = String(fx.rate_krw_per_usd);
      }

      if (prefill) {
        // deal_item_id → cost_basis_usd map
        const costMap = new Map<string, number | null>();
        for (const it of prefill.items) {
          costMap.set(it.deal_item_id, it.cost_basis_usd);
        }
        // 각 row 의 cost_basis_usd 채움 (수동 입력 안 된 자리만)
        for (const row of this.data.quoteModal.items) {
          if (row.deal_item_id && costMap.has(row.deal_item_id)) {
            row.cost_basis_usd = costMap.get(row.deal_item_id) ?? null;
          }
        }
      }

      this.refreshQuoteModal();
    } catch {
      // 무시 — fallback 그대로
    }
  }

  // ─── 견적 모달 닫기 (취소 / overlay 클릭 / X 버튼) ─────────────────
  handleCloseQuoteModal(): void {
    if (this.data.quoteModal.saving) return;            // 저장 중엔 잠금
    this.data.quoteModal = emptyQuoteModalState();
    this.refreshQuoteModal();
  }

  // ─── 견적 모달 메타 변경 (구성명 / 환율 / 부가세 토글) ────────────
  //
  // input 이벤트 — state 만 업데이트 (re-render X — input focus 보존).
  // change 이벤트 (blur) — state + 부분 refresh (합계/마진율 갱신).
  handleQuoteMetaChange(e: Event): void {
    const target = e.target as HTMLInputElement;
    const field  = target.dataset.metaField;
    if (!field) return;

    const m = this.data.quoteModal;
    if (field === 'vatIncluded') {
      m.vatIncluded = target.checked;
    } else if (field === 'name') {
      m.name = target.value;
    } else if (field === 'fxRate') {
      m.fxRate = target.value;
    }

    if (e.type === 'change') this.refreshQuoteModal();
  }

  // ─── 견적 모달 행 변경 (수량 / 매입단가 / 판매단가) ────────────────
  handleQuoteRowChange(e: Event): void {
    const target = e.target as HTMLInputElement;
    const field  = target.dataset.rowField;
    const idx    = Number(target.dataset.rowIndex);
    if (!field || !Number.isFinite(idx)) return;
    const row = this.data.quoteModal.items[idx];
    if (!row) return;

    if (field === 'quantity')      row.quantity      = target.value;
    if (field === 'sellUnit') {
      // 판매단가(개당, KRW) 직접 입력 — 이제 파생 아님(마진율·원가와 독립).
      row.unit_price_krw = target.value.replace(/[^0-9]/g, '');
    }
    if (field === 'costKrw') {
      // 기본 구성 품목의 매입단가는 0 고정이다(마이그 174) — 섀시 값에 포함이라
      // 별도 매입원가가 없다. input 은 disabled 지만 위임 핸들러는 DOM 을 믿지 않는다.
      if (row.isBasePart) { target.value = '0'; return; }
      // 매입단가(개당, KRW) 직접 입력 → KRW cost basis 로 저장, 소스는 해제.
      row.costBasisAmount   = target.value.replace(/[^0-9]/g, '');
      row.costBasisCurrency = 'KRW';
      row.sourceType = null; row.sourceRefId = null;   // 직접 입력이면 소스 해제(선택 없음)
    }
    if (field === 'optionPart')    row.option_part   = target.value || null;
    if (field === 'description')   row.description    = target.value || null;
    if (field === 'costBasisUsd') {
      // string → number | null. 빈 입력은 null.
      const v = target.value.trim();
      if (!v) {
        row.cost_basis_usd = null;
      } else {
        const n = Number(v);
        row.cost_basis_usd = Number.isFinite(n) ? n : null;
      }
    }

    if (e.type === 'change') this.refreshQuoteModal();
  }

  // ─── 임대 견적 (마이그 178/179) ──────────────────────────────────
  /**
   * 임대 계산식 프리셋 로딩. 모달을 열 때 한 번만 부른다.
   *
   * ★ 실패해도 모달은 그대로 돌아야 한다. 판매 견적은 프리셋과 무관하고,
   *   여기서 throw 하면 프리셋 서버가 잠깐 흔들릴 때 견적 작성 전체가 막힌다.
   *   임대 표가 사유를 대신 보여준다(presetsError).
   */
  private async _loadRentalPresets(): Promise<void> {
    const m = this.data.quoteModal;
    if (m.presets.length > 0 || m.presetsLoading) return;
    m.presetsLoading = true;
    try {
      // 활성 프리셋만. 꺼 둔 프리셋을 새 견적에 걸 수는 없다.
      const res = await rentalPresetApi.listRentalPresets({ includeInactive: false });
      if (!this.data.quoteModal.open) return;          // 사이에 닫혔으면 버린다
      this.data.quoteModal.presets      = res.presets;
      this.data.quoteModal.presetsError = '';
    } catch (err) {
      if (!this.data.quoteModal.open) return;
      this.data.quoteModal.presetsError =
        `임대 계산식을 불러오지 못했습니다: ${err instanceof Error ? err.message : 'unknown'}`;
    } finally {
      this.data.quoteModal.presetsLoading = false;
      if (this.data.quoteModal.open) this.refreshQuoteModal();
    }
  }

  /**
   * '견적 계산' ▼ — 판매/임대를 **들어가기 전에** 고른다.
   *
   * ★ 모달 안 토글이 아니다. 판매와 임대는 입력 항목도 결과도 달라서, 모달
   *   안에서 갈아타게 두면 "지금 뭘 만들고 있는지" 가 흐려지고 반쯤 채운
   *   입력이 유형 사이를 떠다닌다. 진입 시점에 정하면 모달은 한 가지 일만 한다.
   */
  handleQuoteKindMenuOpen(): void {
    this.data.quoteKindMenuOpen = !this.data.quoteKindMenuOpen;
    this.refreshMain();
  }
  handleQuoteKindMenuClose(): void {
    if (!this.data.quoteKindMenuOpen) return;
    this.data.quoteKindMenuOpen = false;
    this.refreshMain();
  }

  /**
   * 임대총원가 ⓘ — 원가 항목 내역 팝오버를 연다.
   *
   * 같은 행을 다시 누르면 닫는다(토글). 한 번에 하나만 열어 두므로 다른 행을
   * 누르면 그쪽으로 옮겨간다 — 여러 개가 겹치면 어느 행의 내역인지 읽히지 않는다.
   */
  handleQuoteRentalCostOpen(_e: Event, target: HTMLElement): void {
    const idx = Number(target.dataset.rowIndex);
    if (!Number.isFinite(idx)) return;
    const m = this.data.quoteModal;
    m.costPopRow = m.costPopRow === idx ? null : idx;
    this.refreshQuoteModal();
    if (m.costPopRow !== null) this.positionRentalCostPopover();
  }

  /**
   * 임대 수익성 조건 팝오버 위치 잡기.
   *
   * ★ 왜 CSS 만으로 안 되는가 — 팝오버가 있는 표는 모달 본문
   *   (.deal-quote-modal-body, overflow-y: auto) 안에 있다. 셀에 붙인
   *   absolute 배치는 그 스크롤 컨테이너가 잘라 내서, 아래쪽 항목과 합계
   *   줄이 통째로 잘려 보였다(현민 제보). 스크롤 밖으로 나가려면 fixed 로
   *   띄우고 좌표를 직접 잡는 수밖에 없다 — 니즈 ⋯ 메뉴(positionNeedMenu)와
   *   같은 수법이다.
   *
   * ★ 열려 있는 동안에는 스크롤을 걱정하지 않아도 된다. 팝오버는 화면 전체를
   *   덮는 오버레이(.deal-tag-pop-overlay)와 함께 뜨고, 그 위에서 굴린 휠은
   *   모달 본문을 움직이지 못한다. 아무 데나 누르면 닫힌다.
   *
   * 좌표 규칙(아래 배치·오른쪽 끝 맞춤·화면 밖이면 접기)은 순수 함수
   * `rentalCostPopoverPos` 가 갖는다. 브라우저 없이 검증할 수 있게 떼어 뒀다.
   */
  private positionRentalCostPopover(): void {
    requestAnimationFrame(() => {
      const idx = this.data.quoteModal.costPopRow;
      if (idx === null) return;
      const pop = this._rootElement?.querySelector<HTMLElement>('.deal-quote-costpop');
      const btn = this._rootElement?.querySelector<HTMLElement>(
        `.deal-quote-info[data-row-index="${idx}"]`);
      if (!pop || !btn) return;

      const r = btn.getBoundingClientRect();
      // 좌표 규칙은 render-quote 의 순수 함수가 갖는다 — 여기서는 재지 않는다.
      const { left, top } = rentalCostPopoverPos(
        r, pop.offsetWidth, pop.offsetHeight, window.innerWidth, window.innerHeight,
      );
      pop.style.left = `${left}px`;
      pop.style.top  = `${top}px`;
    });
  }

  handleQuoteRentalCostClose(): void {
    if (this.data.quoteModal.costPopRow === null) return;
    this.data.quoteModal.costPopRow = null;
    this.refreshQuoteModal();
  }

  /**
   * 레퍼런스 "더보기" 팝오버 — costPop 과 같은 규약(토글, 한 번에 하나).
   * 팝오버는 스크롤 컨테이너에 잘리므로 fixed 로 띄우고 rAF 로 좌표를 잡는다.
   */
  handleQuoteRefMoreOpen(_e: Event, target: HTMLElement): void {
    const idx = Number(target.dataset.rowIndex);
    if (!Number.isFinite(idx)) return;
    // 편집 모달과 뷰(저장된 견적) 모달이 같은 셀을 쓴다 — 열려 있는 쪽에 붙는다.
    const isView = this.data.quoteViewModal.open;
    const st = isView ? this.data.quoteViewModal : this.data.quoteModal;
    st.refPopRow = st.refPopRow === idx ? null : idx;
    if (isView) this.refreshQuoteViewModal(); else this.refreshQuoteModal();
    if (st.refPopRow !== null) this.positionRefPopover();
  }

  handleQuoteRefMoreClose(): void {
    const isView = this.data.quoteViewModal.open;
    const st = isView ? this.data.quoteViewModal : this.data.quoteModal;
    if (st.refPopRow === null) return;
    st.refPopRow = null;
    if (isView) this.refreshQuoteViewModal(); else this.refreshQuoteModal();
  }

  /** 레퍼런스 줄의 거래처명 클릭 → 해당 딜 새 탭(DealListView 와 같은 방식). */
  handleQuoteRefOpenDeal(_e: Event, target: HTMLElement): void {
    const id = target.dataset.dealId;
    if (!id) return;
    window.open(`/deal/${encodeURIComponent(id)}`, '_blank', 'noopener');
  }

  private positionRefPopover(): void {
    requestAnimationFrame(() => {
      const idx = this.data.quoteModal.refPopRow;
      if (idx === null) return;
      const pop = this._rootElement?.querySelector<HTMLElement>('.deal-quote-refpop');
      const btn = this._rootElement?.querySelector<HTMLElement>(
        `.deal-quote-ref-more[data-row-index="${idx}"]`);
      if (!pop || !btn) return;
      const r = btn.getBoundingClientRect();
      const { left, top } = rentalCostPopoverPos(
        r, pop.offsetWidth, pop.offsetHeight, window.innerWidth, window.innerHeight,
      );
      pop.style.left = `${left}px`;
      pop.style.top  = `${top}px`;
    });
  }

  /** 라인의 임대 입력 3종(계산식·기간·임대 단가). */
  handleQuoteRentalChange(e: Event): void {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const field  = target.dataset.rentalField;
    const idx    = Number(target.dataset.rowIndex);
    if (!field || !Number.isFinite(idx)) return;
    const row = this.data.quoteModal.items[idx];
    if (!row) return;

    if (field === 'presetId')      row.rentalPresetId    = target.value;
    if (field === 'termMonths')    row.rentalTermMonths  = target.value.replace(/[^0-9]/g, '');
    if (field === 'manualUnitKrw') row.rentalManualUnitKrw = target.value.replace(/[^0-9]/g, '');

    // 계산식·기간이 바뀌면 단가가 통째로 달라지므로 즉시 다시 그린다.
    if (field === 'presetId' || e.type === 'change') { this.refreshQuoteModal(); return; }

    // ★ 임대 단가는 **타이핑하는 동안** 파생 값이 따라와야 한다 (2026-08-20).
    //   서안드레: "처음에 목표마진율에 따라 자동으로 계산되어 채워집니다.
    //             수동으로 적으면 그 때부터 해당 금액으로 재계산됩니다."
    //   기획안도 임대 단가 칸만 input 이벤트로 재계산한다.
    //
    //   전체를 다시 그리면 입력칸이 새 노드로 갈려 커서가 끝으로 튀므로
    //   (그래서 예전엔 change 로 미뤄 뒀다), 결과 셀만 제자리에서 덮는다.
    //   기간 칸은 여기 해당하지 않는다 — 기간이 바뀌면 원가·할증·자동 단가·
    //   ⓘ 팝오버까지 행 전체가 달라져서 change 에서 통째로 다시 그린다.
    if (field === 'manualUnitKrw') this.patchQuoteRentalOutputs();
  }

  // ─── 견적 항목 소스 선택 (딜 RFQ) → 매입단가(원가) 스냅샷 ──────────
  /**
   * 견적 모달 소스 선택 — 재고 + RFQ 통합 (2026-08-04).
   *
   * select value 는 `타입:id` 다(`stock:417` / `deal_rfq:88` / `rfq_history:12`).
   * 예전엔 rfq_id 만 담고 타입을 'deal_rfq' 로 못박아서, 재고를 넣으면 rfq 로
   * 잘못 저장됐다. 딜 품목행(handleItemSource)과 같은 규약을 쓴다.
   */
  handleQuoteSourceChange(e: Event): void {
    const target = e.target as HTMLSelectElement;
    const idx = Number(target.dataset.rowIndex);
    const row = this.data.quoteModal.items[idx];
    if (!row) return;
    // 기본 구성 품목은 매입 소스를 고르지 않는다(마이그 174) — 원가 0 고정.
    if (row.isBasePart) return;

    const [kind, refId] = (target.value || '').split(':');
    if (!kind || !refId) {
      row.sourceType = null; row.sourceRefId = null;
      row.costBasisAmount = ''; row.costBasisCurrency = null;
      this.refreshQuoteModal();
      return;
    }
    row.sourceType  = kind;
    row.sourceRefId = refId;

    if (kind === 'stock') {
      // 재고 원가는 아직 비어 있을 수 있다(이카운트 export 에 원가 없음).
      //   0 으로 치환하지 않는다 — '확인 필요' 가 근거 없음의 표시다.
      const st = this.data.itemSources.stock.find((x) => String(x.stock_id) === refId);
      row.costBasisAmount   = st?.cost_amount != null ? String(st.cost_amount) : '';
      row.costBasisCurrency = st?.cost_currency ?? null;
    } else {
      const rq = this.data.itemSources.rfq.find((x) => String(x.rfq_id) === refId);
      row.costBasisAmount   = rq?.price_amount != null ? String(rq.price_amount) : '';
      row.costBasisCurrency = rq?.price_currency ?? null;
    }
    // 소스는 매입단가(원가)만 덮어씀. 판매단가는 건드리지 않음.
    this.refreshQuoteModal();   // 매입단가/합계/마진 즉시 반영
  }

  // ─── 견적 보기 모달 — 소스 변경 (즉시 저장) ──────────────────────
  // 매입단가(원가)만 소스 KRW 로 덮어씀. 판매단가는 read-only 로 유지.
  handleQuoteViewSourceChange(e: Event): void {
    const target = e.target as HTMLSelectElement;
    const idx = Number(target.dataset.itemIndex);
    const q = this.data.quoteViewModal.data;
    if (!q || !Number.isFinite(idx)) return;
    const it = (q.items ?? []).filter((x) => x.is_active !== false)[idx];
    if (!it) return;
    // 기본 구성 품목은 소스를 고르지 않는다(마이그 174) — 원가 0 고정.
    if (it.is_base_part) return;

    const rfqId = target.value;
    if (!rfqId) {
      it.source_type = null; it.source_ref_id = null;
      it.cost_basis_amount = null; it.cost_basis_currency = null;
    } else {
      const src = this.data.quoteSources.find((s) => String(s.rfq_id) === rfqId);
      if (src) {
        it.source_type        = 'deal_rfq';
        it.source_ref_id       = String(src.rfq_id);
        it.cost_basis_amount   = src.price_amount != null ? Number(src.price_amount) : null;
        it.cost_basis_currency = src.price_currency;
      }
    }
    this.refreshQuoteViewModal();       // 매입단가/합계/마진 즉시 반영
    void this._persistQuoteView();      // 서버 반영(즉시 저장)
  }

  // ─── 견적 보기 모달 — 매입단가 직접 입력 (즉시 저장) ──────────────
  // 직접 입력 → KRW cost basis 로 저장, 소스는 선택없음(해제).
  handleQuoteViewCostChange(e: Event): void {
    const target = e.target as HTMLInputElement;
    const idx = Number(target.dataset.itemIndex);
    const q = this.data.quoteViewModal.data;
    if (!q || !Number.isFinite(idx)) return;
    const it = (q.items ?? []).filter((x) => x.is_active !== false)[idx];
    if (!it) return;
    // 기본 구성 품목은 저장된 견적에서도 매입단가 0 고정(마이그 174 스냅샷).
    if (it.is_base_part) { target.value = '0'; return; }

    const digits = target.value.replace(/[^0-9]/g, '');
    it.cost_basis_amount   = digits ? Number(digits) : null;
    it.cost_basis_currency = digits ? 'KRW' : null;
    it.source_type = null; it.source_ref_id = null;   // 직접 입력이면 소스 해제(선택 없음)

    this.refreshQuoteViewModal();
    void this._persistQuoteView();
  }

  // 보기 모달 편집(소스/매입단가)을 서버에 반영. 판매단가·수량은 그대로 재전송.
  private async _persistQuoteView(): Promise<void> {
    const q = this.data.quoteViewModal.data;
    if (!q) return;
    // 매입·마진 환산은 **오늘 환율**로 한다 (2026-08-24 현민).
    //   저장된 q.fx_rate_krw_per_usd 는 만들 때 못박은 기록이고, 이 모달은
    //   '지금 이 판매가면 마진이 얼마인가' 를 보려고 여는 화면이다.
    //   임대도 같다 — 보기 화면에서 환율이 닿는 곳은 매입단가·매입 합계뿐이고
    //   임대 단가·마진율은 저장값을 그대로 읽는다. 고객 금액이 원가에서
    //   계산되는 것은 생성·수정 모달이고 그쪽은 저장된 환율로 연다.
    const stored = q.fx_rate_krw_per_usd != null ? Number(q.fx_rate_krw_per_usd) : 0;
    const fx = this._fxRate > 0 ? this._fxRate : stored;
    const active = (q.items ?? []).filter((x) => x.is_active !== false);
    // 합계 재계산 — quote-math 규칙 재사용(판매단가 기준 판매합계, 매입 완결시만 마진).
    const totals = computeQuoteTotals({
      fxRate: fx > 0 ? String(fx) : '',
      vatIncluded: false,
      items: active.map((it) => ({
        quantity:          it.quantity != null ? String(it.quantity) : '',
        unit_price_krw:    it.unit_price_krw != null ? String(it.unit_price_krw) : '',
        costBasisAmount:   it.cost_basis_amount != null ? String(it.cost_basis_amount) : '',
        costBasisCurrency: it.cost_basis_currency,
      })),
    });
    const body: api.QuoteFullUpdateInput = {
      total_amount_krw:      totals.totalKrw > 0 ? totals.totalKrw : null,
      amount_with_vat_krw:   totals.amountWithVat > 0 ? totals.amountWithVat : null,
      cost_basis_amount_krw: totals.costComplete && totals.costBasisKrw > 0 ? totals.costBasisKrw : null,
      margin_krw:            totals.marginKrw,
      margin_rate:           totals.marginRate,
      items: active.map((it, idx) => ({
        id:                  it.id,
        deal_item_id:        it.deal_item_id ? Number(it.deal_item_id) : null,
        sort_order:          idx,
        category:            it.category,
        option_part:         it.option_part,
        description:         it.description,
        quantity:            it.quantity,
        unit_price_krw:      it.unit_price_krw,
        unit_price_usd:      null,
        cost_basis_usd:      it.cost_basis_usd,
        source_type:         it.source_type as api.QuoteItemSyncInput['source_type'],
        source_ref_id:       it.source_ref_id ? Number(it.source_ref_id) : null,
        cost_basis_amount:   it.cost_basis_amount,
        cost_basis_currency: it.cost_basis_currency,
      })),
    };
    try {
      await api.updateQuoteFull(q.id, body);
      // 리스트/합계 카드 동기화.
      //
      // ★ refreshBody() 를 쓰면 안 된다. 그건 헤더 + 전 리전 + **모달 전체
      //   (.ddb-modals)** 를 다시 그리는데, 지금 이 모달이 그 안에 있다.
      //   즉 저장할 때마다 열려 있는 모달의 DOM 을 통째로 날리고 새로 만든다.
      //   스크롤이 맨 위로 튀던 진짜 원인이 이것이다(모달 자체 재렌더가 아니라).
      //
      //   여기서 새로 받아온 건 this.data.quotes 하나뿐이고, 그걸 읽는 곳은
      //   견적 리전(총액 카드 + 견적 목록)뿐이다. 그래서 그 리전만 갈아끼우면
      //   결과가 동일하면서 모달은 살아 있는다.
      //
      //   저장 뒤 refreshQuoteViewModal() 도 지웠다. 모달이 읽는 것은
      //   quoteViewModal.data 이고 그건 저장 **전에** 이미 갱신·렌더했다.
      //   await 사이에 바뀌는 값이 없으므로 같은 HTML 을 한 번 더 그릴 뿐이다.
      this.data.quotes = await api.fetchDealQuotes(this.data.full.id);
      this.refreshRegion('.deal-region[data-region="quote"]', () => this.renderRegion('quote'));
    } catch (err) {
      this.toast(`매입단가 저장 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  /**
   * 견적 항목 삭제 — 행 제거(저장 시 백엔드 diff 가 삭제).
   *
   * 서버 헤더행을 지우면 **그 구성품도 함께** 지운다. 헤더만 빼면 구성품이
   * 부모 없는 고아로 남아, 화면엔 낱개 파트처럼 보이고 저장 시에도
   * `parent_deal_item_id` 만 살아 있는 깨진 트리가 된다.
   * (구성품 행에는 삭제 버튼이 없으므로 여기 오는 건 헤더 또는 개별 품목뿐)
   */
  handleQuoteItemDelete(_e: Event, target: HTMLElement): void {
    const idx = Number(target.dataset.rowIndex);
    if (!Number.isFinite(idx)) return;
    const m   = this.data.quoteModal;
    const row = m.items[idx];
    if (!row) return;

    if (row.lineType === 'server' && row.deal_item_id != null) {
      const key = String(row.deal_item_id);
      m.items = m.items.filter((r, i) =>
        i !== idx && String(r.parentDealItemId ?? '') !== key);
      m.expandedServers.delete(key);   // 펼침 상태도 같이 정리
    } else {
      m.items.splice(idx, 1);
    }
    this.refreshQuoteModal();
  }
  // 견적 항목 추가 — 빈 행(id=null) → 저장 시 신규 insert.
  handleQuoteItemAdd(): void {
    this.data.quoteModal.items.push(emptyQuoteModalRow());
    this.refreshQuoteModal();
  }

  // _loadQuoteSources — 상단(부분 렌더 유틸 구역)의 통합 정의 사용 (중복 제거, Step 4).

  // ─── 바로 견적 — 견적 생성 모달을 '판매 단가→견적 단가' prefill 로 열기 ──
  // (가견적: 막지 않고 모달로 넘어가 거기서 숫자 수정/저장. 원가·마진 없이 시작)
  async handleQuickQuote(): Promise<void> {
    const full     = this.data.full;
    const active   = this.activeConfig(full);
    const configs  = [...(full.configurations ?? [])].sort((a, b) => a.config_no - b.config_no);
    const lowestId = configs[0]?.id ?? null;
    const all      = full.items ?? [];
    const items    = active
      ? all.filter((it) =>
          it.configuration_id === active.id
          || (active.id === lowestId && it.configuration_id == null))
      : all;
    if (items.length === 0) { alert('견적할 품목이 없습니다.'); return; }

    // 바로 견적 — 모달 없이 즉시 생성. 각 품목 판매 단가를 견적 단가로 사용.
    const fx = await api.fetchTodayExchangeRate().catch(() => null);
    const m: QuoteModalState = {
      ...emptyQuoteModalState(),
      name:   active?.name ?? '구성 1',
      fxRate: fx ? String(fx.rate_krw_per_usd) : '1380',
      items:  DealDetailView.orderQuoteRows(items).map((it) => dealItemToModalRow(it, true)),
    };
    // 바로 견적은 **막지 않는다**. 이름 그대로 가견적이라, 서버 판매가가 아직
    // 없어도 일단 만들어 두고 나중에 견적 수정에서 채운다. 번들가·구성품 합계
    // 검증은 '견적 계산' 모달의 저장에만 건다(거기서 고칠 자리가 있다).
    try {
      await api.createQuoteFull(this.buildQuoteBody(m));
      const [newQuotes, newFull] = await Promise.all([
        api.fetchDealQuotes(full.id),
        api.fetchDealFull(full.id),
      ]);
      this.data.quotes = newQuotes;
      this.data.full   = newFull;
      this.refreshBody();   // 견적 리스트/딜 상태
      this.syncChat();   // 견적 작성 시스템 메시지
      this.toast('바로 견적을 생성했습니다.');
    } catch (err) {
      alert(`바로 견적 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // 견적 모달 상태(m) → createQuoteFull 요청 body. 모달 저장과 '바로 견적' 공용.
  /**
   * 견적 헤더의 금액 4종. 유형에 따라 **다른 계산**에서 온다.
   *
   * ★ 임대의 '견적 금액'은 계약 총액(단가 × 수량 × 기간)이다. 견적 목록·딜
   *   캐시가 이 값을 그대로 쓰므로, 월 금액을 넣으면 딜 금액이 12분의 1로
   *   보인다. VAT 규칙은 판매와 같게 둔다(vatIncluded 면 그대로, 아니면 ×1.1).
   */
  private quoteAmounts(m: QuoteModalState): {
    total: number | null; vat: number | null; cost: number | null;
    margin: number | null; rate: number | null;
  } {
    if (m.quoteType === 'RENTAL') {
      const t = computeRentalTotals(m.items, parseNum(m.fxRate) ?? 0, m.presets);
      const vat = m.vatIncluded ? t.totalKrw : Math.round(t.totalKrw * 1.1);
      return {
        total:  t.totalKrw > 0 ? t.totalKrw : null,
        vat:    vat > 0 ? vat : null,
        cost:   t.complete && t.costBasisKrw > 0 ? t.costBasisKrw : null,
        margin: t.complete ? t.marginKrw : null,
        rate:   t.marginRate,
      };
    }
    const totals = computeQuoteTotals(m);
    return {
      total:  totals.totalKrw > 0 ? totals.totalKrw : null,
      vat:    totals.amountWithVat > 0 ? totals.amountWithVat : null,
      cost:   totals.costBasisKrw > 0 ? totals.costBasisKrw : null,
      margin: totals.marginKrw,
      rate:   totals.marginRate,
    };
  }

  private buildQuoteBody(m: QuoteModalState): api.QuoteFullCreateInput {
    const a = this.quoteAmounts(m);
    return {
      deal_id:                 Number(this.data.full.id),
      name:                    m.name.trim(),
      currency:                'KRW',
      quote_type:              m.quoteType,
      total_amount_usd:        null,
      total_amount_krw:        a.total,
      amount_with_vat_krw:     a.vat,
      cost_basis_amount_krw:   a.cost,
      margin_krw:              a.margin,
      margin_rate:             a.rate,
      fx_rate_krw_per_usd:     parseNum(m.fxRate),
      items: this.quoteItemPayloads(m),
    };
  }

  /**
   * 견적 행 순서 — **서버 그룹 먼저, 그다음 개별 파트**. 서버 헤더 바로 뒤에는
   * 그 자식들이 붙는다.
   *
   * 서버를 앞으로 보내는 이유는 딜 상세 품목표와 같은 순서를 쓰기 위해서다.
   * 거기서는 `serverRows + partRows` 로 조립해 서버가 항상 위인데(읽기·편집
   * 모드 모두), 견적 모달만 sort_order 순이라 딜에서 보던 순서와 견적서
   * 순서가 달랐다. 견적서는 고객에게 나가는 문서라 서버(주력 상품)가 먼저
   * 오는 편이 읽기도 자연스럽다.
   *
   * `deal_item.sort_order` 는 최상위와 자식이 번호 공간을 공유하므로
   * 그대로 정렬하면 헤더와 자식이 섞인다. 여기서 한 번 정리해 두면
   * 저장(sort_order = 배열 index)과 재조회가 같은 순서를 본다.
   * 이미 저장된 견적은 DB 의 sort_order 로 그리므로 영향받지 않는다
   * (발송된 견적서와 순서가 어긋나면 안 된다).
   */
  private static orderQuoteRows(
    items: readonly NonNullable<api.DealFull['items']>[number][],
  ): NonNullable<api.DealFull['items']>[number][] {
    const tops = items.filter((it) => it.parent_id == null)
      .sort((a, b) => {
        // 1순위: 서버 그룹(0) → 개별 파트(1). 2순위: 기존 sort_order.
        const rank = (x: typeof a): number => (x.line_type === 'server' ? 0 : 1);
        return rank(a) - rank(b) || a.sort_order - b.sort_order;
      });
    const out: typeof tops = [];
    for (const t of tops) {
      out.push(t);
      if (t.line_type !== 'server') continue;
      out.push(...items
        .filter((c) => c.parent_id != null && String(c.parent_id) === String(t.id))
        .sort((a, b) => a.sort_order - b.sort_order));
    }
    return out;
  }

  /**
   * 모달 행 → quote_item 저장 payload. 생성/수정이 공유한다.
   *
   * 서버 그룹(마이그 131)을 실어 보낸다:
   *   헤더행 → line_type='server', server_quantity=대수,
   *            unit_price_override=번들가 (판매단가 칸이 아니라 이 칸이 정본)
   *   구성품 → line_type='part', parent_deal_item_id=부모 **deal_item.id**
   *
   * `sort_order` 는 배열 순서 그대로 부여한다 — 헤더 바로 뒤에 그 자식들이
   * 오도록 모달이 순서를 유지하므로, 조회 시 sort_order 만으로 트리가 복원된다
   * (findItemsByQuoteId 는 정렬을 건드리지 않는다).
   */
  /**
   * 라인 → 저장 페이로드.
   *
   * ★ static 이었으나 인스턴스 메서드로 바꿨다. 임대 필드는 프리셋 목록과
   *   환율을 봐야 계산되는데 둘 다 모달 상태에 있다. 호출부 두 곳(생성·수정)이
   *   같은 매핑을 쓰는 구조는 그대로다 — 두 곳이 각자 매핑하면 서버 필드를
   *   한쪽에만 넣는 사고가 난다(원 주석의 경고).
   */
  private quoteItemPayloads(m: QuoteModalState): api.QuoteItemInput[] {
    const isRental = m.quoteType === 'RENTAL';
    const fx = parseNum(m.fxRate) ?? 0;
    return m.items
      .filter((r) => r.option_part || r.description)
      .map((row, idx) => {
        const isServer = row.lineType === 'server';
        // 임대 필드는 **최상위 라인에만** 붙는다. 서버 구성품은 매입원가의
        //   재료일 뿐이라 computeRentalLine 이 null 을 준다.
        const line = isRental ? computeRentalLine(row, m.items, fx, m.presets) : null;
        const rental = line ? toRentalItemPayload(line, row) : null;
        return {
          deal_item_id:    row.deal_item_id ? Number(row.deal_item_id) : null,
          sort_order:      idx,
          category:        row.category,
          option_part:     row.option_part,
          description:     row.description,
          // 서버 헤더의 수량은 server_quantity 가 정본이라 quantity 는 1 로 둔다
          // (DB CHECK 는 server_quantity 만 요구한다).
          quantity:        isServer ? 1 : parseInt0(row.quantity),
          // ★ 임대는 **적용 임대단가(시트 S열)** 가 계약 금액을 정한다.
          //   임대 모달에는 판매단가 칸이 없어 row.unit_price_krw 가 늘 비어 있고,
          //   서버 라인은 판매 규칙대로 null 로 눌린다. 그대로 두면 임대총원가·
          //   기간·원가 스냅샷은 저장되는데 **단가만 빠져** 월 임대 금액과 금액
          //   합계가 통째로 0 으로 저장된다(dev QUOTE-0270-9 에서 실측).
          //   계산 결과를 그대로 싣는다 — 서버 라인도 예외가 아니다.
          unit_price_krw:  line ? line.pricing.unitKrw
                                : (isServer ? null : parseInt0(row.unit_price_krw)),
          unit_price_usd:  null,
          cost_basis_usd:  row.cost_basis_usd,
          source_type:         isServer ? null : (row.sourceType as api.QuoteItemInput['source_type']),
          source_ref_id:       isServer || !row.sourceRefId ? null : Number(row.sourceRefId),
          cost_basis_amount:   isServer ? null : parseNum(row.costBasisAmount),
          cost_basis_currency: isServer ? null : row.costBasisCurrency,
          line_type:           isServer ? 'server' as const : 'part' as const,
          parent_deal_item_id: row.parentDealItemId ? Number(row.parentDealItemId) : null,
          server_quantity:     isServer ? (parseInt0(row.quantity) ?? 1) : null,
          // 서버 헤더는 자동(자식 합)/수동값을 저장한다 — 화면에서 본 그 값이
          //   저장돼야 다시 열었을 때 같은 값이 나온다.
          unit_price_override: isServer ? serverEffectiveSellKrw(row, m.items) : null,
          // is_base_part 는 보내지 않는다 — quote_item 에 컬럼이 없고(마이그 174),
          // 서버가 deal_item_id 조인으로 유도한다.
          //
          // 임대(마이그 178/179/185).
          //
          // ★ 판매일 때 **비우는 값을 명시해서 보낸다.** 서버의 saveRentalCosts 는
          //   'rental_costs 키가 없으면 기존 스냅샷 유지' 라서, 임대로 저장했던
          //   견적을 판매로 되돌릴 때 키를 빼면 스냅샷과 임대 컬럼이 그대로
          //   남는다 — 판매 견적인데 임대 원가가 붙어 있는 상태가 된다.
          ...(isRental ? (rental ?? { rental_costs: [] }) : RENTAL_CLEAR),
        };
      });
  }

  // 견적 모달 상태(m) → updateFullQuote(PUT /:id/full) body. items 는 id 기준 diff.
  private buildQuoteUpdateBody(m: QuoteModalState): api.QuoteFullUpdateInput {
    const a = this.quoteAmounts(m);
    return {
      expected_version:      m.baseVersion,   // 낙관적 잠금 (마이그 114)
      name:                  m.name.trim(),
      currency:              'KRW',
      quote_type:            m.quoteType,
      total_amount_usd:      null,
      total_amount_krw:      a.total,
      amount_with_vat_krw:   a.vat,
      cost_basis_amount_krw: a.cost,
      margin_krw:            a.margin,
      margin_rate:           a.rate,
      fx_rate_krw_per_usd:   parseNum(m.fxRate),
      // 생성과 같은 매핑 + 기존 행 id(백엔드 diff 기준)만 덧붙인다.
      // 두 곳이 각자 매핑하면 서버 필드를 한쪽에만 넣는 사고가 난다.
      items: this.quoteItemPayloads(m).map((it, idx) => ({
        ...it,
        id: m.items.filter((r) => r.option_part || r.description)[idx]?.id ?? null,
      })) as api.QuoteItemSyncInput[],
    };
  }

  /**
   * 견적 모달의 서버 구성 접기/펼치기 (QUOTE-009 — 기본 접힘).
   *
   * 펼침 상태는 모달 state 에 산다. 모달 안의 재계산·재렌더에는 유지되고,
   * 모달을 닫으면 `emptyQuoteModalState()` 로 초기화된다.
   */
  handleQuoteServerToggle(_e: Event, target: HTMLElement): void {
    const key = target.dataset.serverKey;
    if (!key) return;
    const set = this.data.quoteModal.expandedServers;
    if (set.has(key)) set.delete(key); else set.add(key);
    this.refreshQuoteModal();
  }

  // ─── 견적 모달 저장 — 신규(createQuoteFull) / 수정(updateQuoteFull) ──
  async handleSaveQuote(): Promise<void> {
    const m = this.data.quoteModal;
    if (m.saving) return;

    // 검증
    if (!m.name.trim()) {
      m.errorMessage = '구성명을 입력하세요.';
      this.refreshQuoteModal();
      return;
    }
    if (m.items.length === 0) {
      m.errorMessage = '견적 항목이 1개 이상 필요합니다.';
      this.refreshQuoteModal();
      return;
    }
    // 서버 관련 차단은 quoteBlockReasons 가 전담한다(저장 버튼도 그걸로 비활성).
    // 여기서 또 검사하면 사유가 둘로 갈려 어느 쪽을 고쳐야 하는지 흐려진다.
    // ★ 임대는 판매 사유를 쓰지 않는다. 판매 사유는 '서버 1대 판매가 미입력'
    //   처럼 판매단가 칸을 전제하는데, 임대에는 그 칸이 아예 없다(단가를 계산이
    //   만든다). 섞으면 임대 견적이 영원히 저장되지 않는다.
    const blocked = m.quoteType === 'RENTAL'
      ? rentalBlockReasons(m.items, parseNum(m.fxRate) ?? 0, m.presets)
      : quoteBlockReasons(m.items);
    if (blocked.length > 0) {
      m.errorMessage = blocked.join(' ');
      this.refreshQuoteModal();
      return;
    }
    // 판매단가 필수는 **개별 품목**에만 적용한다. 매입단가는 비어도 OK.
    //   서버 헤더행 → 번들가는 위 quoteBlockReasons 가 이미 확인했다.
    //   서버 구성품 → 판매가는 선택 입력이다. 전부 비어도 견적을 만들 수 있고,
    //                넣었다면 합계 일치 여부만 본다(QUOTE-008).
    // 임대에는 판매단가 칸이 없다 — 단가는 프리셋·기간에서 계산된다.
    //   위 rentalBlockReasons 가 이미 라인별로 확인했다.
    if (m.quoteType !== 'RENTAL') {
      const flat = m.items.filter((r) =>
        (r.option_part || r.description) && r.lineType !== 'server' && r.parentDealItemId == null);
      if (flat.some((r) => parseInt0(r.unit_price_krw) == null)) {
        m.errorMessage = '개별 품목에 판매단가를 입력하세요.';
        this.refreshQuoteModal();
        return;
      }
    }

    const editing = m.editId != null;

    m.saving       = true;
    m.errorMessage = '';
    this.refreshQuoteModal();

    try {
      if (editing) await api.updateQuoteFull(m.editId!, this.buildQuoteUpdateBody(m));
      else         await api.createQuoteFull(this.buildQuoteBody(m));

      // 성공 — quote 목록 + deal full (status 변경 반영) 재조회
      const [newQuotes, newFull] = await Promise.all([
        api.fetchDealQuotes(this.data.full.id),
        api.fetchDealFull(this.data.full.id),
      ]);
      this.data.quotes     = newQuotes;
      this.data.full       = newFull;
      this.data.quoteModal = emptyQuoteModalState();

      // 본문 전체 re-render — 견적 섹션 + 상태 stepper 둘 다 갱신
      this.refreshBody();
      this.refreshQuoteModal();
      this.syncChat();
      this.toast(editing ? '견적을 수정했습니다.' : '견적을 작성했습니다.');
    } catch (err) {
      if (err instanceof api.ApiConflictError) {
        // 409 (§3-1) — 자동 refetch(읽기 영역·version 최신화) + 모달 드래프트 유지.
        // 견적 항목을 외부에서 고치는 경로는 없어(봇 도구 없음) 병합 대상은 없고,
        // 배너 안내 후 재저장하면 최신 version 으로 성공.
        try {
          this.data.full = await api.fetchDealFull(this.data.full.id);
          this.refreshBody();
        } catch { /* refetch 실패 — 드래프트 유지, 안내만 */ }
        m.baseVersion  = this.data.full.version;
        m.errorMessage = '저장하지 않았습니다 — 딜이 다른 곳에서 수정되었습니다. 내용 확인 후 다시 저장해 주세요.';
        m.saving       = false;
        this.refreshQuoteModal();
        // 배너가 모달 하단(스크롤 밖)에 있어 조용히 멈춘 것처럼 보임 — 명시적 알림 (0712 피드백)
        alert(
          '저장하지 못했습니다 — 딜이 다른 곳에서 수정되었습니다.\n\n'
          + '최신 상태를 다시 불러왔고, 입력하신 내용은 그대로 유지되어 있습니다.\n'
          + '내용 확인 후 [수정 저장]을 다시 누르면 저장됩니다.',
        );
        return;
      }
      m.errorMessage = err instanceof Error ? err.message : '저장 실패';
      m.saving       = false;
      this.refreshQuoteModal();
      alert(`견적 저장 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ════════════════════════════════════════════════════════════════
  // 핸들러: 기본정보 — 딜 메타 패치(상태/담당자)·거래처·담당자·유입경로/분류
  // ════════════════════════════════════════════════════════════════
  // ─── 부분 갱신 — patchDeal 후 메모리 + 화면 동기화 ─────────────────
  // loader 가 처음에 받아온 데이터를 기반으로 작업 중. 상태/담당자 변경 같은
  // 작은 patch 는 서버 결과 (또는 낙관적 업데이트) 만 반영하고 refreshBody.
  // 실패 시엔 옛 값 보존 + 토스트 — 라우터 reload 안 부름 (페이지 그대로).
  async handleStatusChange(_e: Event, target: HTMLElement): Promise<void> {
    const newStatus = (target as HTMLSelectElement).value as api.DealStatus;
    if (!newStatus) return;
    const prev = this.data.full.status;
    try {
      await api.patchDeal(this.data.dealId, { status: newStatus });
      this.data.full = { ...this.data.full, status: newStatus };
      this.data.statusPopoverOpen = false;   // 변경 후 팝오버 닫기
      this.refreshHeader();
    } catch (err) {
      // 옛 값 되돌림 (select 도 함께)
      this.data.full = { ...this.data.full, status: prev };
      this.refreshHeader();
      alert(`상태 변경 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  async handleAssigneeChange(_e: Event, target: HTMLElement): Promise<void> {
    // 11-8: select.value 는 항상 string — backend zod 는 number|null 만 허용.
    // '' → null (미배정), 숫자 string → Number 변환.
    const raw = (target as HTMLSelectElement).value;
    const userId: number | null = raw === '' ? null : Number(raw);
    if (userId !== null && (!Number.isFinite(userId) || userId <= 0)) {
      alert('잘못된 담당자 id 입니다.');
      return;
    }
    const prev = this.data.full.assignee_user_id;
    try {
      await api.patchDeal(this.data.dealId, { assignee_user_id: userId });
      // DealFull.assignee_user_id 는 string|null (BIGINT → string 직렬화)
      // 옵션 비교용으로 set 시 다시 string 으로
      this.data.full = {
        ...this.data.full,
        assignee_user_id: userId === null ? null : String(userId),
      };
      this.refreshMain();
    } catch (err) {
      this.data.full = { ...this.data.full, assignee_user_id: prev };
      this.refreshMain();
      alert(`담당자 변경 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ─── 영업담당자 연락처(휴대폰) 인라인 저장 → users.phone ──────────
  // 견적서 공급자 담당자 연락처(F8)에 사용. assignee 의 user record 를 갱신.
  async handleAssigneePhoneSave(e: Event): Promise<void> {
    // keydown 은 Enter 일 때만 (버튼 클릭은 그대로 통과).
    if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Enter') return;
    const assigneeId = this.data.full.assignee_user_id;
    if (!assigneeId) return;
    const input = this._rootElement?.querySelector('.deal-assignee-phone-input') as HTMLInputElement | null;
    const phone = (input?.value ?? '').trim();
    try {
      const updated = await api.updateUserPhone(String(assigneeId), phone || null, this.data.dealId);
      // 로컬 assignees 목록 동기화 (재조회 없이 다음 렌더에 반영)
      this.data.assignees = this.data.assignees.map((a) =>
        a.id === updated.id ? { ...a, phone: updated.phone } : a,
      );
      this.toast('영업담당자 연락처를 저장했습니다.');
      void this._loadDealEvents();   // 히스토리 즉시 갱신
    } catch (err) {
      alert(`연락처 저장 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ─── 거래처 선택 모달 (2026-07-14 스펙) ───────────────────────────────
  handleCustomerChangeOpen(): void {
    this.data.customerModal = { ...emptyDealCustomerModal(), open: true };
    this.refreshCustomerModal();
  }

  // ─── draft 컨펌 (마이그 139) — 거래처 먼저, 확정 후 담당자 ────────────────
  // 컨펌은 "거래처 지정" 모달을 그대로 열되(confirmMode) draft명을 신규 등록칸에 프리필.
  // 좌하단 "신규 거래처 등록" 버튼 = draft 승격(handleCustomerModalCreateSave 분기).
  handleCustomerConfirmOpen(): void {
    if (!this.data.full.customer_is_draft) return;
    this.data.customerModal = {
      ...emptyDealCustomerModal(),
      open: true, confirmMode: true,
      confirmName: this.data.full.customer_name ?? '',
      newName: this.data.full.customer_name ?? '',   // 신규 등록칸 프리필
    };
    this.refreshCustomerModal();
  }
  async handleContactConfirmOpen(): Promise<void> {
    if (!this.data.full.contact_is_draft) return;
    const cid = this.data.full.customer_id;
    this.data.contactModal = {
      ...emptyDealContactModal(),
      open: true, loading: true, confirmMode: true,
      confirmName: this.data.full.contact_name ?? '',
      newName:     this.data.full.contact_name ?? '',    // 신규 등록칸 프리필(이름)
      newPhone:    (this.data.full.contact_phone ?? '').replace(/[^0-9]/g, ''),   // 전화·이메일·직책도 draft 값으로 프리필
      newEmail:    this.data.full.contact_email ?? '',
      newPosition: this.data.full.contact_position ?? '',
      companyName: this.data.full.customer_name ?? '',
    };
    this.refreshContactModal();
    try {
      this.data.contactModal.contacts = cid != null ? await api.fetchContacts(cid) : [];
    } catch { this.data.contactModal.contacts = []; }
    finally { this.data.contactModal.loading = false; this.refreshContactModal(); }
  }
  // 컨펌 모드 "신규 거래처 등록" = 현재 draft 거래처를 승격(is_draft=false, is_active=true).
  // 이어서 담당자도 draft면 담당자 컨펌 모달 자동 오픈.
  private async _confirmCustomerDraftPromote(): Promise<void> {
    const s  = this.data.customerModal;
    const id = this.data.full.customer_id;
    if (id == null) { s.saving = false; s.error = '거래처가 없습니다.'; this.refreshCustomerModal(); return; }
    try {
      await api.confirmCustomerDraft(id);
      this.data.full = { ...this.data.full, customer_is_draft: false };
      this.data.customerModal = emptyDealCustomerModal();
      this.refreshBody();
      this.toast('거래처를 컨펌했습니다.');
      void this._loadDealEvents();
      // 컨펌 편의: 거래처 확정 직후 담당자 모달로 바로 넘어감.
      //   담당자가 draft면 담당자 컨펌, 미지정이면 일반 담당자 지정 모달을 연다.
      if (this.data.full.contact_is_draft) void this.handleContactConfirmOpen();
      else if (this.data.full.contact_id == null) void this.handleContactChangeOpen();
    } catch (err) {
      s.saving = false;
      s.error  = err instanceof Error ? err.message : '컨펌 실패';
      this.refreshCustomerModal();
    }
  }
  private async _confirmContactDraftPromote(): Promise<void> {
    const s  = this.data.contactModal;
    const id = this.data.full.contact_id;
    if (id == null) { s.saving = false; s.error = '담당자가 없습니다.'; this.refreshContactModal(); return; }
    try {
      // 이름은 동일하지만 전화·이메일·직책이 편집됐으면 먼저 반영 후 승격.
      const cid = this.data.full.customer_id;
      const newPhone    = s.newPhone.trim()    || null;
      const newEmail    = s.newEmail.trim()    || null;
      const newPosition = s.newPosition.trim() || null;
      const fieldChanged =
        newPhone    !== ((this.data.full.contact_phone    ?? '') || null) ||
        newEmail    !== ((this.data.full.contact_email    ?? '') || null) ||
        newPosition !== ((this.data.full.contact_position ?? '') || null);
      if (fieldChanged && cid != null) {
        await api.updateCompanyContact(id, {
          customer_id: Number(cid), name: s.newName.trim(),
          position: newPosition, email: newEmail, phone: newPhone,
        });
      }
      await api.confirmContactDraft(id);
      this.data.full = {
        ...this.data.full, contact_is_draft: false,
        ...(fieldChanged ? { contact_phone: newPhone, contact_email: newEmail, contact_position: newPosition } : {}),
      };
      this.data.contactModal = emptyDealContactModal();
      this.refreshBody();
      this.toast('담당자를 컨펌했습니다.');
      void this._loadDealEvents();
    } catch (err) {
      s.saving = false;
      s.error  = err instanceof Error ? err.message : '컨펌 실패';
      this.refreshContactModal();
    }
  }
  handleCustomerModalClose(): void {
    this.data.customerModal = emptyDealCustomerModal();
    this.refreshCustomerModal();
  }
  handleCustomerModalSearch(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value;
    const s = this.data.customerModal;
    s.query = q; s.error = '';
    if (this._customerSearchTimer) window.clearTimeout(this._customerSearchTimer);
    if (q.trim().length < 1) { s.hits = []; s.searching = false; this._updateCustomerModalResults(); return; }
    s.searching = true; this._updateCustomerModalResults();
    this._customerSearchTimer = window.setTimeout(() => { void this._runCustomerModalSearch(q); }, 250);
  }
  private async _runCustomerModalSearch(q: string): Promise<void> {
    const s = this.data.customerModal;
    try {
      const r = await api.fetchCompanyList({ role: 'customer', search: q.trim(), sort: 'name', order: 'asc', page: 1, pageSize: 20 });
      if (s.query !== q) return;   // stale
      s.hits = r.rows;
    } catch { if (s.query === q) s.hits = []; }
    finally { if (s.query === q) { s.searching = false; this._updateCustomerModalResults(); } }
  }
  handleCustomerModalNewName(_e: Event, target: HTMLElement): void {
    this.data.customerModal.newName = (target as HTMLInputElement).value;   // 재렌더 X (포커스 유지)
  }
  async handleCustomerModalPick(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.id;
    if (!id) return;
    await this._assignCustomerToDeal(Number(id), target.dataset.name ?? '', target.dataset.bno || null);
  }
  async handleCustomerModalCreateSave(): Promise<void> {
    const s = this.data.customerModal;
    const name = s.newName.trim();
    if (s.saving) return;
    if (!name) { s.error = '거래처명을 입력하세요.'; this.refreshCustomerModal(); return; }
    s.saving = true; s.error = '';
    this.refreshCustomerModal();
    // 컨펌 모드: 신규 생성 대신 현재 draft 거래처를 승격.
    if (s.confirmMode && name === (this.data.full.customer_name ?? '').trim()) { await this._confirmCustomerDraftPromote(); return; }
    try {
      const created = await api.createCompany({ name, is_customer: true, is_supplier: false });
      await this._assignCustomerToDeal(Number(created.id), created.name, created.business_no ?? null);
    } catch (err) {
      s.saving = false;
      s.error  = err instanceof Error ? err.message : '등록 실패';
      this.refreshCustomerModal();
    }
  }
  // 거래처 지정/변경 — 변경 시 기존 담당자 해제 + 첫 고객 담당자 자동선택(스펙 §5).
  private async _assignCustomerToDeal(id: number, name: string, bno: string | null): Promise<void> {
    const changed = String(id) !== (this.data.full.customer_id ?? '');
    const s = this.data.customerModal;
    const wasConfirm = s.confirmMode;   // 모달 초기화 전에 캡처
    // 컨펌 모드에서 "기존 거래처로 재지정"이면, 버려지는 draft 거래처는 정리(삭제).
    const orphanDraftId = (s.confirmMode && this.data.full.customer_is_draft && changed)
      ? this.data.full.customer_id : null;
    // 컨펌 중 회사명을 바꿔 새 회사가 되면, 채팅으로 잡아둔 draft 담당자(이름·연락처)는
    // 옛 회사 소속이라 새 회사로 옮겨올 수 없다. 대신 새 회사 "신규 담당자" 폼에 프리필해
    // 같은 사람을 새로 등록하도록 넘긴다(채팅 정보 유실 방지). 옛 draft 담당자는 비활성화.
    const carryContact = (wasConfirm && changed && this.data.full.contact_is_draft)
      ? { name: this.data.full.contact_name ?? '', position: this.data.full.contact_position ?? '',
          email: this.data.full.contact_email ?? '', phone: this.data.full.contact_phone ?? '' }
      : null;
    const orphanContactId = (wasConfirm && changed && this.data.full.contact_is_draft)
      ? this.data.full.contact_id : null;
    try {
      const updated = await api.patchDeal(this.data.dealId, changed ? { customer_id: id, contact_id: null } : { customer_id: id });
      this.data.full = {
        ...this.data.full, customer_id: String(id), customer_name: name, customer_business_no: bno,
        customer_is_draft: false,   // 지정 대상은 검색결과(정식 거래처) 또는 확정 → draft 아님
        name: updated.name,   // 서버가 거래처 변경 시 딜명 재생성 → 헤더 반영
        ...(changed ? { contact_id: null, contact_name: null, contact_position: null, contact_email: null, contact_phone: null, contact_is_draft: false } : {}),
      };
      this.data.customerModal = emptyDealCustomerModal();
      this.refreshBody();
      this.toast('거래처를 지정했습니다.');
      void this._loadDealEvents();
      // 재지정 후 남은 draft 정리(딜은 이미 새 거래처를 가리키므로 안전) — best-effort.
      if (orphanDraftId) { void api.deactivateCompany(orphanDraftId).catch(() => {}); }
      if (orphanContactId) { void api.deactivateCompanyContact(orphanContactId).catch(() => {}); }
      // 컨펌 플로우면 재지정 직후에도 담당자 모달로 바로 진입(사용자가 담당자 선택).
      //   일반 변경이면 기존처럼 첫 담당자 자동 선택.
      if (wasConfirm) void this._openContactModal(carryContact);
      else if (changed) void this._autoSelectFirstContact(id);
    } catch (err) {
      s.saving = false;
      s.error  = err instanceof Error ? err.message : '지정 실패';
      this.refreshCustomerModal();
    }
  }
  // 거래처 변경 후 첫 활성 고객 담당자를 자동 연결(없으면 미지정 유지).
  private async _autoSelectFirstContact(companyId: number): Promise<void> {
    try {
      const contacts = await api.fetchContacts(companyId);
      const first = contacts[0];
      if (!first) return;
      await api.patchDeal(this.data.dealId, { contact_id: Number(first.id) });
      this.data.full = {
        ...this.data.full, contact_id: String(first.id), contact_name: first.name,
        contact_position: first.position ?? null, contact_email: first.email ?? null, contact_phone: first.phone ?? null,
      };
      this.refreshBody();
      void this._loadDealEvents();
    } catch { /* 자동선택 실패는 무시 — 사용자가 담당자 변경으로 지정 가능 */ }
  }

  // ─── 담당자 선택 모달 (현재 거래처 소속) ──────────────────────────────
  async handleContactChangeOpen(): Promise<void> { await this._openContactModal(null); }
  // 담당자 모달 오픈 — prefill 이 있으면 "신규 담당자" 폼을 그 값으로 채운다
  // (컨펌 중 회사명 변경 → 새 회사로 넘어온 채팅 draft 담당자 정보 이어받기).
  private async _openContactModal(prefill: { name: string; position: string; email: string; phone: string } | null): Promise<void> {
    const cid = this.data.full.customer_id;
    if (cid == null) { this.toast('거래처를 먼저 지정하세요.'); return; }
    this.data.contactModal = {
      ...emptyDealContactModal(), open: true, loading: true, companyName: this.data.full.customer_name ?? '',
      ...(prefill ? { newName: prefill.name, newPosition: prefill.position, newEmail: prefill.email, newPhone: prefill.phone.replace(/[^0-9]/g, '') } : {}),
    };
    this.refreshContactModal();
    try {
      const contacts = await api.fetchContacts(cid);
      this.data.contactModal.contacts = contacts;
    } catch { this.data.contactModal.contacts = []; }
    finally { this.data.contactModal.loading = false; this.refreshContactModal(); }
  }
  handleContactModalClose(): void {
    this.data.contactModal = emptyDealContactModal();
    this.refreshContactModal();
  }
  handleContactModalSearch(_e: Event, target: HTMLElement): void {
    this.data.contactModal.query = (target as HTMLInputElement).value;
    this._updateContactModalResults();   // 결과 영역만 갱신 — 검색 input 포커스 보존
  }
  handleContactModalNewField(_e: Event, target: HTMLElement): void {
    const el  = target as HTMLInputElement;
    const key = el.dataset.field;
    if (!key) return;
    // 전화번호(휴대)는 하이픈 입력 차단 — 숫자만 허용(저장 시 서버가 하이픈 삽입).
    if (key === 'newPhone') el.value = el.value.replace(/[^0-9]/g, '');
    (this.data.contactModal as unknown as Record<string, string>)[key] = el.value;   // 재렌더 X
  }
  async handleContactModalPick(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.id;
    if (!id) return;
    await this._assignContactToDeal(Number(id), {
      name:     target.dataset.name ?? '',
      phone:    target.dataset.phone || null,
      email:    target.dataset.email || null,
      position: target.dataset.position || null,
    });
  }
  async handleContactModalCreateSave(): Promise<void> {
    const s   = this.data.contactModal;
    const cid = this.data.full.customer_id;
    if (s.saving) return;
    if (cid == null) { s.error = '거래처가 없어 담당자를 등록할 수 없습니다.'; this.refreshContactModal(); return; }
    const name = s.newName.trim();
    if (!name) { s.error = '이름을 입력하세요.'; this.refreshContactModal(); return; }
    s.saving = true; s.error = '';
    this.refreshContactModal();
    // 컨펌 모드: 신규 생성 대신 현재 draft 담당자를 승격.
    if (s.confirmMode && name === (this.data.full.contact_name ?? '').trim()) { await this._confirmContactDraftPromote(); return; }
    try {
      const created = await api.createCompanyContact({
        customer_id: Number(cid), name,
        position: s.newPosition.trim() || null, email: s.newEmail.trim() || null, phone: s.newPhone.trim() || null,
        affiliation:  s.newAffiliation.trim() || null,
        office_phone: s.newOfficePhone.trim() || null,
        memo:         s.newNotes.trim() || null,
      });
      await this._assignContactToDeal(Number(created.id), {
        name: created.name, phone: created.phone, email: created.email, position: created.position,
      });
    } catch (err) {
      s.saving = false;
      s.error  = err instanceof Error ? err.message : '등록 실패';
      this.refreshContactModal();
    }
  }
  // 담당자 지정 — deal.contact_id 갱신 + 표시 연락처 반영.
  private async _assignContactToDeal(
    id: number,
    info: { name: string; phone: string | null; email: string | null; position: string | null },
  ): Promise<void> {
    const s = this.data.contactModal;
    // 컨펌 모드에서 "기존 담당자로 재지정"이면, 버려지는 draft 담당자는 정리(삭제).
    const orphanDraftId = (s.confirmMode && this.data.full.contact_is_draft && String(id) !== (this.data.full.contact_id ?? ''))
      ? this.data.full.contact_id : null;
    try {
      await api.patchDeal(this.data.dealId, { contact_id: id });
      this.data.full = {
        ...this.data.full, contact_id: String(id), contact_name: info.name,
        contact_position: info.position, contact_email: info.email, contact_phone: info.phone,
        contact_is_draft: false,   // 지정 대상은 기존(정식) 담당자 또는 확정 → draft 아님
      };
      this.data.contactModal = emptyDealContactModal();
      this.refreshBody();
      this.toast('거래처 담당자를 지정했습니다.');
      void this._loadDealEvents();
      if (orphanDraftId) { void api.deactivateCompanyContact(orphanDraftId).catch(() => {}); }
    } catch (err) {
      s.saving = false;
      s.error  = err instanceof Error ? err.message : '지정 실패';
      this.refreshContactModal();
    }
  }

  // ─── 유입 경로 변경 (inquiry_channel) ─────────────────────────────
  async handleLeadSourceChange(_e: Event, target: HTMLElement): Promise<void> {
    const raw = (target as HTMLSelectElement).value;
    const value: string | null = raw === '' ? null : raw;
    const prev = this.data.full.inquiry_channel;
    try {
      await api.patchDeal(this.data.dealId, { inquiry_channel: value });
      this.data.full = { ...this.data.full, inquiry_channel: value };
      this.refreshMain();
    } catch (err) {
      this.data.full = { ...this.data.full, inquiry_channel: prev };
      this.refreshMain();
      alert(`유입 경로 변경 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // 문의분류 변경 (§4.2.8) — 선택 UI. 낙관적 갱신 + 실패 롤백.
  async handleClassificationChange(_e: Event, target: HTMLElement): Promise<void> {
    const raw = (target as HTMLSelectElement).value;
    const value = (raw === '' ? null : raw) as api.DealClassification | null;
    const prev = this.data.full.classification;
    try {
      await api.patchDeal(this.data.dealId, { classification: value });
      this.data.full = { ...this.data.full, classification: value };
      this.refreshMain();
    } catch (err) {
      this.data.full = { ...this.data.full, classification: prev };
      this.refreshMain();
      alert(`문의분류 변경 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ════════════════════════════════════════════════════════════════
  // 핸들러: 품목/구성 — 구성 탭·확정 토글·편집 모드(E2)·카탈로그 검색
  // ════════════════════════════════════════════════════════════════
  // ─── 구성(Configuration) (B3) ─────────────────────────────────────
  //
  // 구성 변경/잠금은 품목 복제·상태 전이 등 서버 사이드 효과가 있으므로
  // 변경 후 fetchDealFull 로 전체를 다시 받아 화면을 동기화한다.
  private async reloadFull(preferConfigId?: string): Promise<void> {
    const fresh = await api.fetchDealFull(this.data.dealId);
    this.data.full = fresh;
    const configs = fresh.configurations ?? [];
    const wanted  = preferConfigId ?? this.data.activeConfigId ?? '';
    const exists  = configs.some((c) => c.id === wanted);
    if (exists) {
      this.data.activeConfigId = wanted;
    } else {
      const first = [...configs].sort((a, b) => a.config_no - b.config_no)[0] ?? null;
      this.data.activeConfigId = first?.id ?? null;
    }
  }

  // 판매 단가 인라인 저장 (blur/change). 성공 시 메모리만 갱신(포커스 보존).
  /** 타이핑 중 실시간 천 단위 콤마 — 커서 위치 보존. DB 저장은 안 함(change 담당). */
  handleItemSellPriceInput(_e: Event, target: HTMLElement): void {
    const input    = target as HTMLInputElement;
    const oldValue = input.value;
    const oldCaret = input.selectionStart ?? oldValue.length;
    // 커서 앞의 '숫자 개수' 기준으로 새 커서 위치 계산(콤마 삽입/삭제와 무관하게 안정).
    const digitsBeforeCaret = oldValue.slice(0, oldCaret).replace(/[^0-9]/g, '').length;

    const digits    = oldValue.replace(/[^0-9]/g, '');
    const formatted = digits === '' ? '' : Number(digits).toLocaleString('ko-KR');
    if (formatted === oldValue) return;   // 변화 없음 — 커서 안 건드림

    let newCaret = 0;
    for (let seen = 0; newCaret < formatted.length && seen < digitsBeforeCaret; newCaret++) {
      if (formatted.charCodeAt(newCaret) >= 48 && formatted.charCodeAt(newCaret) <= 57) seen++;
    }
    input.value = formatted;
    input.setSelectionRange(newCaret, newCaret);
  }

  async handleItemSellPriceChange(_e: Event, target: HTMLElement): Promise<void> {
    const input  = target as HTMLInputElement;
    const itemId = input.dataset.dealItemId;
    if (!itemId) return;
    const digits = input.value.replace(/[^0-9]/g, '');   // 콤마 제거
    const value: number | null = digits === '' ? null : Number(digits);
    try {
      const updated = await api.patchDealItem(itemId, { sell_unit_price_krw: value });
      this.data.full = {
        ...this.data.full,
        items: this.data.full.items.map((it) =>
          it.id === itemId ? { ...it, sell_unit_price_krw: updated.sell_unit_price_krw } : it),
      };
      // 콤마 포맷으로 재표시
      input.value = fmtThousands(updated.sell_unit_price_krw);
      input.classList.add('is-saved');
      setTimeout(() => input.classList.remove('is-saved'), 800);
    } catch (err) {
      alert(`판매 단가 저장 실패: ${err instanceof Error ? err.message : 'unknown'}`);
      this.refreshMain();
    }
  }

  // 구성 탭 선택.
  handleConfigSelect(_e: Event, target: HTMLElement): void {
    const configId = target.dataset.configId;
    if (!configId || configId === this.data.activeConfigId) return;
    // 저장 안 된 변경이 있으면 탭 전환 차단(탭도 disabled 지만 안전망).
    const edit = this.data.editConfig;
    if (edit && editConfigDirty(edit.items, edit.baseline)) {
      this.toast('저장되지 않은 변경이 있습니다. 저장 또는 취소 후 이동해 주세요.');
      return;
    }
    this.data.activeConfigId = configId;
    this.ensureEditDraft();   // 선택한 구성이 미확정이면 편집 모드로
    this.refreshMain();
  }

  // 새 구성 — 활성 구성 품목을 복제해 새 구성 생성 후 '곧바로 편집 모드' 진입.
  //   미확정 구성은 상시 편집(editConfig 항상 존재)이므로, 편집 여부가 아니라
  //   '저장 안 된 변경(dirty)'만 차단한다. (구버전 editConfig 존재 가드는 항상 막던 버그)
  async handleConfigCreate(): Promise<void> {
    const edit = this.data.editConfig;
    if (edit && editConfigDirty(edit.items, edit.baseline)) {
      this.toast('저장되지 않은 변경이 있습니다. 저장 또는 취소 후 새 구성을 만들어 주세요.');
      return;
    }
    const active = this.activeConfig(this.data.full);
    try {
      const created = await api.createDealConfiguration(this.data.dealId, {
        duplicate_from_config_id: active?.id,
      });
      await this.reloadFull(created.id);   // 새 구성 + 복제 품목 로드
      this.ensureEditDraft();              // 새 구성(미확정) → 편집 모드
      this.refreshMain();
    } catch (err) {
      alert(`새 구성 생성 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }


  // 품목 확정 — §4.4.26: 확인 모달/alert 없이 즉시 실행.
  //   저장 안 된 변경이 있으면 먼저 저장 요구(확정은 저장된 상태만).
  async handleConfigConfirmOpen(_e: Event, target: HTMLElement): Promise<void> {
    const edit = this.data.editConfig;
    if (edit && editConfigDirty(edit.items, edit.baseline)) {
      this.toast('저장되지 않은 변경이 있습니다. 먼저 저장해 주세요.');
      return;
    }
    await this.executeConfigLock(target.dataset.configId, true);
  }

  // is_locked 토글 공통 실행 — 확정/취소 모두 모달 없이 즉시 실행 (§4.4.26).
  private async executeConfigLock(configId: string | undefined, nextLocked: boolean): Promise<void> {
    if (!configId) return;
    try {
      await api.patchDealConfiguration(configId, { is_locked: nextLocked });
      await this.reloadFull(configId);   // 상태 전이 반영
      this.ensureEditDraft();            // 확정→읽기전용 / 확정취소→편집 모드
      this.refreshBody();
      if (!nextLocked) this.toast('품목 확정 취소가 완료되었습니다. 재확정 해주세요.');
    } catch (err) {
      alert(`품목 확정 ${nextLocked ? '' : '취소 '}실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // 품목 확정 취소 — 모달 없이 즉시 실행 (현민 요청).
  async handleConfigUnconfirmOpen(_e: Event, target: HTMLElement): Promise<void> {
    await this.executeConfigLock(target.dataset.configId, false);
  }

  // 가벼운 토스트 — 하단 중앙에 잠깐 표시 후 사라짐.
  private toast(message: string): void {
    const el = document.createElement('div');
    el.className = 'deal-toast';
    el.textContent = message;
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-shown'));
    setTimeout(() => {
      el.classList.remove('is-shown');
      setTimeout(() => el.remove(), 250);
    }, 2600);
  }

  // 구성 삭제 (마지막 1개는 서버가 거부).
  async handleConfigDelete(_e: Event, target: HTMLElement): Promise<void> {
    const configId = target.dataset.configId;
    if (!configId) return;
    if (!confirm('이 구성을 삭제할까요? 구성에 속한 품목도 함께 삭제됩니다.')) return;
    try {
      await api.deleteDealConfiguration(configId);
      await this.reloadFull();   // 삭제됐으니 대표로 fallback
      this.ensureEditDraft();
      this.refreshMain();
    } catch (err) {
      alert(`구성 삭제 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  /**
   * 편집 드래프트에 들어갈 수 있는 행인가 — 서버 구성 V2 트리는 제외한다.
   *
   * 드래프트(EditDraftItem)는 평면 모델이라 line_type/parent_id 를 모른다.
   * 서버 헤더행과 그 자식 부품행이 섞여 들어오면
   *   - 헤더행이 파트넘버 없는 빈 행처럼 보이고
   *   - 구성 일괄 교체(replaceConfigItems)가 트리를 건드릴 수 있다.
   * 서버는 별탭 편집기만 수정하므로(서버 서브트리 커맨드), 여기서 걸러낸다.
   */
  private static isFlatPartRow(it: NonNullable<api.DealFull['items']>[number]): boolean {
    return it.line_type !== 'server' && it.parent_id == null;
  }

  /**
   * 그 구성에 서버 그룹(헤더행)이 하나라도 있는가.
   *
   * "구성이 비었는가" 를 판단할 때 필요하다 — 서버 트리는 편집 드래프트 밖이라
   * `editConfig.items.length` 만 보면 서버만 남은 구성을 빈 구성으로 오판한다.
   *
   * configuration_id 가 null 인 행은 최저 config_no 구성에 귀속시킨다
   * (backfill 안전망 — enterEditMode·reconcileEditDraft·activeItems 와 같은 규칙).
   */
  private configHasServer(configId: string): boolean {
    const lowestId = [...(this.data.full.configurations ?? [])]
      .sort((a, b) => a.config_no - b.config_no)[0]?.id;
    return (this.data.full.items ?? []).some((it) =>
      it.line_type === 'server'
      && (it.configuration_id === configId
        || (configId === lowestId && it.configuration_id == null)));
  }

  // 서버 품목 row → 편집 드래프트 아이템 (enterEditMode·reconcile 공유 매핑).
  private draftItemFromServer(it: NonNullable<api.DealFull['items']>[number]): EditDraftItem {
    return {
      lineId:              nextLineId(),
      id:                  String(it.id),
      category:            it.category,
      option_part:         it.option_part,
      item_description:    it.item_description,
      quantity:            it.quantity == null ? '' : String(it.quantity),
      requested_condition: it.requested_condition,
      sell_unit_price_krw: it.sell_unit_price_krw,
      is_unregistered:     it.is_unregistered,
      is_pending:          it.is_pending ?? false,
      is_non_hw:           it.is_non_hw ?? false,
      description_raw:     it.description_raw ?? null,
      // 표시 전용 carry — 소스는 드래프트가 아니라 select 가 즉시 저장한다.
      source_type:         it.source_type   ?? null,
      source_ref_id:       it.source_ref_id ?? null,
    };
  }

  // ─── refetch 후 편집 드래프트 재조정 ───────────────────────────────
  //   채팅(봇)·외부 변경으로 새 full 을 받았을 때 품목 드래프트를 맞춤.
  //   상시 편집이라 품목 표는 editConfig 를 그리므로, 이 재조정이 없으면
  //   봇이 추가한 행이 full 에만 들어오고 표에 안 보임(새로고침해야 보임).
  //
  //   정책 (동시성 설계 "409=refetch+드래프트유지+병합" 과 동일 계열):
  //     - 확정/잠금 or 다른 구성 활성화 → ensureEditDraft 로 재생성
  //     - 같은 구성 + 미저장 변경 없음(clean) → 새 full 로 통째 재클론
  //     - 같은 구성 + 미저장 변경 있음(dirty) → 사용자 편집 보존 +
  //       서버 신규행(봇 추가)만 append 병합. 기존행 서버 수정은 v1 범위 밖.
  //   반환: 병합으로 새로 들어온 행 수(알림용).
  private reconcileEditDraft(): number {
    const active = this.activeConfig(this.data.full);
    const edit   = this.data.editConfig;

    if (!active || active.is_locked) { this.data.editConfig = null; return 0; }
    if (!edit || edit.configId !== active.id) { this.ensureEditDraft(); return 0; }

    if (!editConfigDirty(edit.items, edit.baseline)) {
      this.enterEditMode(active.id, false);   // clean → 새 full 로 재클론
      return 0;
    }

    // dirty → 서버 신규행만 병합 (사용자 편집·로컬 미저장행 보존)
    //
    // ★ "드래프트에 없다" 만으로 판단하면 안 된다. 두 경우가 섞여 있다:
    //     ① 봇/외부가 새로 추가한 행 — 편집 시작 스냅샷에도 없다  → 병합해야 함
    //     ② 사용자가 드래프트에서 지운 행 — 스냅샷에는 있다      → 되살리면 안 됨
    //   `baseItemIds`(편집 진입 시점의 id 집합)가 이 둘을 가르는 근거이고,
    //   원래 그 용도로 만들어졌는데 필터에서 빠져 있었다. 그래서 품목을 지운 뒤
    //   refetch 가 한 번이라도 돌면(서버 추가·인라인 저장 등) 지운 행이
    //   되살아나 "삭제가 저장되지 않는" 것처럼 보였다.
    const cfgs     = [...(this.data.full.configurations ?? [])].sort((a, b) => a.config_no - b.config_no);
    const lowestId = cfgs[0]?.id;
    const draftIds = new Set(edit.items.map((i) => i.id).filter((v): v is string => v != null));
    const baseIds  = new Set(edit.baseItemIds);
    const appended = (this.data.full.items ?? [])
      .filter((it) => it.configuration_id === active.id
        || (active.id === lowestId && it.configuration_id == null))
      .filter(DealDetailView.isFlatPartRow)   // 서버 트리는 드래프트 대상 아님
      .filter((it) => !draftIds.has(String(it.id)) && !baseIds.has(String(it.id)))
      .map((it) => this.draftItemFromServer(it));

    if (appended.length > 0) {
      edit.items.push(...appended);
      // 새로 들어온 서버행은 baseline 에도 추가 → dirty 로 오판 안 함.
      edit.baseline.push(...appended.map((a) => ({ ...a })));
      edit.baseItemIds = [
        ...edit.baseItemIds,
        ...appended.map((a) => a.id).filter((v): v is string => v != null),
      ];
    }
    edit.baseVersion = this.data.full.version;   // 최신 버전까지 반영됨
    return appended.length;
  }

  // ─── 구성 편집 모드 ────────────────────────────────────────────────
  //   미확정 구성은 ensureEditDraft 로 상시 편집 진입(구성 수정 버튼 폐기).
  // 구성 id 의 현재 품목을 드래프트로 클론 → 편집 모드 진입.
  // isNew: '새 구성'으로 막 만든 구성.
  private enterEditMode(configId: string, isNew: boolean): void {
    const cfgs = [...(this.data.full.configurations ?? [])].sort((a, b) => a.config_no - b.config_no);
    const cfg = cfgs.find((c) => c.id === configId);
    if (!cfg) return;
    const lowestId = cfgs[0]?.id;
    const items = (this.data.full.items ?? [])
      .filter((it) => it.configuration_id === configId
        || (configId === lowestId && it.configuration_id == null))
      // 서버 구성 V2 트리(서버 헤더행·자식 부품행)는 드래프트에서 제외 — 별탭 편집기 전용.
      .filter(DealDetailView.isFlatPartRow)
      .map<EditDraftItem>((it) => this.draftItemFromServer(it));
    this.data.editConfig = {
      configId, configName: cfg.name, items, saving: false, isNew,
      // dirty 판정·'취소' 복원 기준 = 진입 시점 스냅샷(깊은 복사).
      baseline:    items.map((it) => ({ ...it })),
      // 낙관적 잠금 기준 스냅샷 (§3-1) — 저장 시 expected_version + 409 병합 판별 근거
      baseVersion: this.data.full.version,
      baseItemIds: items.map((it) => it.id).filter((v): v is string => v != null),
      conflict:    null,
    };
  }

  // 미확정 활성 구성 = 항상 편집 모드. 확정 구성 = 편집 없음(editConfig=null).
  //   draft 가 없거나 다른 구성이면 새로 클론. (탭 전환/확정 토글/로드 후 호출)
  private ensureEditDraft(): void {
    const active = this.activeConfig(this.data.full);
    if (!active || active.is_locked) { this.data.editConfig = null; return; }
    if (this.data.editConfig?.configId === active.id) return;
    this.enterEditMode(active.id, false);
  }

  handleEditAddRow(): void {
    if (!this.data.editConfig) return;
    this.data.editConfig.items.push({
      lineId: nextLineId(),
      category: null, option_part: null, item_description: null,
      quantity: '1', requested_condition: null, sell_unit_price_krw: null,
      is_unregistered: false,   // §4.5.13 — 추가 직후 자동 미등록 표기 안 함
      description_raw: null,
      // 아직 deal_item 이 없어 PATCH 대상이 없다 — 저장 전까지 소스 select 는 비활성.
      source_type: null, source_ref_id: null,
    });
    this.refreshMain();
  }

  /**
   * ＋ 서버 추가 — 서버 구성 편집기(별탭)로 진입한다.
   *
   * 서버는 이 편집기에서만 만든다(딜 상세에서 인라인 편집하지 않음).
   * 저장 원천은 이 딜의 deal_item 서브트리이며, 편집기의 '완료'가 유일한 커밋
   * 지점이다. 여기서는 draft 를 저장하지 않는다 — 그냥 탭만 연다.
   */
  handleServerAdd(_e: Event, target: HTMLElement): void {
    const configId = target.dataset.configId ?? this.data.editConfig?.configId;
    if (!configId) return;
    // 두 갈래를 먼저 고르게 한다 — 새로 구성할지, 다른 딜의 구성을 가져올지.
    this.data.serverAdd = { ...emptyServerAdd(), open: true, configId };
    this.refreshModals();
  }

  /** '신규 구성 만들기' — 별탭 편집기에서 Chassis 부터 고른다. */
  handleServerAddNew(): void {
    const configId = this.data.serverAdd.configId;
    if (!configId) return;
    const url = `/deal/${encodeURIComponent(this.data.dealId)}`
      + `/config/${encodeURIComponent(configId)}/server/new`;
    this.data.serverAdd = emptyServerAdd();
    this.refreshModals();
    // 'noopener' 를 붙이지 않는다 — 브라우저는 **스크립트가 연 창만** 닫도록
    // 허용하는데, noopener 는 그 관계를 끊어 편집기의 window.close() 가 거부된다.
    // 그러면 저장 후 폴백으로 딜 URL 로 이동해 **딜 탭이 둘**이 된다(실제 증상).
    // 같은 오리진의 우리 화면이라 opener 를 열어도 위험이 없다.
    window.open(url, '_blank');
  }

  /** '기존 구성 불러오기' — 검색 화면으로 전환하고 최근 목록을 먼저 띄운다. */
  async handleServerAddImportOpen(): Promise<void> {
    this.data.serverAdd = { ...this.data.serverAdd, mode: 'import' };
    this.refreshModals();
    await this.runSavedServerSearch('');
  }

  handleServerAddSearch(e: Event): void {
    const q = (e.target as HTMLInputElement).value;
    this.data.serverAdd.query = q;
    if (this._savedServerTimer) window.clearTimeout(this._savedServerTimer);
    this._savedServerTimer = window.setTimeout(() => { void this.runSavedServerSearch(q); }, 250);
  }

  /**
   * 저장된 서버 구성 검색.
   * `append=false` 면 첫 페이지(검색어 변경), true 면 '더 보기'로 이어붙인다.
   */
  private async runSavedServerSearch(q: string, append = false): Promise<void> {
    const m = this.data.serverAdd;
    const seq = ++this._savedServerSeq;
    m.searching = true;
    // 지난 실패 문구를 지우고 시작한다 — 안 지우면 검색 중에도 옛 에러(예: 불러오기
    // 실패)가 결과 자리에 남아, 방금 친 검색어 때문에 난 것처럼 보인다.
    m.error = '';
    if (!append) { m.offset = 0; m.hits = []; }
    this.refreshServerAddResults();
    try {
      // 같은 딜은 제외한다 — 그 안에서는 서버 ⋯ 메뉴의 '복제'가 있다.
      const res = await api.fetchSavedServers(q, this.data.dealId, m.offset);
      if (seq !== this._savedServerSeq) return;
      m.hits    = append ? [...m.hits, ...res.items] : res.items;
      m.offset  = m.hits.length;
      m.hasMore = res.has_more;
      m.error   = '';
    } catch (err) {
      if (seq !== this._savedServerSeq) return;
      if (!append) m.hits = [];
      m.hasMore = false;
      m.error   = err instanceof Error ? err.message : '검색 실패';
    }
    m.searching = false;
    this.refreshServerAddResults();
  }

  /** '더 보기' — 다음 10건을 이어붙인다. */
  async handleServerAddMore(): Promise<void> {
    const m = this.data.serverAdd;
    if (m.searching || !m.hasMore) return;
    await this.runSavedServerSearch(m.query, true);
  }

  /** 목록에서 하나 선택 → 이 구성으로 복사. */
  async handleServerAddImportPick(_e: Event, target: HTMLElement): Promise<void> {
    const m = this.data.serverAdd;
    const sourceId = target.dataset.serverItemId;
    if (!sourceId || !m.configId || m.saving) return;
    this.data.serverAdd = { ...m, saving: true, error: '' };
    this.refreshModals();
    try {
      await api.importDealServer(this.data.dealId, {
        configuration_id:      m.configId,
        source_server_item_id: sourceId,
      }, this.data.full.version);
      this.data.serverAdd = emptyServerAdd();
      this.refreshModals();
      await this.reloadAfterServerCommand();
      this.toast('서버 구성을 불러왔습니다. 가격·소스는 다시 지정해 주세요.');
    } catch (err) {
      this.data.serverAdd = {
        ...this.data.serverAdd, saving: false,
        error: err instanceof Error ? err.message : '불러오기 실패',
      };
      this.refreshModals();
    }
  }

  handleServerAddClose(): void {
    if (this.data.serverAdd.saving) return;
    this._savedServerSeq += 1;   // 진행 중인 검색 무효화
    this.data.serverAdd = emptyServerAdd();
    this.refreshModals();
  }

  /** 결과 목록만 — 검색 input 의 caret 을 지킨다. */
  private refreshServerAddResults(): void {
    this.refreshRegion('.deal-svradd-results', () => renderServerAddResults(this.data));
  }

  /**
   * 서버 커맨드(복제·삭제) 후 재조회.
   *
   * 서버 서브트리는 편집 드래프트에 없지만(별탭 편집기 전용), deal.version 이
   * 올라가므로 드래프트의 낙관적 잠금 기준을 함께 맞춰야 다음 저장이 409 로
   * 막히지 않는다. 그래서 reconcileEditDraft 를 거친다.
   */
  private async reloadAfterServerCommand(): Promise<void> {
    this.data.full = await api.fetchDealFull(this.data.dealId);
    this.reconcileEditDraft();
    this.refreshHeader();
    this.refreshMain();
    // 구성품이 바뀌었을 수 있다(추가/불러오기/편집기 복귀) — 새 파트의 소스
    // 후보가 없으면 dropdown 이 '후보 없음' 으로 굳는다. 화면은 이미 그렸으므로
    // 기다리지 않고, 도착하면 다시 그린다.
    void this._loadItemSources();
  }

  // ─── 서버 구성 V2 — 그룹 접기/펼치기 · ⋯메뉴 · 수정/복제/삭제 ──────
  //
  //   구성 변경(부품 추가·삭제·수량)은 별탭 편집기만 한다. 여기서는 표시와
  //   서버 단위 커맨드(수정 진입 / 복제 / 삭제)만 제공한다.

  handleServerToggle(_e: Event, target: HTMLElement): void {
    const id = target.dataset.serverId;
    if (!id) return;
    const set = this.data.expandedServerIds;
    if (set.has(id)) set.delete(id); else set.add(id);
    this.refreshMain();
  }

  handleServerMenuToggle(_e: Event, target: HTMLElement): void {
    const id = target.dataset.serverId ?? null;
    const willOpen = this.data.serverMenuId !== id;
    this.data.serverMenuId = willOpen ? id : null;
    // 품목 행 메뉴와 동시에 열리지 않게 한다 — positionItemMenu 가
    // document 에서 첫 .deal-item-menu 를 잡으므로 둘이 공존하면 엉뚱한 것을 옮긴다.
    this.data.itemMenuId = null;
    this.refreshMain();
    // ★ 이 한 줄이 없으면 팝오버가 렌더돼도 **보이지 않는다**.
    //   .deal-tag-pop 은 position:absolute 인데 품목 테이블이 가로 overflow 를
    //   잘라내서 셀 밖으로 나온 부분이 사라진다. 품목 행 ⋯ 와 똑같이 렌더 후
    //   fixed 로 못박아 화면 좌표로 띄운다.
    if (willOpen) this.positionItemMenu();
  }

  // ─── 서버 헤더/구성품 인라인 편집 ────────────────────────────────
  //
  //   대수·번들가(헤더)와 소스·판매가(구성품)만 여기서 고친다. 구성 자체
  //   (부품 추가/삭제/수량)는 여전히 별탭 편집기 전용이다.
  //   전용 PATCH 를 쓰는 이유: PUT /deal/server/:id 는 자식 목록 전체를 받아
  //   diff 하는 계약이라, 값 하나 고치려고 부르면 구성품이 통째로 사라진다.

  /** 서버 대수 — 1 미만은 저장하지 않는다(딜 전용, 1 이상). */
  async handleServerQty(_e: Event, target: HTMLElement): Promise<void> {
    const input = target as HTMLInputElement;
    const id    = input.dataset.serverId;
    if (!id) return;
    const n = Math.floor(Number(input.value));
    if (!Number.isFinite(n) || n < 1) {
      alert('서버 대수는 1 이상이어야 합니다.');
      this.refreshMain();   // 입력칸을 저장된 값으로 되돌린다
      return;
    }
    await this.saveServerPatch(input,
      () => api.patchDealServerHeader(id, { server_quantity: n }, this.data.full.version));
  }

  /** 서버 1대 판매가(번들가). 빈 값 = 미입력(null) — 0 으로 바꾸지 않는다. */
  async handleServerBundle(_e: Event, target: HTMLElement): Promise<void> {
    const input = target as HTMLInputElement;
    const id    = input.dataset.serverId;
    if (!id) return;
    const digits = input.value.replace(/[^0-9]/g, '');
    const value  = digits === '' ? null : Number(digits);
    await this.saveServerPatch(input,
      () => api.patchDealServerHeader(id, { unit_price_override: value }, this.data.full.version));
    input.value = fmtThousands(value);   // 콤마 포맷으로 재표시(기존 판매가 칸과 동일)
  }

  /** 구성품 참고 판매가 — 서버 번들가와 독립이며 고객 총액을 바꾸지 않는다. */
  async handleServerChildPrice(_e: Event, target: HTMLElement): Promise<void> {
    const input = target as HTMLInputElement;
    const id    = input.dataset.dealItemId;
    if (!id) return;
    const digits = input.value.replace(/[^0-9]/g, '');
    const value  = digits === '' ? null : Number(digits);
    await this.saveServerPatch(input,
      () => api.patchDealItemSource(id, { sell_unit_price_krw: value }, this.data.full.version));
    input.value = fmtThousands(value);
  }

  /**
   * 품목 소스 선택 → 원가 스냅샷. 서버 구성품과 개별 품목이 같은 핸들러를 쓴다.
   *
   * 스냅샷 조립은 `lib/source-options` 가 정본이다(편집기와 같은 함수). 라벨은
   * 화면 문구가 아니라 저장되는 값이라, 두 화면이 각자 만들면 갈라진다.
   *
   * 개별 품목은 편집 드래프트로 그려지므로, 저장 성공 시 드래프트와 baseline 을
   * **함께** 갱신한다. 한쪽만 고치면 dirty 판정이 흔들려 '저장/취소' 가 잘못 뜬다.
   */
  async handleItemSource(_e: Event, target: HTMLElement): Promise<void> {
    const el = target as HTMLSelectElement;
    const id = el.dataset.dealItemId;
    if (!id) return;
    const item = (this.data.full.items ?? []).find((it) => String(it.id) === id);
    const snap = sourceSnapshot(this.data.itemSources, item?.option_part ?? '', el.value);
    await this.saveServerPatch(el, () => api.patchDealItemSource(id, {
      source_type:         snap.sourceType,
      source_ref_id:       snap.sourceRefId,
      source_label:        snap.sourceLabel,
      cost_basis_amount:   snap.costAmount,
      cost_basis_currency: snap.costCurrency,
    }, this.data.full.version));
    this.syncDraftSource(id, snap.sourceType, snap.sourceRefId);
  }

  /**
   * 개별 품목의 소스 변경을 편집 드래프트에 반영한다.
   *
   * 표는 `full.items` 가 아니라 드래프트를 그리므로, 이걸 안 하면 select 가
   * 저장 직후 원래 값으로 되돌아 보인다. `baseline` 도 같이 고치는 이유는
   * dirty 판정 때문이 아니라(소스는 비교 목록에 없다) **'취소' 복원 기준**이
   * baseline 이기 때문이다 — 한쪽만 고치면 취소 시 소스가 옛 값으로 돌아간다.
   */
  private syncDraftSource(itemId: string, type: string | null, refId: string | null): void {
    const edit = this.data.editConfig;
    if (!edit) return;
    for (const list of [edit.items, edit.baseline]) {
      const row = list.find((r) => r.id === itemId);
      if (row) { row.source_type = type; row.source_ref_id = refId; }
    }
  }

  /**
   * 서버 인라인 저장 공통부 — **성공 시 메모리만 갱신한다.**
   *
   * 품목 판매가 인라인 저장(`handleItemSellPriceChange`)과 같은 규칙이다.
   * `refreshMain()` 은 main 을 innerHTML 로 통째 갈아치우므로, 저장 왕복
   * 중에 옆 칸으로 옮겨 타이핑하던 값과 포커스가 사라진다. 서버 헤더·구성품
   * 인라인 필드가 한 화면에 4개나 모여 있어 매우 흔하게 재현된다.
   *
   * 그래서 PATCH 응답의 서브트리(`items`)로 그 행들만 갈아끼우고, 화면은
   * 다시 그리지 않는다. `deal.version` 은 별도로 올려 둔다 — 갱신하지 않으면
   * 다음 편집이 낙관적 잠금에 걸려 409 로 튕긴다.
   *
   * 실패했을 때만 다시 그린다. 입력칸이 저장 안 된 값을 들고 있으면 사용자가
   * 저장된 줄 알기 때문이다.
   */
  private async saveServerPatch(
    input: HTMLInputElement | HTMLSelectElement,
    run:   () => Promise<api.ServerPatchResult>,
  ): Promise<void> {
    try {
      const { items, version } = await run();
      // 돌아온 서브트리 행만 교체한다 — 다른 서버·개별 품목은 건드리지 않는다.
      // version 도 함께 갱신해야 다음 편집이 낙관적 잠금에 걸리지 않는다.
      const byId = new Map(items.map((it) => [String(it.id), it]));
      this.data.full = {
        ...this.data.full,
        version,
        items: this.data.full.items.map((it) => byId.get(String(it.id)) ?? it),
      };
      input.classList.add('is-saved');
      setTimeout(() => input.classList.remove('is-saved'), 800);
    } catch (err) {
      alert(err instanceof Error ? err.message : '저장에 실패했습니다.');
      await this.reloadAfterServerCommand();
    }
  }

  /** 팝오버 바깥(투명 오버레이) 클릭으로 닫기 — 품목 행 메뉴와 같은 규칙. */
  handleServerMenuClose(): void {
    this.data.serverMenuId = null;
    this.refreshMain();
  }

  /**
   * Esc 로 닫을 최상위 레이어 하나를 닫는다. 닫았으면 true.
   *
   * 순서 = 시각적으로 위에 있는 것부터. 저장 중인 모달은 닫지 않는다
   * (요청이 날아가는 중에 화면만 사라지면 결과를 확인할 수 없다).
   */
  private closeTopLayer(): boolean {
    const d = this.data;
    if (d.serverAdd.open)          { this.handleServerAddClose();   return !d.serverAdd.open; }
    if (d.needCreate.open)         { this.handleNeedCreateClose();  return !d.needCreate.open; }
    if (d.cancelModal.open)        { d.cancelModal.open = false;    this.refreshModals(); return true; }
    if (d.quoteViewModal.open)     { d.quoteViewModal.open = false; this.refreshModals(); return true; }
    if (d.quoteModal.open)         { d.quoteModal.open = false;     this.refreshModals(); return true; }
    if (d.rfqViewBubble)           { d.rfqViewBubble = null;        this.refreshModals(); return true; }
    if (d.customerModal.open)      { d.customerModal.open = false;  this.refreshModals(); return true; }
    if (d.contactModal.open)       { d.contactModal.open = false;   this.refreshModals(); return true; }
    if (d.itemSearch.open)         { d.itemSearch.open = false;     this.refreshModals(); return true; }
    // 팝오버류 — 모달보다 아래
    if (d.itemMenuId)              { d.itemMenuId = null;    this.refreshMain(); return true; }
    if (d.serverMenuId)            { d.serverMenuId = null;  this.refreshMain(); return true; }
    if (d.quoteMenuId)             { d.quoteMenuId = null;   this.refreshMain(); return true; }
    if (d.quoteKindMenuOpen)       { d.quoteKindMenuOpen = false; this.refreshMain(); return true; }
    if (d.needMenuId)              { d.needMenuId = null;    this.refreshMain(); return true; }
    if (d.tagEditor)               { d.tagEditor = null;     this.refreshMain(); return true; }
    if (d.statusPopoverOpen)       { d.statusPopoverOpen = false; this.refreshHeader(); return true; }
    return false;
  }

  /** 수정 — 별탭 편집기 edit 모드. 딜 사본을 로드하며 원본 개념은 없다. */
  handleServerEdit(_e: Event, target: HTMLElement): void {
    const serverId = target.dataset.serverId;
    const configId = this.data.editConfig?.configId ?? this.activeConfig(this.data.full)?.id;
    if (!serverId || !configId) return;
    this.data.serverMenuId = null;
    const url = `/deal/${encodeURIComponent(this.data.dealId)}`
      + `/config/${encodeURIComponent(configId)}/server/${encodeURIComponent(serverId)}`;
    // 'noopener' 를 붙이지 않는다 — 브라우저는 **스크립트가 연 창만** 닫도록
    // 허용하는데, noopener 는 그 관계를 끊어 편집기의 window.close() 가 거부된다.
    // 그러면 저장 후 폴백으로 딜 URL 로 이동해 **딜 탭이 둘**이 된다(실제 증상).
    // 같은 오리진의 우리 화면이라 opener 를 열어도 위험이 없다.
    window.open(url, '_blank');
    this.refreshMain();
  }

  /** 복제 — 원본 바로 뒤에 새 ID 묶음. 원본은 변경되지 않는다. */
  async handleServerDuplicate(_e: Event, target: HTMLElement): Promise<void> {
    const serverId = target.dataset.serverId;
    if (!serverId) return;
    this.data.serverMenuId = null;
    try {
      await api.duplicateDealServer(serverId, this.data.full.version);
      await this.reloadAfterServerCommand();
    } catch (err) {
      alert(err instanceof Error ? err.message : '서버 복제에 실패했습니다.');
      this.refreshMain();
    }
  }

  /** 삭제 — 그 서버행과 자식행만 비활성(soft). 다른 서버·개별 파트는 무영향. */
  async handleServerDelete(_e: Event, target: HTMLElement): Promise<void> {
    const serverId = target.dataset.serverId;
    if (!serverId) return;
    this.data.serverMenuId = null;
    if (!window.confirm('이 서버 구성을 삭제할까요? 서버와 내부 부품이 모두 제거됩니다.')) {
      this.refreshMain();
      return;
    }
    try {
      await api.deleteDealServer(serverId, this.data.full.version);
      this.data.expandedServerIds.delete(serverId);
      await this.reloadAfterServerCommand();
    } catch (err) {
      alert(err instanceof Error ? err.message : '서버 삭제에 실패했습니다.');
      this.refreshMain();
    }
  }

  // 품목 복제 (§4.4.4·31) — 해당 행 바로 아래에 값 복사한 새 행 삽입.
  handleItemDuplicate(_e: Event, target: HTMLElement): void {
    const lineId = target.dataset.lineId;
    if (!lineId || !this.data.editConfig) return;
    const items = this.data.editConfig.items;
    const idx = items.findIndex((it) => it.lineId === lineId);
    if (idx < 0) return;
    const src = items[idx]!;
    items.splice(idx + 1, 0, {
      ...src,
      lineId: nextLineId(),
      id: undefined,          // 신규 행으로 삽입
      // 소스는 원본 행에 붙은 저장값이라 복제본이 물려받을 수 없다
      // (아직 deal_item 이 없다). 저장 후 다시 고른다.
      source_type: null, source_ref_id: null,
    });
    this.data.itemMenuId = null;   // 작업 메뉴 닫기
    this.refreshMain();
  }

  handleEditDeleteRow(_e: Event, target: HTMLElement): void {
    const lineId = target.dataset.lineId;
    if (!lineId || !this.data.editConfig) return;
    this.data.editConfig.items = this.data.editConfig.items.filter((it) => it.lineId !== lineId);
    this.data.itemMenuId = null;   // 작업 메뉴 닫기
    this.refreshMain();
  }

  // 드래프트 필드 갱신 — 메모리만(포커스 보존, re-render 안 함).
  handleEditFieldChange(_e: Event, target: HTMLElement): void {
    const el     = target as HTMLInputElement | HTMLSelectElement;
    const lineId = el.dataset.lineId;
    const field  = el.dataset.field;
    if (!lineId || !field || !this.data.editConfig) return;
    const item = this.data.editConfig.items.find((it) => it.lineId === lineId);
    if (!item) return;
    const v = el.value;
    switch (field) {
      case 'category':         item.category = v || null; break;
      case 'option_part':      item.option_part = v.trim() || null; break;   // 미등록 파트넘버 직접 입력
      case 'item_description': item.item_description = v || null; break;
      case 'requested_condition': item.requested_condition = v || null; break;
      case 'quantity':         item.quantity = v.replace(/[^0-9]/g, ''); break;
      case 'price': {
        const digits = v.replace(/[^0-9]/g, '');
        item.sell_unit_price_krw = digits === '' ? null : Number(digits);
        break;
      }
    }
    // dirty 표시 — 재렌더 없이 타깃 DOM만(포커스 보존).
    //   변경된 이 input 에 노란 border(.is-dirty) + 섹션 has-changes(저장/취소 노출).
    const edit = this.data.editConfig;
    const base = edit.baseline.find((b) => b.lineId === lineId);
    if (field === 'option_part' || field === 'item_description'
      || field === 'requested_condition' || field === 'quantity') {
      // 신규 행(base 없음)은 변경이 아니라 validation 대상 → border 안 줌.
      el.classList.toggle('is-dirty', !!base && editItemFieldDirty(item, base, field));
    } else if (field === 'price') {
      const norm = (v: unknown): string => (v == null ? '' : String(v));
      el.classList.toggle('is-dirty', !!base && norm(item.sell_unit_price_krw) !== norm(base.sell_unit_price_krw));
    }
    el.closest('.deal-section-editing')
      ?.classList.toggle('has-changes', editConfigDirty(edit.items, edit.baseline));
  }

  // 편집 테이블 컨디션 헤더 셀렉터 → 현재 구성 전 품목에 동일 컨디션 일괄 적용.
  //   자동저장(완료 버튼)이 커밋. 적용 후 헤더는 재렌더로 placeholder("컨디션")로 복귀.
  handleEditCondBulk(_e: Event, target: HTMLElement): void {
    const v = (target as HTMLSelectElement).value;
    if (!v || !this.data.editConfig) return;
    for (const it of this.data.editConfig.items) it.requested_condition = v;
    this.refreshMain();
  }

  async handleEditComplete(): Promise<void> {
    const edit = this.data.editConfig;
    if (!edit || edit.saving) return;
    // 검증 — 빈 구성/수량 0 차단.
    //
    // `edit.items` 는 **평면 품목만** 담는다(enterEditMode 의 isFlatPartRow 필터).
    // 서버 트리는 별탭 편집기 소유라 드래프트 밖에 있으므로, items 가 0 이어도
    // 구성이 비었다는 뜻이 아니다. 서버 구성 V2 이전에는 items 가 구성의 전부라
    // 이 가드가 맞았지만, 지금은 "서버 + 개별 품목"에서 개별 품목만 지우면
    // 화면에 서버가 멀쩡히 있는데도 저장이 막혔다(dirty 만 남아 저장 불가).
    // 구성이 정말 비었는지는 서버 그룹까지 세어 판단한다.
    if (edit.items.length === 0 && !this.configHasServer(edit.configId)) {
      this.toast('품목을 1개 이상 추가해 주세요.');
      return;
    }
    // 완료(수정 저장) 필수값 — 디스크립션·컨디션·수량 (파트넘버는 미등록 허용이라 제외).
    for (const it of edit.items) {
      const qty     = Number(it.quantity);
      const hasDesc = !!((it.item_description && it.item_description.trim())
        || (it.description_raw && it.description_raw.trim()));   // 등록 파트는 카탈로그 원문으로 충족
      const hasCond = !!(it.requested_condition && it.requested_condition.trim());
      const hasQty  = Number.isFinite(qty) && qty >= 1;
      if (!hasDesc || !hasCond || !hasQty) {
        this.toast('필수 품목 정보를 모두 입력해 주세요.');
        return;
      }
    }
    this.data.editConfig = { ...edit, saving: true };
    this.refreshMain();
    try {
      await api.replaceDealConfigItems(edit.configId, edit.items.map((it) => ({
        id:                  it.id,
        category:            it.category,
        option_part:         it.option_part,
        item_description:    it.item_description,
        quantity:            it.quantity.trim() === '' ? null : Number(it.quantity),
        requested_condition: it.requested_condition,
        sell_unit_price_krw: it.sell_unit_price_krw,
        is_unregistered:     it.is_unregistered,
      })), edit.baseVersion);
      await this.reloadFull(edit.configId);
      // 저장 후에도 편집 모드 유지(미확정 = 항상 편집). 재클론 → baseline 갱신(dirty 해제).
      this.enterEditMode(edit.configId, false);
      this.refreshMain();
      this.toast('저장되었습니다.');
    } catch (err) {
      if (err instanceof api.ApiConflictError) {
        // 409 — 자동 refetch + 드래프트 유지 + 외부 변경분 병합 (§3-1). alert 없음.
        await this.mergeEditConflict();
        return;
      }
      this.data.editConfig = { ...this.data.editConfig!, saving: false };
      this.refreshMain();
      alert(`구성 저장 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  /**
   * 409 병합 (동시성_설계 §3-1) — 저장하지 않은 드래프트를 UI 에 그대로 유지한 채:
   *   1. 자동 refetch → full 교체 (읽기 영역·version 최신화)
   *   2. 외부 추가 (기준 스냅샷에 없던 새 item id) → 드래프트에 append + '봇 추가' 하이라이트
   *   3. 외부 삭제 → 드래프트에서 제거하지 않고 배너로만 알림 (재저장 시 사용자 값 우선 — 신규로 재삽입)
   *   4. 배너: "저장하지 않았습니다 — ..." → 사용자가 확인 후 [완료] 다시 누르면 최신 version 으로 성공
   */
  private async mergeEditConflict(): Promise<void> {
    const edit = this.data.editConfig;
    if (!edit) return;
    try {
      await this.reloadFull(edit.configId);
    } catch {
      // refetch 실패 — 드래프트는 유지, 일반 배너만
      this.data.editConfig = { ...edit, saving: false, conflict: '딜이 다른 곳에서 수정되어 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' };
      this.refreshMain();
      return;
    }
    const fresh = this.data.full;
    const baseIds  = new Set(edit.baseItemIds);
    const draftIds = new Set(edit.items.map((it) => it.id).filter((v): v is string => v != null));
    const freshItems = (fresh.items ?? [])
      .filter((it) => it.configuration_id === edit.configId)
      .filter(DealDetailView.isFlatPartRow);   // 서버 트리는 드래프트 비교 대상 아님
    const freshIds   = new Set(freshItems.map((it) => String(it.id)));

    // 2. 외부 추가 → 드래프트 append + 하이라이트
    const addedNames: string[] = [];
    for (const it of freshItems) {
      const idStr = String(it.id);
      if (baseIds.has(idStr) || draftIds.has(idStr)) continue;
      edit.items.push({
        lineId:              nextLineId(),
        id:                  idStr,
        category:            it.category,
        option_part:         it.option_part,
        item_description:    it.item_description,
        quantity:            it.quantity == null ? '' : String(it.quantity),
        requested_condition: it.requested_condition,
        sell_unit_price_krw: it.sell_unit_price_krw,
        is_unregistered:     it.is_unregistered,
        description_raw:     it.description_raw ?? null,
        source_type:         it.source_type   ?? null,
        source_ref_id:       it.source_ref_id ?? null,
        externalAdded:       true,
      });
      addedNames.push(it.description_raw ?? it.item_description ?? it.option_part ?? `#${idStr}`);
    }

    // 3. 외부 삭제 — 드래프트엔 남김 (사용자 우선), 알림만
    const removedNames = edit.items
      .filter((it) => it.id && baseIds.has(it.id) && !freshIds.has(it.id))
      .map((it) => it.description_raw ?? it.item_description ?? it.option_part ?? `#${it.id}`);

    // 4. 배너 문구 + 기준 스냅샷 갱신 (재저장은 최신 version 으로)
    const parts: string[] = [];
    if (addedNames.length)   parts.push(`외부에서 품목이 추가되어 아래 목록에 병합했습니다: ${addedNames.join(', ')}`);
    if (removedNames.length) parts.push(`외부에서 삭제된 품목이 있습니다 (드래프트에는 유지됨): ${removedNames.join(', ')}`);
    if (parts.length === 0)  parts.push('딜의 다른 정보가 수정되었습니다. 품목 변경은 없습니다.');
    this.data.editConfig = {
      ...edit,
      saving:      false,
      baseVersion: fresh.version,
      baseItemIds: freshItems.map((it) => String(it.id)),
      conflict:    `저장하지 않았습니다 — ${parts.join(' · ')} 목록을 확인하고 다시 [저장]을 눌러 주세요.`,
    };
    this.refreshMain();
  }

  async handleEditCancel(): Promise<void> {
    const edit = this.data.editConfig;
    if (!edit || edit.saving) return;
    // 저장 안 된 변경을 마지막 저장 상태(baseline)로 복원 — 편집 모드는 유지.
    //   (구성 자체를 없애려면 탭의 × 로 삭제)
    edit.items = edit.baseline.map((b) => ({ ...b }));
    edit.conflict = null;
    this.refreshMain();
  }

  // ── 카탈로그 검색 패널 ──
  handleItemSearchOpen(_e: Event, target: HTMLElement): void {
    const lineId = target.dataset.lineId;
    if (!lineId) return;
    // 현재 파트넘버를 검색창에 프리필 + 그 값으로 바로 검색.
    const item = this.data.editConfig?.items.find((it) => it.lineId === lineId);
    const q = (item?.option_part ?? '').trim();
    this.data.itemSearch = {
      ...emptyItemSearchState(), open: true, targetLineId: lineId,
      query: q, loading: q.length >= 2,
    };
    this.refreshModals();
    requestAnimationFrame(() => {
      const input = this._rootElement?.querySelector<HTMLInputElement>('.deal-item-search-input');
      input?.focus();
      input?.select();   // 프리필 값 전체 선택 → 바로 덮어쓰기/삭제 편하게
    });
    if (q.length >= 2) void this.runItemSearch(q);
  }

  handleItemSearchClose(): void {
    this.data.itemSearch = emptyItemSearchState();
    this.refreshModals();
  }

  // 클리어 — 검색어/결과 비우고 입력창 비움(포커스 유지). 프리필 값 빠르게 지우기용.
  handleItemSearchClear(): void {
    if (this._itemSearchTimer) window.clearTimeout(this._itemSearchTimer);
    this.data.itemSearch.query = '';
    this.data.itemSearch.selectedKey = '';
    this.data.itemSearch.hits = [];
    this.data.itemSearch.loading = false;
    const input = this._rootElement?.querySelector<HTMLInputElement>('.deal-item-search-input');
    if (input) { input.value = ''; input.focus(); }
    this.updateItemSearchResults();
  }

  // 입력 시 검색 — 250ms 디바운스, 최신 쿼리만 반영. 결과 컨테이너만 갱신(포커스 보존).
  handleItemSearchInput(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value;
    this.data.itemSearch.query = q;
    this.data.itemSearch.selectedKey = '';
    if (this._itemSearchTimer) window.clearTimeout(this._itemSearchTimer);
    if (q.trim().length < 2) {
      this.data.itemSearch.hits = [];
      this.data.itemSearch.loading = false;
      this.updateItemSearchResults();
      return;
    }
    this._itemSearchTimer = window.setTimeout(() => { void this.runItemSearch(q); }, 250);
  }

  private async runItemSearch(q: string): Promise<void> {
    this.data.itemSearch.loading = true;
    this.updateItemSearchResults();
    try {
      const res = await api.searchAll(q, 0, 20);
      // 응답 도착 시점에 쿼리가 바뀌었으면 폐기
      if (this.data.itemSearch.query !== q) return;
      this.data.itemSearch.hits = res.catalog ?? [];
    } catch {
      this.data.itemSearch.hits = [];
    } finally {
      if (this.data.itemSearch.query === q) {
        this.data.itemSearch.loading = false;
        this.updateItemSearchResults();
      }
    }
  }

  // 결과 리스트 + 적용 버튼만 갱신 (입력 포커스 유지).
  private updateItemSearchResults(): void {
    const box = this._rootElement?.querySelector('#item-search-results');
    if (box) box.innerHTML = renderItemSearchResultsHtml(this.data.itemSearch, this._fxRate);
    // 적용 버튼 morph(적용 ↔ 미등록 품목으로 적용) — 결과 유무에 따라 재렌더.
    const slot = this._rootElement?.querySelector('#item-search-apply-slot');
    if (slot) slot.innerHTML = renderItemSearchApplyBtn(this.data.itemSearch);
  }

  handleItemSearchSelect(_e: Event, target: HTMLElement): void {
    const key = target.dataset.key;
    if (!key) return;
    this.data.itemSearch.selectedKey = key;
    // innerHTML 재생성 대신 선택 표시만 in-place 갱신 —
    // 재생성하면 더블클릭한 노드가 교체돼 dblclick delegation(rootEl.contains)이 깨짐.
    const box = this._rootElement?.querySelector('#item-search-results');
    box?.querySelectorAll<HTMLElement>('.deal-item-search-row').forEach((el) => {
      el.classList.toggle('is-selected', el.dataset.key === key);
    });
    const slot = this._rootElement?.querySelector('#item-search-apply-slot');
    if (slot) slot.innerHTML = renderItemSearchApplyBtn(this.data.itemSearch);
  }

  // 결과 행 더블클릭 → 선택 + 즉시 적용 (적용 버튼 클릭 생략).
  handleItemSearchPick(_e: Event, target: HTMLElement): void {
    const key = target.dataset.key;
    if (!key) return;
    this.data.itemSearch.selectedKey = key;
    this.handleItemSearchApply();
  }

  // '재고 있는 품목만 검색' 토글 — 결과만 다시 그림(포커스 영향 없음).
  handleItemSearchStockToggle(_e: Event, target: HTMLElement): void {
    this.data.itemSearch.stockOnly = (target as HTMLInputElement).checked;
    this.updateItemSearchResults();
  }

  // 선택 적용 — hit → 드래프트 행의 파트넘버/디스크립션/카테고리 채움.
  handleItemSearchApply(): void {
    const s = this.data.itemSearch;
    if (!s.selectedKey || !this.data.editConfig) return;
    const hit = s.hits.find((h) => `${h.source}::${h.option_part}` === s.selectedKey);
    const item = this.data.editConfig.items.find((it) => it.lineId === s.targetLineId);
    if (!hit || !item) { this.handleItemSearchClose(); return; }
    item.option_part = hit.option_part;
    item.is_unregistered = false;   // 정식 카탈로그 품목 지정 → 등록으로 전환 (§4.5.18)
    const desc = hit.extra_description ? String(hit.extra_description) : '';
    if (desc) item.item_description = desc;
    // 등록 파트는 카탈로그 원문(description_raw)을 읽기전용 표시.
    //   검색 hit 의 top-level description_raw 를 미리보기로 넣고(원문 없으면 null →
    //   표시 로직이 item_description 으로 폴백), 저장·재조회 시 백엔드
    //   v_catalog_part_desc 값으로 확정된다.
    //   (구버전은 존재하지 않는 hit.extra_description_raw 를 참조해 정제본이 들어가던 버그)
    item.description_raw = hit.description_raw != null ? String(hit.description_raw) : null;
    if ((ITEM_CATEGORY_OPTIONS as readonly string[]).includes(hit.source)) {
      item.category = hit.source;
    }
    this.data.itemSearch = emptyItemSearchState();
    this.refreshMain();      // 편집 행에 선택 반영
    this.refreshModals();    // 검색 패널 닫기
  }

  // '미등록 품목으로 적용' — 대상 행을 미등록으로 변경(검색 결과 없을 때만 노출).
  //   box7: 검색어 있으면 P/N=검색어, 없으면 P/N 공란. DESC 는 항상 공란(edit mode 에서 입력).
  handleItemSearchApplyUnregistered(): void {
    const s = this.data.itemSearch;
    if (!this.data.editConfig) { this.handleItemSearchClose(); return; }
    const item = this.data.editConfig.items.find((it) => it.lineId === s.targetLineId);
    if (!item) { this.handleItemSearchClose(); return; }
    const q = s.query.trim();
    item.is_unregistered = true;
    item.is_pending      = false;        // 순수 미등록(임시등록은 품목 확정 시 서버가 부여)
    item.option_part     = q || null;    // 검색어 → 파트넘버 (없으면 공란)
    item.item_description = null;         // 디스크립션 공란
    item.description_raw  = null;         // 등록 카탈로그 원문 제거
    this.data.itemSearch = emptyItemSearchState();
    this.refreshMain();
    this.refreshModals();
  }

  // ─── 견적 보기 모달 (P9-UI Step 3c) ───────────────────────────────
  //
  // 견적 행의 [보기] 클릭 → fetchQuoteFull → 모달 표시.
  // ─── 견적 행 작업(⋯) 메뉴 ─────────────────────────────────────────
  handleQuoteMenuOpen(_e: Event, target: HTMLElement): void {
    const id = target.dataset.quoteId;
    if (!id) return;
    const willOpen = this.data.quoteMenuId !== id;
    this.data.quoteMenuId = willOpen ? id : null;
    this.refreshMain();
    if (willOpen) this.positionQuoteMenu();
  }
  handleQuoteMenuClose(): void {
    if (!this.data.quoteMenuId) return;
    this.data.quoteMenuId = null;
    this.refreshMain();
  }
  private positionQuoteMenu(): void {
    requestAnimationFrame(() => {
      const menu = document.querySelector<HTMLElement>('.deal-quote-menu');
      if (!menu) return;
      const cell    = menu.closest('.deal-tag-cell');
      const trigger = cell?.querySelector<HTMLElement>('.deal-quote-menu-trigger');
      if (!trigger) return;
      const r  = trigger.getBoundingClientRect();
      const mh = menu.offsetHeight;
      let top = r.bottom + 4;
      if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 4);
      menu.style.position = 'fixed';
      menu.style.left     = `${Math.max(8, r.right - menu.offsetWidth)}px`;
      menu.style.top      = `${top}px`;
    });
  }
  /**
   * 견적서 다운로드 — 서버 품목이 있으면 정식/약식 선택 팝업, 없으면 바로 받는다 (§13-1).
   *
   * 서버 포함 여부를 **서버에 물어본다**. 목록 행에는 품목 정보가 없고
   * (quote 목록은 금액·상태만 준다), 견적 전체를 불러오면 표시에 쓰지도 않을
   * 품목 배열을 통째로 받게 된다.
   *
   * ★ 임대 견적은 팝업이 없다 (2026-08-20). 임대 양식은 서버·파트 각각 한 장씩
   *   뿐이고 그중 어느 쪽인지는 서버 품목 유무가 정한다 — 사람이 고를 게 없다.
   *   정식/약식 선택은 구매 서버 견적서에만 있는 갈림길이다(임대는 약식 구조
   *   한 장뿐이라 정식 양식 자체가 템플릿에 없다).
   */
  async handleQuoteDownload(_e: Event, target: HTMLElement): Promise<void> {
    const quoteId = target.dataset.quoteId;
    this.data.quoteMenuId = null;
    this.refreshMain();
    if (!quoteId) return;

    // 임대면 곧바로 받는다. preset 을 보내지 않으면 백엔드가 견적 유형과 서버
    // 유무를 보고 파트임대/서버임대를 고른다 — 유형 판정을 두 곳에 두지 않는다.
    const row = (this.data.quotes.quotes ?? []).find((r) => String(r.id) === String(quoteId));
    if (row?.quote_type === 'RENTAL') {
      try {
        await api.downloadQuoteDocument(quoteId);
      } catch (err) {
        alert(`견적서 다운로드 실패: ${err instanceof Error ? err.message : 'unknown'}`);
      }
      return;
    }

    // 판정 실패는 다운로드를 막지 않는다.
    //
    // 이건 "어떤 양식으로 줄까"를 묻는 보조 질의일 뿐인데, 여기서 예외를 던지면
    // 원래 되던 다운로드까지 함께 죽는다(백엔드 배포 전 404 로 실제로 그랬다).
    // 실패하면 preset 없이 그냥 받는다 — 서버가 있으면 백엔드가 정식을 기본으로
    // 골라 주므로 문서는 정상적으로 나온다.
    let hasServer = false;
    try {
      hasServer = await api.quoteDocumentHasServer(quoteId);
    } catch {
      hasServer = false;
    }

    if (hasServer) {
      const q = (this.data.quotes.quotes ?? []).find((r) => String(r.id) === String(quoteId));
      this.data.quoteDownloadModal = {
        open: true, quoteId, quoteName: q?.name ?? '견적', busy: null, error: '',
      };
      this.refreshModals();
      return;
    }
    // 파일명은 서버가 생성(담당자·직책·첫파트·외) → Content-Disposition 노출로 그대로 사용.
    try {
      await api.downloadQuoteDocument(quoteId);
    } catch (err) {
      alert(`견적서 다운로드 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  /** 팝업에서 정식/약식을 고름 → 그 preset 으로 내려받고 닫는다. */
  async handleQuoteDownloadPick(_e: Event, target: HTMLElement): Promise<void> {
    const m = this.data.quoteDownloadModal;
    const preset = target.dataset.preset as api.QuoteDocPreset | undefined;
    if (!m.open || !m.quoteId || !preset || m.busy) return;

    this.data.quoteDownloadModal = { ...m, busy: preset as 'server-full' | 'server-brief', error: '' };
    this.refreshModals();
    try {
      await api.downloadQuoteDocument(m.quoteId, undefined, preset);
      this.data.quoteDownloadModal = emptyQuoteDownloadModal();
    } catch (err) {
      // 모달은 열어 둔다 — 다른 preset 을 바로 다시 시도할 수 있게.
      this.data.quoteDownloadModal = {
        ...this.data.quoteDownloadModal, busy: null,
        error: `다운로드 실패: ${err instanceof Error ? err.message : 'unknown'}`,
      };
    }
    this.refreshModals();
  }

  handleQuoteDownloadClose(): void {
    if (this.data.quoteDownloadModal.busy) return;   // 생성 중엔 못 닫는다
    this.data.quoteDownloadModal = emptyQuoteDownloadModal();
    this.refreshModals();
  }

  /**
   * 클립보드 쓰기 — 판매/임대 두 경로가 같은 폴백을 쓰게 한 곳에 둔다.
   * 비-secure context(사내 http)에서는 navigator.clipboard 가 없어서
   * textarea + execCommand 로 떨어져야 한다. 복붙해 두면 한쪽만 고치게 된다.
   */
  private async _copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch { /* 아래 폴백 */ }
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch { /* 무시 */ }
    ta.remove();
  }

  /** 견적 복사 — 견적 구성 품목을 카톡용 문구로 클립보드에 복사. */
  async handleQuoteCopy(_e: Event, target: HTMLElement): Promise<void> {
    const quoteId = target.dataset.quoteId;
    this.data.quoteMenuId = null;
    this.refreshMain();
    if (!quoteId) return;
    try {
      const qf = await api.fetchQuoteFull(quoteId);
      const dealItems = this.data.full.items ?? [];
      const lines = (qf.items ?? [])
        .filter((it) => it.is_active)
        // 서버 구성품은 빼고 서버 한 줄로 낸다 — 구성 상세는 약식 요약 안에 있다.
        // 안 빼면 CPU/RAM/DISK 가 각각 "단가 : -원" 블록으로 20줄 넘게 쏟아진다.
        .filter((it) => it.parent_deal_item_id == null)
        .sort((a, b) => a.sort_order - b.sort_order);

      // ★ 임대 견적은 문구가 통째로 다르다 — 컨디션·워런티 대신 기간·월 금액·
      //   계약 총액을 말한다. 판매 매퍼에 임대 분기를 심지 않고 여기서 갈라
      //   전용 포매터로 보낸다(quote-copy.ts 의 두 함수를 나눠 둔 이유와 같다).
      if (qf.quote_type === 'RENTAL') {
        const rentalItems = lines.map((it) => {
          const di = it.deal_item_id
            ? dealItems.find((d) => String(d.id) === String(it.deal_item_id))
            : undefined;
          const isServer = it.line_type === 'server';
          return {
            optionPart:   isServer ? null : it.option_part,
            description:  it.description,
            // 서버는 약식 요약이 정본 — 견적의 description 에는 모델명 한 줄만 있다.
            summary:      isServer ? (di?.item_description ?? it.description ?? null) : null,
            kind:         (isServer ? 'server' : 'part') as 'server' | 'part',
            months:       it.rental_term_months ?? null,
            // 서버 라인의 수량은 대수다(판매의 번들가 규칙과 같은 자리).
            quantity:     isServer ? (it.server_quantity ?? it.quantity ?? null) : (it.quantity ?? null),
            // 적용 임대단가. 수동 단가를 넣었으면 저장 시 그 값이 여기 들어와 있다.
            unitPriceKrw: it.unit_price_krw ?? null,
          };
        });
        if (rentalItems.length === 0) { alert('복사할 견적 품목이 없습니다.'); return; }
        await this._copyToClipboard(formatRentalQuoteCopyText(rentalItems));
        this.toast('견적 문구를 복사했습니다.');
        return;
      }

      const items = lines
        .map((it) => {
          // 컨디션은 연결된 딜 품목(deal_item_id)의 requested_condition 에서.
          const di = it.deal_item_id
            ? dealItems.find((d) => String(d.id) === String(it.deal_item_id))
            : undefined;

          if (it.line_type === 'server') {
            return {
              optionPart:   null,
              description:  it.description,
              // 약식 요약의 정본은 서버 헤더행의 item_description 이다.
              // 견적의 description 에는 모델명 한 줄만 들어 있다(QUOTE-009).
              summary:      di?.item_description ?? it.description ?? null,
              // 서버 컨디션은 NEW/USED — 부품 컨디션 코드(F/S 등)와 어휘가 다르다.
              condition:    di?.server_condition === 'USED' ? 'REF'
                          : di?.server_condition === 'NEW'  ? 'F/S' : null,
              // 고객이 보는 값은 서버 1대 판매가(번들가)다.
              unitPriceKrw: it.unit_price_override ?? it.unit_price_krw,
              kind:         'server' as const,
            };
          }
          return {
            optionPart:   it.option_part,
            description:  it.description,
            unitPriceKrw: it.unit_price_krw,
            condition:    di?.requested_condition ?? null,
            kind:         'part' as const,
          };
        });
      if (items.length === 0) { alert('복사할 견적 품목이 없습니다.'); return; }

      await this._copyToClipboard(formatQuoteCopyText(items));
      this.toast('견적 문구를 복사했습니다.');
    } catch (err) {
      alert(`견적 복사 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  /** 견적 삭제 — 작성됨(draft) 상태만. 서버가 딜 상태 되돌림(→ '리드 검토됨')까지 처리. */
  async handleQuoteDelete(_e: Event, target: HTMLElement): Promise<void> {
    const quoteId = target.dataset.quoteId;
    this.data.quoteMenuId = null;
    this.refreshMain();
    if (!quoteId) return;
    if (!confirm('이 견적을 삭제하시겠습니까?\n\n'
               + '작성됨 상태의 견적만 삭제됩니다.\n'
               + '남은 작성 견적이 없으면 딜 상태가 \'리드 검토됨\' 으로 되돌아갑니다.')) {
      return;
    }
    try {
      await api.deleteQuote(quoteId);
      await this.afterQuoteAction(quoteId);   // 견적 리스트·딜 상태·합계 카드·채팅 갱신
      this.toast('견적을 삭제했습니다.');
    } catch (err) {
      alert(`견적 삭제 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // 견적 수정 — 전체 견적을 불러와 기존 작성 모달을 "수정 모드"로 연다.
  async handleQuoteEdit(_e: Event, target: HTMLElement): Promise<void> {
    const quoteId = target.dataset.quoteId;
    if (!quoteId) return;
    try {
      const qf = await api.fetchQuoteFull(quoteId);
      // 매입 환산 환율 — 판매는 오늘 값, 임대는 저장된 값(고객 금액이 원가에서
      //   계산되므로 흔들리면 안 된다). 저장하면 이 값이 새 스냅샷이 된다.
      const storedFx = qf.fx_rate_krw_per_usd != null ? Number(qf.fx_rate_krw_per_usd) : 1380;
      const editFx   = qf.quote_type === 'RENTAL'
        ? storedFx
        : (this._fxRate > 0 ? this._fxRate : storedFx);
      const items = (qf.items ?? [])
        .filter((it) => it.is_active)
        .sort((a, b) => a.sort_order - b.sort_order)
        // 수정 모달도 오늘 환율로 연다 — 저장하면 그 값이 새 스냅샷이 된다.
        //   임대는 고객 금액이 원가에서 계산되므로 저장된 환율을 지킨다.
        .map((it) => quoteItemToModalRow(it, editFx));
      const total = qf.total_amount_krw != null ? Number(qf.total_amount_krw) : null;
      const vat   = qf.amount_with_vat_krw != null ? Number(qf.amount_with_vat_krw) : null;
      this.data.quoteViewModal = emptyQuoteViewModalState();   // 보기 모달 닫기
      this.data.quoteModal = {
        ...emptyQuoteModalState(),
        open:        true,
        editId:      quoteId,
        name:        qf.name ?? '',
        fxRate:      String(editFx),
        vatIncluded: vat != null && total != null && vat > total,
        // 저장된 유형을 그대로 되살린다. 임대 견적을 열었는데 판매로 보이면
        //   사람이 모르고 저장해 유형이 뒤집힌다.
        quoteType:   qf.quote_type === 'RENTAL' ? 'RENTAL' as const : 'SALE' as const,
        items,
        // 생성 모달과 같게 서버 구성품을 펼친 채로 연다(2026-08-24).
        //   서버 헤더행은 quote_item 이라 deal_item_id 로 담는다 —
        //   자식의 parent_deal_item_id 가 그 값을 가리킨다(마이그 131).
        expandedServers: new Set<string>(
          (qf.items ?? []).filter((it) => it.line_type === 'server' && it.deal_item_id != null)
                          .map((it) => String(it.deal_item_id))),
        baseVersion: this.data.full.version,
      };
      this.refreshQuoteViewModal();
      this.refreshQuoteModal();
      void this._loadQuoteSources();
      void this._loadRentalPresets();
    } catch (err) {
      alert(`견적 불러오기 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ─── 소싱 니즈 행 작업(⋯) 메뉴 ────────────────────────────────────
  handleNeedMenuOpen(_e: Event, target: HTMLElement): void {
    const id = target.dataset.needId;
    if (!id) return;
    const willOpen = this.data.needMenuId !== id;
    this.data.needMenuId = willOpen ? id : null;
    this.refreshMain();
    if (willOpen) this.positionNeedMenu();
  }
  handleNeedMenuClose(): void {
    if (!this.data.needMenuId) return;
    this.data.needMenuId = null;
    this.refreshMain();
  }
  private positionNeedMenu(): void {
    requestAnimationFrame(() => {
      const menu = document.querySelector<HTMLElement>('.deal-need-menu');
      if (!menu) return;
      const cell    = menu.closest('.deal-tag-cell');
      const trigger = cell?.querySelector<HTMLElement>('.deal-need-menu-trigger');
      if (!trigger) return;
      const r  = trigger.getBoundingClientRect();
      const mh = menu.offsetHeight;
      let top = r.bottom + 4;
      if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 4);
      menu.style.position = 'fixed';
      menu.style.left     = `${Math.max(8, r.right - menu.offsetWidth)}px`;
      menu.style.top      = `${top}px`;
    });
  }
  // 니즈 삭제 (소프트). RFQ 이력은 보존되고 소싱 목록에서만 사라짐.
  async handleNeedDelete(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.needId;
    if (!id) return;
    this.data.needMenuId = null;
    this.refreshMain();   // 메뉴 닫기
    if (!confirm('이 니즈를 삭제할까요?\n연결된 RFQ 이력은 보존되며, 소싱 목록에서만 사라집니다.')) return;
    try {
      await api.deleteNeed(id);
      this.data.sourcing = await api.fetchDealSourcing(this.data.dealId);
      this.refreshMain();
      this.toast('니즈를 삭제했습니다.');
    } catch (err) {
      this.refreshMain();
      alert(`니즈 삭제 실패: ${(err as Error).message}`);
    }
  }

  // 니즈 구매(매입) 요청 토글 — 표시/플래그만. data-requested 로 현재값 판단.
  async handleNeedPurchaseRequest(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.dataset.needId;
    if (!id) return;
    const requested = target.dataset.requested !== '1';   // 현재 0 → 요청, 1 → 취소
    this.data.needMenuId = null;
    this.refreshMain();   // 메뉴 닫기
    try {
      await api.setNeedPurchaseRequest(id, requested);
      this.data.sourcing = await api.fetchDealSourcing(this.data.dealId);
      this.refreshMain();
      this.toast(requested ? '구매 요청으로 표시했습니다.' : '구매 요청을 취소했습니다.');
    } catch (err) {
      this.refreshMain();
      alert(`구매 요청 처리 실패: ${(err as Error).message}`);
    }
  }

  // 니즈 수정 모달 열기 — 소싱 니즈에서 수량·컨디션 프리필.
  handleNeedEdit(_e: Event, target: HTMLElement): void {
    const id = target.dataset.needId;
    if (!id) return;
    const n = this.data.sourcing?.needs.find((x) => x.id === id);
    this.data.needMenuId = null;
    this.data.needEdit = {
      open: true, needId: id,
      partNumber:  n?.option_part ?? '',
      description: n?.item_text ?? '',
      quantity:    n ? String(n.quantity) : '',
      condition:   n?.requested_condition ?? '',
      saving: false, error: '',
    };
    this.refreshMain();
    this.refreshModals();
  }
  handleNeedEditClose(e: Event, target: HTMLElement): void {
    if (e.target !== target) return;   // 배경/취소 직접 클릭만 닫기
    this.data.needEdit.open = false;
    this.refreshModals();
  }
  handleNeedEditField(_e: Event, target: HTMLElement): void {
    const el = target as HTMLInputElement | HTMLSelectElement;
    const f  = el.dataset.field;
    if (f === 'quantity')  this.data.needEdit.quantity  = (el as HTMLInputElement).value;
    if (f === 'condition') this.data.needEdit.condition = el.value;
    // uncontrolled — 재렌더 X (저장 때 state 사용)
  }
  async handleNeedEditSave(): Promise<void> {
    const s = this.data.needEdit;
    if (s.saving) return;
    const qty = Number(s.quantity);
    if (!Number.isFinite(qty) || qty < 1) { s.error = '요청 수량을 1 이상 입력하세요.'; this.refreshModals(); return; }
    s.saving = true; s.error = '';
    this.refreshModals();
    try {
      await api.updateNeed(s.needId, { quantity: qty, requested_condition: s.condition || null });
      this.data.sourcing = await api.fetchDealSourcing(this.data.dealId);
      this.data.needEdit.open = false;
      this.refreshMain();
      this.refreshModals();
      this.toast('니즈를 수정했습니다.');
    } catch (err) {
      s.saving = false;
      s.error  = err instanceof Error ? err.message : '수정 실패';
      this.refreshModals();
    }
  }

  // 견적 행 클릭 → 보기 모달. quoteId 는 위임 target(tr)에서 읽는다.
  async handleViewQuote(_e: Event, trigger: HTMLElement): Promise<void> {
    const quoteId = trigger.dataset.quoteId;
    if (!quoteId) return;

    // loading 상태로 모달 open
    this.data.quoteViewModal = {
      open:    true,
      loading: true,
      error:   '',
      data:    null,
      refPopRow: null,
    };
    this.refreshQuoteViewModal();

    try {
      const full = await api.fetchQuoteFull(quoteId);
      this.data.quoteViewModal = {
        open:    true,
        loading: false,
        error:   '',
        data:    full,
        refPopRow: null,
      };
    } catch (err) {
      this.data.quoteViewModal = {
        open:    true,
        loading: false,
        error:   err instanceof Error ? err.message : '견적 조회 실패',
        data:    null,
        refPopRow: null,
      };
    }
    this.refreshQuoteViewModal();
    void this._loadQuoteSources();   // 소스(매입처명) 해석용
  }

  handleCloseQuoteViewModal(): void {
    this.data.quoteViewModal = emptyQuoteViewModalState();
    this.refreshQuoteViewModal();
  }

  // ─── 견적 발송 (P9-UI Step 3c) ────────────────────────────────────
  //
  // 견적 목록 행의 [발송] 버튼 클릭. status='draft' 일 때만 표시됨.
  // sendQuote 호출 → backend 가 status='sent' + deal.status='견적 발송 완료'
  // + 채팅에 자동 알림 게시 → UI refetch.
  // 견적 발송 — 확인 모달 없이 즉시 발송. 보기 모달은 열어둔 채 상태만 갱신해
  // 버튼이 발송됨(견적 채택/반려/수정)으로 바뀌게 한다. (현민 — 모달 없이 바로)
  async handleSendQuote(e: Event): Promise<void> {
    const target = e.target as HTMLElement;
    const quoteId = target.dataset.quoteId;
    if (!quoteId) return;

    try {
      await api.sendQuote(quoteId);
      await this.afterQuoteAction(quoteId);   // 모달 유지 + 본문/채팅 갱신
      this.toast('견적을 발송했습니다.');
    } catch (err) {
      alert(`발송 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ─── 견적 수락 (P9-UI Step 3e) ────────────────────────────────────
  //
  // 워크플로우 24-26 — 고객이 견적 수락 → quote.accept.
  // 같은 deal 의 기존 accepted → sent 로 되돌아감.
  // 이 quote → accepted + result='수락'.
  // deal.status='수주' 자동 변경 + cache sync + 채팅 알림.
  async handleAcceptQuote(e: Event): Promise<void> {
    const target = e.target as HTMLElement;
    const quoteId = target.dataset.quoteId;
    if (!quoteId) return;

    if (!confirm('이 견적을 수락 상태로 변경하시겠습니까?\n\n'
               + '딜 상태가 \'수주\' 로 자동 변경되고,\n'
               + '같은 딜의 다른 수락 견적은 자동으로 발송 상태로 되돌아갑니다.\n'
               + '견적 금액 카드도 이 견적 값으로 갱신됩니다.')) {
      return;
    }

    try {
      await api.acceptQuote(quoteId);
      await this.afterQuoteAction(quoteId);
    } catch (err) {
      alert(`수락 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // ─── 견적 거절 (P9-UI Step 3e) ────────────────────────────────────
  //
  // status='rejected' + result='거절'. deal.status 는 그대로 (영업담당자가
  // 새 견적 작성 흐름). 다만 이전이 accepted 였으면 deal 캐시 NULL.
  async handleRejectQuote(e: Event): Promise<void> {
    const target = e.target as HTMLElement;
    const quoteId = target.dataset.quoteId;
    if (!quoteId) return;

    if (!confirm('이 견적을 거절 상태로 변경하시겠습니까?\n\n'
               + '딜 상태는 그대로 유지됩니다.\n'
               + '수락 상태였다면 견적 금액 카드가 비워집니다.')) {
      return;
    }

    try {
      await api.rejectQuote(quoteId);
      await this.afterQuoteAction(quoteId);
    } catch (err) {
      alert(`거절 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    }
  }

  // 공통 — 견적 액션(발송/채택/반려) 후처리.
  // 견적 리스트·딜 상태·합계 카드 + 채팅(시스템 메시지)을 갱신한다. 보기 모달이
  // 그 견적을 열고 있으면 닫지 않고 최신 데이터로 다시 채워(상태/결과/풋터 버튼)
  // 즉시 반영한다. (refreshBody 가 채팅은 안 덮으므로 syncChat 별도 호출.)
  private async afterQuoteAction(quoteId: string): Promise<void> {
    const v = this.data.quoteViewModal;
    const modalOpen = v.open && v.data != null && v.data.id === quoteId;
    const [newQuotes, newFull, modalFull] = await Promise.all([
      api.fetchDealQuotes(this.data.full.id),
      api.fetchDealFull(this.data.full.id),
      modalOpen ? api.fetchQuoteFull(quoteId) : Promise.resolve(null),
    ]);
    this.data.quotes = newQuotes;
    this.data.full   = newFull;
    if (modalOpen && modalFull) {
      this.data.quoteViewModal = { ...v, data: modalFull };
    }
    this.refreshBody();   // 헤더(딜 상태)/메인(합계 카드·리스트)/모달(풋터 버튼)
    this.syncChat();   // 발송/채택/반려 시스템 메시지 반영
  }

  // ══════════════════════════════════════════════════════════════════
  // 견적 렌더 (생성/수정 모달 · 보기 모달 · 합계 카드)
  //   → deal-detail/render-quote.ts 로 이동 (Step 2, 2026-07-03)
  // ══════════════════════════════════════════════════════════════════



}

// 견적 렌더 (모달/보기/합계) + quoteSourceLabel + fmt 헬퍼
//   → deal-detail/render-quote.ts 로 이동 (Step 2, 2026-07-03).
// state factory (emptyQuoteModalState 등) → deal-detail/types.ts 로 이동 (Step 1, 2026-07-03).
// rowCostKrw / parseNum / parseInt0 / computeQuoteTotals → lib/quote-math.ts (2026-07-02).
